/**
 * Flows Studio prototype (?test=flows-studio)
 * v4.32: A proposed step is a diff. Green is the new card. Red is a card
 * the proposal drops. The blue selection color stays off those cards.
 * v4.31: Refine's agent pane lists session files the way chat does.
 * Flow soul, memory, and a transcript. Clear starts a new transcript.
 * v4.29: A sparkle suggestion is a diff hunk. Prompt lines use the review
 * washes. A short field is the hunk: proposed value in the field, current
 * value struck beside it.
 * v4.28: The Run summary card selects the run. That is the Runs preview
 * when no step is selected.
 * v4.27: Definition fields commit on blur. Sparkles previews a rewrite.
 * An agent change washes the step, then leaves a sparkles mark.
 * v4.30: The Activity pin hairline uses `useStickyStuck` from the shell kit.
 * v4.26: Pulse activity bar sticks to the panel top and shares the preview line.
 * The hairline is the reading column, and only once the bar is stuck.
 * List headings stick under that bar.
 * v4.25: Column width collapses Pulse Filters and Sort to icons.
 * v4.24: Pulse activity rows fade and collapse with the shell motion (200ms).
 * v4.23: Pulse stat tiles are separate cards. Click again to clear.
 * v4.22: Right-click Preview on a flow step opens the details pane.
 * It stays disabled when that pane is already showing the step.
 * v4.21: Pulse filter and sort menus use the quiet menu chrome.
 * v4.20: A live run shows Running on the flow document, not only in Activity.
 * Filters and sort drop to icons when the activity bar is narrow.
 * v4.30: Pulse has no expand button. The preview pane is the run detail.
 * v4.19: retired. Expand used to open a drill sheet over the feed.
 * v4.18: Pulse activity uses the folder filter bar. Search is a magnifying glass.
 * Up next shows three cards, then Show more. Row status is the badge only.
 * v4.17: Pulse Mine / Team, filter menu, flat run rows, and a run-detail trace.
 * v4.16: Sidebar file tree matches #sidebar-tree. Folder click lists that folder.
 * File click opens the flow. Chevron only expands. One menu on the tree and the list.
 * v4.15: Definition Visual / Source uses the document footer toggle, beside Column.
 * v4.13: Runs uses the same full-size, 760px pipeline cards as Definition.
 * v4.12: Definition / Runs are the only flow navigation. Runtime is one
 * title-row state control (Disabled / Shadow / Live); Run now is an action.
 * v4.10: tab open rules (persist, pin, Cmd-click). Light/dark only.
 * Flow document = Definition (pipeline workspace) / Runs. Recipes dropped; Gallery stays catalog.
 *
 * Continuity: docs/design/maater-list-surface-continuity.md
 * Brainstorm: docs/product/brainstorms/2026-07-09-flows-vision-and-studio-reinvention.md
 * Critique:   docs/product/brainstorms/2026-07-10-flows-studio-v4-critique-and-elegance-pass.md
 * Bet:        docs/product/bets/2026-07-31-flows-studio-production-port.md
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import '@/components/prototype/maater-ui-diff-review-scene.css'
import {
  Activity,
  AlignCenter,
  AlignJustify,
  AlertTriangle,
  ArrowUpDown,
  Ban,
  BookOpen,
  Bot,
  Check,
  Search,
  ChevronDown,
  ChevronRight,
  Clock,
  Code,
  Copy,
  DollarSign,
  Eye,
  Folder,
  FolderOpen,
  GitBranch,
  Globe,
  Inbox,
  List,
  Loader2,
  Mic,
  MoreHorizontal,
  MoreVertical,
  MessageSquare,
  PauseCircle,
  Pin,
  Play,
  Puzzle,
  Plus,
  Radio,
  ShieldAlert,
  SlidersHorizontal,
  Sparkles,
  StretchHorizontal,
  ThumbsUp,
  Video,
  Wand2,
  Webhook,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { SHELL_PANEL_MOTION_MS } from '@/lib/shell-panel-motion'
import { testIds } from '@/lib/test-ids'
import { Button } from '@/components/ui/button'
import { IconButton } from '@/components/ui/icon-button'
import {
  EntityContextMenu,
  type EntityContextMenuEntry,
} from '@/components/features/entity-context-menu'
import { OpenDestinationIcons } from '@/lib/open-destination-icons'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { TooltipProvider } from '@/components/ui/tooltip'
import { WorkspacePipGlyph } from '@/components/layout/workspace-pip-glyph'
import { WorkspaceToggleChip } from '@/components/layout/workspace-scope-selector'
import type { Workspace } from '@/providers/workspace-provider'
import { PIP_CAP } from '@/hooks/use-workspace-recency'
import { ExtensionListRow } from '@/components/integrations/_shared/extension-list-row'
import { ListViewModeToggle } from '@/components/integrations/_shared/list-view-mode-toggle'
import { IndexPreviewEmpty } from '@/components/integrations/_shared/index-preview-empty'
import { PreviewPanelToggle } from '@/components/integrations/_shared/preview-panel-toggle'
import { PreviewPaneHeader } from '@/components/features/preview-pane-header'
import { SecondaryPreviewResizeHandle } from '@/components/integrations/_shared/secondary-preview-resize-handle'
import { SecondaryPanel } from '@/components/layout/secondary-panel'
import { PANEL_SIZES } from '@/lib/panel-sizes'
import { Panel, PanelGroup } from 'react-resizable-panels'
import { UpOneLevelRow } from '@/components/ui/up-one-level-row'
import { wsNavRowPadLeftPx } from '@/components/layout/left-sidebar.components'
import { ListSurfaceStatusBar } from '@/components/ui/list-surface-status-bar'
import { ShellIndexRow } from '@/components/layout/shell/shell-index-row'
import { useStickyStuck } from '@/components/layout/shell/use-sticky-stuck'
import { STICKY_COLUMN_HEAD_CLASS, STICKY_INDEX_BAND_CLASS } from '@/lib/shell-chrome'
import { ModePanelNavItem } from '@/components/layout/sidebar/ModePanelNavItem'
import { SidebarPanelHeader } from '@/components/layout/sidebar/SidebarPanelHeader'
import { SidebarTreeRow } from '@/components/layout/sidebar/SidebarTreeRow'
import { getDocumentDisplayIcon } from '@/lib/document-icons'
import { isModClick, resolveOpenIntent } from '@/lib/open-intent'
import { SHELL_COLUMN_HEADER_CLASS } from '@/lib/shell-chrome'
import { wsNavSectionStyle } from '@/lib/ws-nav'
import { SettingsSection, SettingsSectionRow } from '@/components/features/settings/settings-section'
import {
  MaaterProtoSegmentTrack,
  maaterProtoAppSkinStyle,
} from '@/components/prototype/maater-proto-shell'
import { useTheme } from '@/providers/theme-provider'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
  QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS,
} from '@/lib/menu-chrome'
import {
  ProtoAppHeader,
  ProtoIconRail,
  ProtoTitleRow,
} from '@/components/prototype/maater-proto-chrome'

// ---------------------------------------------------------------------------
// Types + mock data
// ---------------------------------------------------------------------------

/** Mode-panel nouns. A flow document is not a noun - it is the selected tree row. */
type Surface = 'pulse' | 'flow' | 'folder' | 'gallery' | 'sources' | 'composer'
type FlowTreeTarget = { kind: 'folder'; path: string } | { kind: 'flow'; id: string }
type FolderPeek = {
  selectedId: string | null
  selectedKind: 'folder' | 'flow' | null
  open: boolean
}
/** Per-flow document tabs. Definition is the pipeline workspace. Runs is this flow's history. */
type FlowDocTab = 'overview' | 'runs'
type RunStatus = 'ok' | 'warning' | 'failed' | 'policy_blocked' | 'awaiting_approval' | 'shadow' | 'running'
type RuntimeMode = 'draft' | 'shadow' | 'live'
type PipelineMode = 'definition' | 'execution'
type PipelineDensity = 'full' | 'mini'
/** Pipeline node id for the trigger card (not a step). */
const FLOW_TRIGGER_NODE = 'trigger'
/** Per-step outcome in execution / Run mode - required so failure paths are honest */
type StepRunState = 'ok' | 'failed' | 'skipped' | 'stubbed' | 'pending' | 'running'

const SURFACES: { id: Surface; label: string; icon: LucideIcon }[] = [
  { id: 'pulse', label: 'Pulse', icon: Activity },
  { id: 'gallery', label: 'Gallery', icon: BookOpen },
  { id: 'sources', label: 'Sources', icon: Radio },
  { id: 'composer', label: 'Composer', icon: Wand2 },
]

/** Option B (locked 2026-08-29): rust-pale bind chips, moss emits, dashed Shadow. */
const CHIP_BIND_CLASS =
  'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium border bg-primary/10 text-primary border-primary/25'
const CHIP_EMIT_CLASS =
  'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium border border-emerald-700/35 bg-emerald-500/10 text-emerald-800 dark:border-emerald-500/40 dark:text-emerald-400'
const BADGE_SCHEMA_CLASS =
  'border border-emerald-500/25 bg-emerald-500/10 text-emerald-800 dark:text-emerald-400'
const BADGE_LIVE_CLASS =
  'border border-emerald-500/25 bg-emerald-500/10 text-emerald-800 dark:text-emerald-400'
const BADGE_SHADOW_CLASS =
  'border border-dashed border-primary/40 bg-primary/10 text-primary'
const BADGE_DRAFT_CLASS = 'border border-border bg-muted text-muted-foreground'

function StudioStatusPill({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
        className,
      )}
    >
      {children}
    </span>
  )
}

interface StudioStep {
  id: string
  registryId: string
  label: string
  inputs: Record<string, string>
  emits: string[]
  when?: string
  dangerous?: boolean
  /** Sentence template with {field} placeholders matching inputs keys */
  intent?: string
}

interface StudioFlow {
  id: string
  name: string
  slug: string
  /** Path for pruned-tree mock (folders that contain flows) */
  path: string
  trigger: { registryId: string; label: string; detail: string; plainSummary: string }
  steps: StudioStep[]
  mode: RuntimeMode
  systemPaused?: string
  health: 'ok' | 'warning' | 'failed'
  lastRun: string
  nextRun: string
  costWeekUsd: number
  installedFromGallery?: boolean
  /** Missing means Mine. False is a teammate's flow. */
  mine?: boolean
}

interface FeedRun {
  id: string
  flowId: string
  flowName: string
  status: RunStatus
  when: string
  headline: string
  detail: string
  costUsd?: number
  failedStep?: string
  fixHint?: string
  threadLink?: boolean
  /** Proto: captured step outcomes for a mocked live run */
  stepStates?: Record<string, StepRunState>
}

type PulseTriggerKind = 'schedule' | 'webhook' | 'manual' | 'event'
type OwnerScope = 'mine' | 'team'

interface RunMeta {
  mine: boolean
  ageHours: number
  durationSec: number | null
  triggerKind: PulseTriggerKind
  extension?: string
  agentStepId?: string
  agentOutput?: string
  conversationLabel?: string
  runningForSec?: number
}

/** Human labels for bindable fields - registry metadata stand-in */
const FIELD_LABELS: Record<string, string> = {
  'trigger.meeting_topic': 'Meeting topic',
  'trigger.host': 'Host',
  'trigger.duration_min': 'Duration (min)',
  'trigger.transcript': 'Transcript',
  'trigger.todo_id': 'Todo ID',
  'trigger.todo_title': 'Todo title',
  'trigger.comment_author': 'Comment author',
  'trigger.comment_body': 'Comment',
  'trigger.linked_work_spec': 'Linked work spec',
  'trigger.fired_at': 'Fired at',
  'trigger.error': 'Error',
  'trigger.flow_name': 'Flow name',
  'trigger.document_id': 'Document',
  'trigger.file_path': 'File path',
  'trigger.body.event': 'Event',
  'trigger.body.form_name': 'Form name',
  'trigger.body.email': 'Email',
  'now.date': 'Today',
  'now.iso_date': 'Timestamp',
  'steps.ai_session.text': 'AI reply',
  'steps.ai_session.label': 'Classification',
  'steps.ai_summarize.text': 'Summary',
  'steps.ai_summarize.action_items': 'Action items',
  'steps.inbox_post_thread.thread_id': 'Thread',
  'steps.document_create.document_id': 'Document ID',
  'steps.document_append.ok': 'Appended',
  'steps.human_approve.approved': 'Approved',
  'steps.basecamp_fetch_recent_activity.events': 'Week activity',
  'steps.basecamp_post_comment.url': 'Comment URL',
  folder: 'Folder',
  title: 'Title',
  body: 'Body',
  summary: 'Summary',
  workspace: 'Workspace',
  prompt: 'Prompt',
  tools: 'Tools',
  max_tokens: 'Max tokens',
  text: 'Text',
  style: 'Style',
  document: 'Document',
  section: 'Section',
  content: 'Content',
  context: 'Context',
  when: 'When',
  preview: 'Preview',
  timeout: 'Timeout',
  timeout_action: 'On timeout',
  since_days: 'Since (days)',
  project_filter: 'Project filter',
  message_board: 'Message board',
}

const CAPTURED_PAYLOADS: Record<string, Record<string, string>> = {
  'zoom.transcript_ready': {
    'trigger.meeting_topic': 'GGSC monthly planning',
    'trigger.host': 'rowan@hopstudios.com',
    'trigger.duration_min': '47',
    'trigger.transcript': '"Rowan: ok so the marketing meeting cadence..." (14,208 chars)',
  },
  'basecamp.comment.created': {
    'trigger.todo_id': '9884150565',
    'trigger.todo_title': 'Speedy Static cache hygiene',
    'trigger.comment_author': 'Dan Levine',
    'trigger.comment_body': '"Looks good - approved to proceed with the..." (312 chars)',
    'trigger.linked_work_spec': 'clients/greater-good/greatergood/todos/9884150565-speedy-static-cache-hygiene.md',
  },
  'schedule.cron': {
    'trigger.fired_at': '2026-07-09T08:00:00-07:00',
    'now.date': '2026-07-09',
    'now.iso_date': '2026-07-09T15:00:12.000Z',
  },
  'webhook.received': {
    'trigger.body.event': 'form.submitted',
    'trigger.body.form_name': 'Contact - greatergood.berkeley.edu',
    'trigger.body.email': 'visitor@example.org',
    'trigger.headers.x-signature': 'sha256=9f2c...',
  },
  'flow.run.failed': {
    'trigger.flow_name': 'Zoom meeting notes',
    'trigger.error': 'Unresolved binding: Meeting topic',
  },
  'document.created': {
    'trigger.document_id': 'doc-pilot-feedback-12',
    'trigger.file_path': 'hop-studios/flownotes-pilot/pilot-feedback/2026-07-10.md',
  },
}

const INITIAL_FLOWS: StudioFlow[] = [
  {
    id: 'morning-briefing',
    name: 'Morning briefing',
    slug: 'morning-briefing',
    path: 'Hop Studios/flows',
    trigger: {
      registryId: 'schedule.cron',
      label: 'Schedule',
      detail: '0 8 * * MON-FRI (America/Los_Angeles)',
      plainSummary: 'Runs weekday mornings at 8:00',
    },
    steps: [
      {
        id: 's1',
        registryId: 'ai.session',
        label: 'Review open todos',
        intent: 'Ask the AI to {prompt} using {tools}.',
        inputs: {
          prompt: 'Review my open Basecamp todos and summarise what needs attention today.',
          tools: 'basecamp.fetch_recent_activity',
          max_tokens: '500',
        },
        emits: ['steps.ai_session.text'],
      },
      {
        id: 's2',
        registryId: 'inbox.post_thread',
        label: 'Post briefing to inbox',
        intent: 'Post to inbox titled {summary} with body {body}.',
        inputs: {
          summary: 'Morning briefing - {{now.date}}',
          body: '{{steps.ai_session.text}}',
          workspace: 'hop-studios',
        },
        emits: ['steps.inbox_post_thread.thread_id'],
      },
    ],
    mode: 'live',
    health: 'ok',
    lastRun: 'Today 08:00',
    nextRun: 'Tomorrow 08:00',
    costWeekUsd: 0.14,
  },
  {
    id: 'zoom-meeting-notes',
    name: 'Zoom meeting -> notes',
    slug: 'zoom-auto-meeting-notes',
    path: 'Hop Studios/flows',
    trigger: {
      registryId: 'zoom.transcript_ready',
      label: 'Zoom',
      detail: 'Cloud recording transcript completed',
      plainSummary: 'Runs when a Zoom transcript is ready',
    },
    steps: [
      {
        id: 's1',
        registryId: 'ai.summarize',
        label: 'Summarize transcript',
        intent: 'Summarize {text} as {style}.',
        inputs: { text: '{{trigger.transcript}}', style: 'meeting-notes', max_tokens: '1200' },
        emits: ['steps.ai_summarize.text', 'steps.ai_summarize.action_items'],
      },
      {
        id: 's2',
        registryId: 'document.create',
        label: 'Draft meeting notes',
        intent: 'Create a document in {folder} titled {title} containing {body}.',
        inputs: {
          folder: 'hop-studios/meetings/',
          title: '{{trigger.meeting_topic}} - {{now.date}}',
          body: '{{steps.ai_summarize.text}}',
        },
        emits: ['steps.document_create.document_id'],
      },
      {
        id: 's3',
        registryId: 'inbox.post_thread',
        label: 'Notify inbox',
        intent: 'Post to inbox titled {summary} with body {body}.',
        inputs: {
          summary: 'Meeting notes ready: {{trigger.meeting_topic}}',
          body: '{{steps.ai_summarize.action_items}}',
          workspace: 'hop-studios',
        },
        emits: ['steps.inbox_post_thread.thread_id'],
      },
    ],
    mode: 'live',
    health: 'failed',
    lastRun: '2h ago',
    nextRun: 'Event-driven',
    costWeekUsd: 0.42,
  },
  {
    id: 'basecamp-spec-sync',
    name: 'Basecamp comment -> work spec',
    slug: 'basecamp-spec-sync',
    path: 'Hop Studios/clients/ggsc/automations',
    trigger: {
      registryId: 'basecamp.comment.created',
      label: 'Basecamp',
      detail: 'Comment on todo with linked work spec',
      plainSummary: 'Runs when a Basecamp comment lands on a linked todo',
    },
    steps: [
      {
        id: 's1',
        registryId: 'document.append',
        label: 'Append comment to spec',
        intent: 'Append {content} to {section} in {document}.',
        inputs: {
          document: '{{trigger.linked_work_spec}}',
          section: 'Comments',
          content: '{{trigger.comment_author}}: {{trigger.comment_body}}',
        },
        emits: ['steps.document_append.ok'],
      },
      {
        id: 's2',
        registryId: 'ai.session',
        label: 'Check status contradictions',
        intent: 'Ask the AI: {prompt}',
        inputs: {
          prompt: 'Does this comment contradict the work spec status? Answer changed|unchanged + one line.',
          context: '{{trigger.linked_work_spec}}',
        },
        emits: ['steps.ai_session.label', 'steps.ai_session.text'],
      },
      {
        id: 's3',
        registryId: 'inbox.post_thread',
        label: 'Flag if stale',
        when: '{{steps.ai_session.label}} == "changed"',
        intent: 'If status changed, post inbox titled {summary}.',
        inputs: {
          summary: 'Spec status stale: {{trigger.todo_title}}',
          body: '{{steps.ai_session.text}}',
          workspace: 'hop-studios',
        },
        emits: ['steps.inbox_post_thread.thread_id'],
      },
    ],
    mode: 'draft',
    mine: false,
    health: 'ok',
    lastRun: 'Never',
    nextRun: '-',
    costWeekUsd: 0,
    installedFromGallery: true,
  },
  {
    id: 'client-status-approve',
    name: 'Friday client status',
    slug: 'weekly-client-status',
    path: 'Hop Studios/clients/ggsc/automations',
    trigger: {
      registryId: 'schedule.cron',
      label: 'Schedule',
      detail: '0 15 * * FRI',
      plainSummary: 'Runs Friday afternoons at 15:00',
    },
    steps: [
      {
        id: 's1',
        registryId: 'basecamp.fetch_recent_activity',
        label: 'Fetch week activity',
        intent: 'Fetch Basecamp activity for the last {since_days} days in {project_filter}.',
        inputs: { since_days: '7', project_filter: 'greater-good' },
        emits: ['steps.basecamp_fetch_recent_activity.events'],
      },
      {
        id: 's2',
        registryId: 'ai.summarize',
        label: 'Draft client status',
        intent: 'Draft a {style} from {text}.',
        inputs: {
          text: '{{steps.basecamp_fetch_recent_activity.events}}',
          style: 'client-status',
          max_tokens: '900',
        },
        emits: ['steps.ai_summarize.text'],
      },
      {
        id: 's3',
        registryId: 'human.approve',
        label: 'Ask for approval',
        intent: 'Ask: {prompt}',
        inputs: {
          prompt: 'Post this status to Basecamp?',
          preview: '{{steps.ai_summarize.text}}',
          timeout: '48h',
          timeout_action: 'archive',
        },
        emits: ['steps.human_approve.approved'],
      },
      {
        id: 's4',
        registryId: 'basecamp.post_comment',
        label: 'Post to Basecamp',
        dangerous: true,
        when: '{{steps.human_approve.approved}} == "true"',
        intent: 'Post {body} to {message_board}.',
        inputs: {
          message_board: 'GGSC - Status updates',
          body: '{{steps.ai_summarize.text}}',
        },
        emits: ['steps.basecamp_post_comment.url'],
      },
    ],
    mode: 'shadow',
    health: 'warning',
    lastRun: 'Fri 15:00 (shadow)',
    nextRun: 'Fri 15:00',
    costWeekUsd: 0.09,
  },
  {
    id: 'error-router',
    name: 'Flow error router',
    slug: 'error-router',
    path: 'Hop Studios/flows',
    trigger: {
      registryId: 'flow.run.failed',
      label: 'Flow event',
      detail: 'Any flow run fails',
      plainSummary: 'Runs when another flow fails',
    },
    steps: [
      {
        id: 's1',
        registryId: 'ai.session',
        label: 'Classify failure',
        intent: 'Ask the AI: {prompt}',
        inputs: {
          prompt: 'Classify this flow failure: transient | config | code. Suggest a one-line fix.',
          context: '{{trigger.error}}',
        },
        emits: ['steps.ai_session.label', 'steps.ai_session.text'],
      },
      {
        id: 's2',
        registryId: 'inbox.post_thread',
        label: 'File incident thread',
        intent: 'Post to inbox titled {summary}.',
        inputs: {
          summary: '[{{steps.ai_session.label}}] {{trigger.flow_name}} failed',
          body: '{{steps.ai_session.text}}',
          workspace: 'hop-studios',
        },
        emits: ['steps.inbox_post_thread.thread_id'],
      },
    ],
    mode: 'live',
    systemPaused: 'Event bus v2 (subscriber dispatch) not shipped - saved but cannot receive events yet.',
    health: 'warning',
    lastRun: 'Never',
    nextRun: 'Event-driven',
    costWeekUsd: 0,
  },
]

const FEED: FeedRun[] = [
  {
    id: 'r-running',
    flowId: 'morning-briefing',
    flowName: 'Morning briefing',
    status: 'running',
    when: 'Just now',
    headline: 'Reviewing open todos',
    detail: 'The agent step is still running.',
    costUsd: 0.01,
  },
  {
    id: 'r-team-1',
    flowId: 'basecamp-spec-sync',
    flowName: 'Basecamp comment -> work spec',
    status: 'ok',
    when: '3h ago',
    headline: 'Appended the comment to the work spec',
    detail: 'document.append wrote the Comments section.',
    costUsd: 0.01,
  },
  {
    id: 'r-991',
    flowId: 'morning-briefing',
    flowName: 'Morning briefing',
    status: 'ok',
    when: 'Today 08:00',
    headline: 'Posted briefing to Hop inbox',
    detail: '6 todos need attention; 2 overdue. 412 tokens.',
    costUsd: 0.02,
    threadLink: true,
  },
  {
    id: 'r-shadow-1',
    flowId: 'client-status-approve',
    flowName: 'Friday client status',
    status: 'shadow',
    when: 'Fri 15:02',
    headline: "Would have posted: 'Meeting notes ready: GGSC monthly planning'",
    detail: 'Side effects stubbed. Promote to Live when the draft looks right.',
    costUsd: 0.03,
  },
  {
    id: 'r-990',
    flowId: 'zoom-meeting-notes',
    flowName: 'Zoom meeting -> notes',
    status: 'failed',
    when: '2h ago',
    headline: 'Step 2 failed: unresolved Meeting topic',
    costUsd: 0.04,
    detail: 'Trigger fired via legacy path which did not pass the payload through the executor.',
    failedStep: 'document.create',
    fixHint: 'Replay with captured payload',
  },
  {
    id: 'r-zoom-ok-1',
    flowId: 'zoom-meeting-notes',
    flowName: 'Zoom meeting -> notes',
    status: 'ok',
    when: '1d ago',
    headline: 'Posted notes to inbox',
    detail: 'Matched existing work spec. 4/4 steps done.',
    costUsd: 0.04,
    threadLink: true,
  },
  {
    id: 'r-zoom-ok-2',
    flowId: 'zoom-meeting-notes',
    flowName: 'Zoom meeting -> notes',
    status: 'ok',
    when: '2d ago',
    headline: 'Posted notes to inbox',
    detail: '4/4 steps done.',
    costUsd: 0.04,
    threadLink: true,
  },
  {
    id: 'r-989',
    flowId: 'client-status-approve',
    flowName: 'Friday client status',
    status: 'awaiting_approval',
    when: 'Fri 15:02',
    headline: 'Draft ready - waiting on your approval',
    detail: 'AI drafted 340-word GGSC status. Expires in 31h.',
    costUsd: 0.03,
  },
  {
    id: 'r-988',
    flowId: 'morning-briefing',
    flowName: 'Morning briefing',
    status: 'ok',
    when: 'Yesterday 08:00',
    headline: 'Posted briefing to Hop inbox',
    detail: '4 todos need attention. 366 tokens.',
    costUsd: 0.02,
    threadLink: true,
  },
  {
    id: 'r-987',
    flowId: 'zoom-meeting-notes',
    flowName: 'Zoom meeting -> notes',
    status: 'policy_blocked',
    when: 'Mon 14:22',
    headline: 'Policy blocked: cost cap ($0.25) would be exceeded',
    detail: '92-minute transcript estimated $0.31. Raise the cap or trim the transcript step.',
    costUsd: 0,
  },
  {
    id: 'r-986',
    flowId: 'morning-briefing',
    flowName: 'Morning briefing',
    status: 'warning',
    when: 'Mon 08:00',
    headline: 'Completed with fallback',
    detail: 'Basecamp fetch timed out once; retry succeeded.',
    costUsd: 0.02,
  },
]

const FEED_META: Record<string, RunMeta> = {
  'r-running': {
    mine: true,
    ageHours: 0,
    durationSec: null,
    triggerKind: 'schedule',
    runningForSec: 47,
    agentStepId: 's1',
    conversationLabel: 'Flow agent: Morning briefing',
  },
  'r-team-1': {
    mine: false,
    ageHours: 3,
    durationSec: 8,
    triggerKind: 'event',
    extension: 'Basecamp',
    agentStepId: 's2',
    agentOutput: 'Status unchanged. Comment agrees with the work spec.',
    conversationLabel: 'Flow agent: Basecamp comment -> work spec',
  },
  'r-991': {
    mine: true,
    ageHours: 8,
    durationSec: 42,
    triggerKind: 'schedule',
    extension: 'Basecamp',
    agentStepId: 's1',
    agentOutput: '6 todos need attention. 2 are overdue.',
    conversationLabel: 'Flow agent: Morning briefing',
  },
  'r-shadow-1': {
    mine: true,
    ageHours: 50,
    durationSec: 18,
    triggerKind: 'schedule',
    agentStepId: 's1',
    agentOutput: "Would have posted: 'Meeting notes ready: GGSC monthly planning'",
    conversationLabel: 'Flow agent: Friday client status',
  },
  'r-990': {
    mine: true,
    ageHours: 2,
    durationSec: 11,
    triggerKind: 'event',
    extension: 'Zoom',
  },
  'r-zoom-ok-1': {
    mine: true,
    ageHours: 26,
    durationSec: 36,
    triggerKind: 'event',
    extension: 'Zoom',
    agentStepId: 's1',
    agentOutput: 'Matched the existing work spec and drafted notes.',
    conversationLabel: 'Flow agent: Zoom meeting -> notes',
  },
  'r-zoom-ok-2': {
    mine: true,
    ageHours: 50,
    durationSec: 33,
    triggerKind: 'event',
    extension: 'Zoom',
  },
  'r-989': {
    mine: true,
    ageHours: 52,
    durationSec: 21,
    triggerKind: 'schedule',
    agentStepId: 's1',
    agentOutput: 'Drafted a 340-word GGSC status. Waiting on approval.',
    conversationLabel: 'Flow agent: Friday client status',
  },
  'r-988': {
    mine: true,
    ageHours: 28,
    durationSec: 39,
    triggerKind: 'schedule',
    extension: 'Basecamp',
  },
  'r-987': {
    mine: true,
    ageHours: 80,
    durationSec: 2,
    triggerKind: 'event',
    extension: 'Zoom',
  },
  'r-986': {
    mine: true,
    ageHours: 82,
    durationSec: 55,
    triggerKind: 'schedule',
    extension: 'Basecamp',
  },
}

function runMeta(run: FeedRun): RunMeta {
  return (
    FEED_META[run.id] ?? {
      mine: true,
      ageHours: 48,
      durationSec: 12,
      triggerKind: 'event',
    }
  )
}

function formatDuration(sec: number) {
  const whole = Math.max(0, Math.round(sec))
  if (whole < 60) return `${whole}s`
  const minutes = Math.floor(whole / 60)
  const rest = whole % 60
  return rest ? `${minutes}m ${rest}s` : `${minutes}m`
}

function OwnerScopeSegment({
  value,
  onChange,
}: {
  value: OwnerScope
  onChange: (next: OwnerScope) => void
}) {
  const options: { id: OwnerScope; label: string }[] = [
    { id: 'mine', label: 'Mine' },
    { id: 'team', label: 'Team' },
  ]
  return (
    <div
      className="inline-flex gap-1 rounded-[10px] bg-muted p-[3px]"
      role="tablist"
      aria-label="Owner scope"
      data-testid={testIds.flowsStudioPrototype.pulseScope}
    >
      {options.map(option => {
        const active = value === option.id
        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.id)}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium',
              active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

interface TriggerCatalogEntry {
  registryId: string
  label: string
  group: string
  status: 'live' | 'partial' | 'planned'
  description: string
  payloadFields: { path: string; label: string }[]
  usedBy: string[]
  sample?: string
}

const TRIGGER_CATALOG: TriggerCatalogEntry[] = [
  {
    registryId: 'schedule.cron',
    label: 'Schedule',
    group: 'Schedule',
    status: 'live',
    description: 'Fires on a cron expression with timezone.',
    payloadFields: [
      { path: 'fired_at', label: 'Fired at' },
      { path: 'now.date', label: 'Today' },
    ],
    usedBy: ['Morning briefing', 'Friday client status'],
    sample: "Last fired: Today 08:00 (weekday morning)",
  },
  {
    registryId: 'document.edited',
    label: 'Document edited',
    group: 'Documents',
    status: 'live',
    description: 'A document under a GitSource prefix was saved.',
    payloadFields: [
      { path: 'file_path', label: 'File path' },
      { path: 'document_id', label: 'Document' },
    ],
    usedBy: [],
    sample: "You're not reacting to Git changes yet - 2 recipes use this.",
  },
  {
    registryId: 'document.created',
    label: 'Document created',
    group: 'Documents',
    status: 'planned',
    description: 'New document in a folder (with folder filters).',
    payloadFields: [
      { path: 'file_path', label: 'File path' },
      { path: 'folder', label: 'Folder' },
    ],
    usedBy: [],
  },
  {
    registryId: 'zoom.transcript_ready',
    label: 'Zoom transcript ready',
    group: 'Zoom',
    status: 'partial',
    description: 'Cloud recording transcript completed.',
    payloadFields: [
      { path: 'meeting_topic', label: 'Meeting topic' },
      { path: 'host', label: 'Host' },
      { path: 'duration_min', label: 'Duration' },
      { path: 'transcript', label: 'Transcript' },
    ],
    usedBy: ['Zoom meeting -> notes'],
    sample: "Last seen: 'GGSC monthly planning', 47 min, 2h ago",
  },
  {
    registryId: 'basecamp.todo.updated',
    label: 'Basecamp todo updated',
    group: 'Basecamp',
    status: 'planned',
    description: 'Todo assigned / due date / completion changed.',
    payloadFields: [
      { path: 'todo_title', label: 'Todo title' },
      { path: 'change', label: 'Change' },
    ],
    usedBy: [],
  },
  {
    registryId: 'basecamp.comment.created',
    label: 'Basecamp comment created',
    group: 'Basecamp',
    status: 'planned',
    description: 'New comment on a todo; enriched with linked work spec when one exists.',
    payloadFields: [
      { path: 'todo_title', label: 'Todo title' },
      { path: 'comment_author', label: 'Author' },
      { path: 'comment_body', label: 'Comment' },
    ],
    usedBy: ['Basecamp comment -> work spec'],
  },
  {
    registryId: 'webhook.received',
    label: 'Inbound webhook',
    group: 'Webhooks',
    status: 'planned',
    description: 'Generated URL per flow. Anything that can POST JSON can trigger a flow.',
    payloadFields: [
      { path: 'body.*', label: 'Body fields' },
      { path: 'headers.*', label: 'Headers' },
    ],
    usedBy: [],
    sample: '2 deliveries in the last 3h',
  },
  {
    registryId: 'email.inbound',
    label: 'Inbound email',
    group: 'Email',
    status: 'planned',
    description: 'Forward to your @in.flownotes.app address.',
    payloadFields: [
      { path: 'from', label: 'From' },
      { path: 'subject', label: 'Subject' },
    ],
    usedBy: [],
  },
  {
    registryId: 'git.upstream_changed',
    label: 'Git upstream changed',
    group: 'Git',
    status: 'live',
    description: 'Origin branch moved during a GitSource sync.',
    payloadFields: [
      { path: 'branch', label: 'Branch' },
      { path: 'commits', label: 'Commits' },
    ],
    usedBy: [],
    sample: "You're not reacting to Git changes yet - 2 recipes use this.",
  },
  {
    registryId: 'flow.run.failed',
    label: 'Flow run failed',
    group: 'Flow events',
    status: 'partial',
    description: 'Another flow failed. Subscriber dispatch pending.',
    payloadFields: [
      { path: 'flow_name', label: 'Flow name' },
      { path: 'error', label: 'Error' },
    ],
    usedBy: ['Flow error router'],
  },
  {
    registryId: 'saved_search.matched',
    label: 'Saved search matched',
    group: 'Documents',
    status: 'planned',
    description: 'A saved query gains new results.',
    payloadFields: [
      { path: 'query', label: 'Query' },
      { path: 'new_document_ids', label: 'New docs' },
    ],
    usedBy: [],
  },
]

interface Recipe {
  id: string
  name: string
  oneLiner: string
  group: string
  triggerLabel: string
  stepLabels: string[]
  /** Capability keys checked against the install-target workspace */
  requires: { label: string; cap: string }[]
  /** Optional registry id for a mock trigger row */
  triggerRegistryId?: string
}

/**
 * Real FlowNotes workspaces (orgs), not client folders.
 * GGSC is under hop-studios/clients/ - not a workspace. Basecamp extension is Hop-only.
 */
const PROTO_WORKSPACES: Workspace[] = [
  {
    id: 'ws-fp',
    name: 'FlowNotes Product',
    slug: 'flownotes-product',
    type: 'work',
    icon: 'Rocket',
    color: '#22c55e',
    contentRoot: 'flownotes-app-docs',
    showInSidebar: true,
  },
  {
    id: 'ws-hop',
    name: 'Hop Studios',
    slug: 'hop-studios',
    type: 'work',
    icon: 'RubberDuck',
    color: '#3b82f6',
    contentRoot: 'hop-studios',
    showInSidebar: true,
  },
  {
    id: 'ws-kl',
    name: 'Kootenay Lightweight',
    slug: 'kootenay-lightweight',
    type: 'client',
    icon: 'Briefcase',
    color: '#a855f7',
    contentRoot: 'kootenay-lightweight',
    showInSidebar: true,
  },
  {
    id: 'ws-personal',
    name: 'Personal',
    slug: 'personal',
    type: 'personal',
    icon: 'User',
    color: '#e11d48',
    contentRoot: 'personal',
    showInSidebar: true,
  },
]

/** Per-workspace extension / capability mock. Basecamp lives on Hop Studios only. */
const WORKSPACE_CAPS: Record<string, string[]> = {
  'ws-fp': ['llm'],
  'ws-hop': [
    'llm',
    'zoom',
    'zoom_secrets',
    'basecamp',
    'basecamp_webhooks',
    'human_approve',
    'webhook',
    'outage_sop',
    'cursor',
  ],
  'ws-kl': ['llm'],
  'ws-personal': ['llm', 'webhook'],
}

/** Recipe group -> primary extension cap; hide group when install target lacks it. */
const GROUP_EXTENSION_GATE: Record<string, string> = {
  Basecamp: 'basecamp',
  Zoom: 'zoom',
}

function capsFor(workspaceId: string): string[] {
  return WORKSPACE_CAPS[workspaceId] ?? []
}

function flowsPathFor(ws: Workspace): string {
  const root = (ws.contentRoot ?? ws.slug).replace(/\/+$/, '')
  return `${root}/flows`
}

const RECIPES: Recipe[] = [
  {
    id: 'rec-briefing',
    name: 'Morning briefing',
    group: 'Getting started',
    oneLiner: 'One AI prompt on a schedule, result in your inbox.',
    triggerLabel: 'Schedule',
    stepLabels: ['AI session', 'Post to inbox'],
    requires: [{ label: 'LLM provider configured', cap: 'llm' }],
  },
  {
    id: 'rec-zoom',
    name: 'Meeting -> notes + action items',
    group: 'Zoom',
    oneLiner: 'Every recorded meeting becomes a filed notes doc with action items in your inbox.',
    triggerLabel: 'Zoom transcript ready',
    stepLabels: ['Summarize', 'Create document', 'Post to inbox'],
    requires: [
      { label: 'Zoom extension enabled', cap: 'zoom' },
      { label: 'ZOOM_* workspace secrets', cap: 'zoom_secrets' },
    ],
  },
  {
    id: 'rec-spec-sync',
    name: 'Self-refreshing work specs',
    group: 'Basecamp',
    oneLiner: 'Client comments append to the linked work spec and flag stale statuses.',
    triggerLabel: 'Basecamp comment',
    stepLabels: ['Append to document', 'AI session', 'Post to inbox'],
    requires: [
      { label: 'Basecamp extension enabled', cap: 'basecamp' },
      { label: 'Basecamp webhooks', cap: 'basecamp_webhooks' },
    ],
  },
  {
    id: 'rec-status',
    name: 'Client status with approval gate',
    group: 'Basecamp',
    oneLiner: 'AI drafts the Friday status; you approve; it posts to Basecamp.',
    triggerLabel: 'Schedule',
    stepLabels: ['Fetch activity', 'Summarize', 'Ask for approval', 'Post to Basecamp'],
    requires: [
      { label: 'Basecamp extension enabled', cap: 'basecamp' },
      { label: 'Approval step', cap: 'human_approve' },
    ],
  },
  {
    id: 'rec-uptime',
    name: 'Uptime alert triage',
    group: 'Webhooks + HTTP',
    oneLiner: 'Uptime webhook alerts enriched with client context and routed to the week watcher.',
    triggerLabel: 'Inbound webhook',
    stepLabels: ['AI session', 'Post to inbox'],
    requires: [
      { label: 'Webhook trigger', cap: 'webhook' },
      { label: 'Outage SOP in workspace', cap: 'outage_sop' },
    ],
  },
  {
    id: 'rec-error-router',
    name: 'Flow error router',
    group: 'Meta',
    oneLiner: 'When any flow fails, AI classifies it and files a routed incident.',
    triggerLabel: 'Flow run failed',
    stepLabels: ['AI session', 'Post to inbox'],
    requires: [{ label: 'Event bus v2', cap: 'event_bus_v2' }],
  },
]

const COMPOSER_PREVIEW_STEPS: StudioStep[] = [
  {
    id: 'c1',
    registryId: 'ai.session',
    label: 'Classify feedback',
    intent: 'Ask the AI to classify severity and draft a summary from {context}.',
    inputs: { context: '{{trigger.document_id}}', prompt: 'Classify P0-P3 and area.' },
    emits: ['steps.ai_session.label', 'steps.ai_session.text'],
  },
  {
    id: 'c2',
    registryId: 'inbox.post_thread',
    label: 'Post triage to inbox',
    intent: 'Post to inbox titled {summary}.',
    inputs: {
      summary: 'Pilot feedback [{{steps.ai_session.label}}]: {{trigger.file_path}}',
      body: '{{steps.ai_session.text}}',
    },
    emits: ['steps.inbox_post_thread.thread_id'],
  },
]

const COMPOSER_REFINE_STEP: StudioStep = {
  id: 'c1b',
  registryId: 'ai.session',
  label: 'Shape filing suggestions',
  intent: 'Ask the AI to shape filing suggestions from {context}.',
  inputs: {
    context: '{{steps.ai_session.text}}',
    prompt: 'Suggest where to file this and a short action list.',
  },
  emits: ['steps.shape_suggestions.text'],
}

const COMPOSER_EXAMPLE_YAML = `slug: pilot-feedback-triage
kind: flow
triggers:
  - id: document.created
    filters:
      folder: 'hop-studios/flownotes-pilot/pilot-feedback/'
steps:
  - id: ai.session
    inputs:
      prompt: Classify severity (P0-P3) and area.
      context: '{{trigger.document_id}}'
  - id: inbox.post_thread
    inputs:
      summary: 'Pilot feedback [{{steps.ai_session.label}}]'
      body: '{{steps.ai_session.text}}'
policy:
  cost_cap_usd: 0.10`

const COMPOSER_REFINED_YAML = `slug: pilot-feedback-triage
kind: flow
triggers:
  - id: document.created
    filters:
      folder: 'hop-studios/flownotes-pilot/pilot-feedback/'
steps:
  - id: ai.session
    inputs:
      prompt: Classify severity (P0-P3) and area.
      context: '{{trigger.document_id}}'
  - id: ai.session
    inputs:
      prompt: Shape filing suggestions and a short action list.
      context: '{{steps.ai_session.text}}'
  - id: inbox.post_thread
    inputs:
      summary: 'Pilot feedback [{{steps.ai_session.label}}]'
      body: '{{steps.ai_session.text}}\n\n{{steps.shape_suggestions.text}}'
policy:
  cost_cap_usd: 0.10`

function composerRefinedSteps(): StudioStep[] {
  return [COMPOSER_PREVIEW_STEPS[0], COMPOSER_REFINE_STEP, COMPOSER_PREVIEW_STEPS[1]]
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function bareToken(raw: string): string {
  return raw.replace(/^\{\{\s*|\s*\}\}$/g, '').trim()
}

function humanLabel(pathOrToken: string): string {
  const bare = bareToken(pathOrToken)
  if (FIELD_LABELS[bare]) return FIELD_LABELS[bare]
  if (FIELD_LABELS[pathOrToken]) return FIELD_LABELS[pathOrToken]
  const leaf = bare.split('.').pop() ?? bare
  return leaf.replace(/_/g, ' ')
}

function modeLabel(mode: RuntimeMode) {
  if (mode === 'live') return 'Live'
  if (mode === 'shadow') return 'Shadow'
  return 'Draft'
}

const FLOW_FOLDER_ICON_COLOR = 'hsl(var(--primary) / 0.8)'

function flowFolderIcon(expanded: boolean) {
  const Icon = expanded ? FolderOpen : Folder
  return <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: FLOW_FOLDER_ICON_COLOR }} />
}

function flowRowIcon() {
  return getDocumentDisplayIcon({ kind: 'flow' }, { className: 'h-3.5 w-3.5 shrink-0' })
}

function modeBadge(mode: RuntimeMode) {
  if (mode === 'live') {
    return <StudioStatusPill className={BADGE_LIVE_CLASS}>Live</StudioStatusPill>
  }
  if (mode === 'shadow') {
    return <StudioStatusPill className={BADGE_SHADOW_CLASS}>Shadow</StudioStatusPill>
  }
  return <StudioStatusPill className={BADGE_DRAFT_CLASS}>Draft</StudioStatusPill>
}

function statusBadge(status: RunStatus) {
  const map: Record<RunStatus, { label: string; cls: string }> = {
    ok: { label: 'Success', cls: 'bg-green-500/10 text-green-500 border-green-500/20' },
    warning: { label: 'Warning', cls: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
    failed: { label: 'Failed', cls: 'bg-red-500/10 text-red-500 border-red-500/20' },
    policy_blocked: { label: 'Policy blocked', cls: 'bg-orange-500/10 text-orange-500 border-orange-500/20' },
    awaiting_approval: { label: 'Awaiting approval', cls: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
    shadow: { label: 'Shadow preview', cls: 'border-dashed border-primary/40 bg-primary/10 text-primary' },
    running: { label: 'Running', cls: 'bg-primary/10 text-primary border-primary/20' },
  }
  const v = map[status]
  return <StudioStatusPill className={cn('border', v.cls)}>{v.label}</StudioStatusPill>
}

function sourceStatusBadge(status: TriggerCatalogEntry['status']) {
  if (status === 'live') {
    return <StudioStatusPill className={BADGE_LIVE_CLASS}>Available</StudioStatusPill>
  }
  if (status === 'partial') {
    return (
      <StudioStatusPill className="border border-yellow-500/20 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400">
        Partial
      </StudioStatusPill>
    )
  }
  return <StudioStatusPill className={BADGE_DRAFT_CLASS}>Coming</StudioStatusPill>
}

function groupIcon(group: string) {
  switch (group) {
    case 'Schedule':
      return <Clock className="h-4 w-4" />
    case 'Zoom':
      return <Video className="h-4 w-4" />
    case 'Basecamp':
      return <Inbox className="h-4 w-4" />
    case 'Webhooks':
      return <Webhook className="h-4 w-4" />
    case 'Webhooks + HTTP':
      return <Globe className="h-4 w-4" />
    case 'Email':
      return <Radio className="h-4 w-4" />
    case 'Git':
      return <GitBranch className="h-4 w-4" />
    case 'Documents':
      return <BookOpen className="h-4 w-4" />
    case 'Flow events':
      return <Zap className="h-4 w-4" />
    case 'Meta':
      return <Bot className="h-4 w-4" />
    case 'Getting started':
      return <Sparkles className="h-4 w-4" />
    default:
      return <Mic className="h-4 w-4" />
  }
}

const TOKEN_SPLIT_RE = /(\{\{[^}]+\}\})/g
const TOKEN_MATCH_RE = /^\{\{[^}]+\}\}$/

const CONTINUITY_GUIDE = 'docs/design/maater-list-surface-continuity.md'

function ConceptNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex gap-2 border-t border-border/60 pt-3 text-sm leading-relaxed text-muted-foreground/80">
      <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>{children}</span>
    </p>
  )
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T
  onChange: (v: T) => void
  options: { id: T; label: React.ReactNode }[]
}) {
  return (
    <MaaterProtoSegmentTrack
      value={value}
      onChange={(id) => onChange(id as T)}
      options={options.map((o) => ({ id: o.id, label: o.label }))}
    />
  )
}

/** Binding chip: human label, no braces. Path in title tooltip. Span only - never nest in buttons. */
function BindingChip({
  token,
  variant = 'bind',
  title,
  onClick,
}: {
  token: string
  variant?: 'bind' | 'emit' | 'literal'
  title?: string
  onClick?: () => void
}) {
  const bare = bareToken(token)
  const label = humanLabel(bare)
  const tip = title ?? (TOKEN_MATCH_RE.test(token.trim()) || bare.includes('.') ? bare : undefined)
  if (variant === 'literal') {
    return <span className="font-medium text-foreground">{token}</span>
  }
  return (
    <span
      title={tip}
      onClick={onClick}
      onKeyDown={
        onClick
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
              }
            }
          : undefined
      }
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      className={cn(
        variant === 'emit' ? CHIP_EMIT_CLASS : CHIP_BIND_CLASS,
        onClick && 'cursor-pointer',
      )}
    >
      {label}
    </span>
  )
}

function TokenizedValue({
  value,
  onChipClick,
}: {
  value: string
  onChipClick?: () => void
}) {
  const parts = value.split(TOKEN_SPLIT_RE)
  return (
    <span className="break-words text-xs leading-relaxed">
      {parts.map((part, i) =>
        TOKEN_MATCH_RE.test(part) ? (
          <BindingChip key={i} token={part} onClick={onChipClick} />
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </span>
  )
}

function isBoundValue(raw: string): boolean {
  return /\{\{/.test(raw)
}

/** Sentence-form: intent template with {field} slots replaced by chips/literals */
function IntentSentence({
  step,
  onFieldClick,
}: {
  step: StudioStep
  onFieldClick?: (field: string) => void
}) {
  const template = step.intent ?? Object.keys(step.inputs).map(k => `{${k}}`).join(' | ')
  const parts = template.split(/(\{[^}]+\})/g)
  return (
    <p className="text-[15px] leading-relaxed text-foreground/90">
      {parts.map((part, i) => {
        const m = part.match(/^\{([^}]+)\}$/)
        if (!m) return <span key={i}>{part}</span>
        const field = m[1]
        const raw = step.inputs[field]
        if (raw == null) return <span key={i} className="text-muted-foreground">[{field}]</span>
        const isToken = isBoundValue(raw)
        return (
          <span
            key={i}
            className={cn('mx-0.5 inline align-baseline', onFieldClick && 'cursor-pointer')}
            onClick={() => onFieldClick?.(field)}
            onKeyDown={
              onFieldClick
                ? e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onFieldClick(field)
                    }
                  }
                : undefined
            }
            role={onFieldClick ? 'button' : undefined}
            tabIndex={onFieldClick ? 0 : undefined}
          >
            {isToken ? (
              <TokenizedValue value={raw} />
            ) : (
              <span
                className="rounded px-1 py-0.5 font-medium underline decoration-dotted underline-offset-2 hover:bg-muted/50"
                title={raw}
              >
                {raw.length > 56 ? `${raw.slice(0, 56)}...` : raw}
              </span>
            )}
          </span>
        )
      })}
    </p>
  )
}

function resolveSample(value: string, payload: Record<string, string>): string | null {
  const bare = bareToken(value.trim())
  if (payload[bare]) return payload[bare]
  if (value.includes('{{')) {
    const tokens = value.match(/\{\{[^}]+\}\}/g)
    if (tokens?.length === 1 && payload[bareToken(tokens[0])]) {
      return payload[bareToken(tokens[0])]
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// FlowPipeline - one component, definition | execution, full | mini
// ---------------------------------------------------------------------------

function stepStateLine(
  state: StepRunState,
  copy: 'run' | 'simulate' = 'simulate',
): { cls: string; icon: React.ReactNode; text: string } {
  switch (state) {
    case 'ok':
      return {
        cls: 'text-green-500',
        icon: <Check className="h-3 w-3" />,
        text: copy === 'run' ? 'Done' : 'Ran against captured payload',
      }
    case 'failed':
      return { cls: 'text-red-500', icon: <AlertTriangle className="h-3 w-3" />, text: 'Failed on this run' }
    case 'skipped':
      return { cls: 'text-muted-foreground', icon: <PauseCircle className="h-3 w-3" />, text: 'Skipped (run stopped earlier)' }
    case 'stubbed':
      return { cls: 'text-primary', icon: <Eye className="h-3 w-3" />, text: 'Side effect stubbed - preview only' }
    case 'running':
      return {
        cls: 'text-primary',
        icon: <Loader2 className="h-3 w-3 animate-spin" />,
        text: 'Running',
      }
    default:
      return { cls: 'text-muted-foreground', icon: <Clock className="h-3 w-3" />, text: 'Pending' }
  }
}

function defaultSimulateStates(steps: StudioStep[]): Record<string, StepRunState> {
  const out: Record<string, StepRunState> = {}
  for (const s of steps) {
    out[s.id] =
      s.dangerous || s.registryId === 'inbox.post_thread' || s.registryId === 'basecamp.post_comment'
        ? 'stubbed'
        : 'ok'
  }
  return out
}

type StudioPageWidth = 'default' | 'full'

const StudioPageWidthContext = createContext<{
  mode: StudioPageWidth
  toggle: () => void
}>({ mode: 'default', toggle: () => {} })

function useStudioPageWidth() {
  return useContext(StudioPageWidthContext)
}

/** Column keeps each surface's own measure. Full drops the cap, same as documents. */
function studioPageWidthClass(mode: StudioPageWidth, columnClass: string) {
  return mode === 'full' ? 'max-w-none' : columnClass
}

const FOOTER_TOGGLE_CLASS =
  'flex items-center justify-center gap-1.5 rounded-sm px-2 py-1 text-xs transition-colors'

function StudioPageWidthToggle() {
  const { mode, toggle } = useStudioPageWidth()
  const title =
    mode === 'default' ? 'Switch to full width' : 'Switch to reading column (760px)'
  return (
    <button
      type="button"
      aria-label={title}
      className={cn(FOOTER_TOGGLE_CLASS, 'shrink-0 text-muted-foreground hover:bg-muted/50 hover:text-foreground')}
      title={title}
      onClick={toggle}
      data-testid={testIds.documentContentStatusBar.pageWidthToggle}
    >
      {mode === 'default' ? (
        <AlignCenter className="h-3.5 w-3.5" aria-hidden />
      ) : (
        <StretchHorizontal className="h-3.5 w-3.5" aria-hidden />
      )}
      <span>{mode === 'default' ? 'Column' : 'Full'}</span>
    </button>
  )
}

function DefinitionViewToggle({
  value,
  onChange,
}: {
  value: 'visual' | 'source'
  onChange: (value: 'visual' | 'source') => void
}) {
  const options = [
    { id: 'visual' as const, label: 'Visual', icon: Eye, title: 'Visual pipeline' },
    { id: 'source' as const, label: 'Source', icon: Code, title: 'Source YAML' },
  ]
  return (
    <div
      className="inline-flex shrink-0 items-center rounded-md border border-border p-0.5"
      role="tablist"
      aria-label="Definition view"
    >
      {options.map(option => {
        const selected = value === option.id
        const Icon = option.icon
        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={selected}
            title={option.title}
            className={cn(
              FOOTER_TOGGLE_CLASS,
              selected
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
            )}
            onClick={() => onChange(option.id)}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden />
            <span>{option.label}</span>
          </button>
        )
      })}
    </div>
  )
}

/** Zoom failure demo: step 1 ok, step 2 failed, step 3 never ran */
const ZOOM_FAILED_RUN_STATES: Record<string, StepRunState> = {
  s1: 'ok',
  s2: 'failed',
  s3: 'skipped',
}

function FlowPipeline({
  flow,
  mode,
  density = 'full',
  activeStepId,
  onSelectStep,
  payload,
  stepStates,
  statusCopy = 'simulate',
  inboxThreadStepIds,
  onViewInbox,
  runningForSec,
  markedStepId,
  washingStepId,
  proposalRole,
}: {
  flow: Pick<StudioFlow, 'trigger' | 'steps' | 'name'> & {
    lastRun?: string
    nextRun?: string
  }
  mode: PipelineMode
  density?: PipelineDensity
  activeStepId?: string
  onSelectStep?: (id: string) => void
  payload?: Record<string, string>
  /** When set in execution mode, drives per-step status (do not infer from registry alone) */
  stepStates?: Record<string, StepRunState>
  /** Historical run vs simulate overlay - changes ok-step copy */
  statusCopy?: 'run' | 'simulate'
  /** Inbox steps that posted a thread on this run */
  inboxThreadStepIds?: string[]
  onViewInbox?: (stepId: string) => void
  /** Elapsed seconds for the step that is running right now */
  runningForSec?: number
  /** Step Maater last changed. The mark stays until a person edits that step. */
  markedStepId?: string
  /** Step currently in the one-shot wash. */
  washingStepId?: string
  /** Proposal diff. Added cards rest green. Removed cards rest red. */
  proposalRole?: Record<string, 'add' | 'remove'>
}) {
  const pl = payload ?? CAPTURED_PAYLOADS[flow.trigger.registryId] ?? {}
  const isMini = density === 'mini'
  const isExec = mode === 'execution'
  const { mode: pageWidthMode } = useStudioPageWidth()
  const states = stepStates ?? (isExec ? defaultSimulateStates(flow.steps) : undefined)

  return (
    <div
      className={cn(
        'mx-auto w-full space-y-0',
        isMini ? 'max-w-lg' : studioPageWidthClass(pageWidthMode, 'max-w-[760px]'),
      )}
      data-testid={testIds.flowsStudioPrototype.flowPipeline}
    >
      <div
        data-flow-node={FLOW_TRIGGER_NODE}
        role={onSelectStep ? 'button' : undefined}
        tabIndex={onSelectStep ? 0 : undefined}
        onClick={e => {
          if (!onSelectStep) return
          e.stopPropagation()
          onSelectStep(FLOW_TRIGGER_NODE)
        }}
        onKeyDown={e => {
          if (!onSelectStep) return
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            e.stopPropagation()
            onSelectStep(FLOW_TRIGGER_NODE)
          }
        }}
        className={cn(
          'rounded-lg border',
          isMini ? 'p-2.5' : 'p-4',
          onSelectStep && 'cursor-pointer transition-colors',
          activeStepId === FLOW_TRIGGER_NODE
            ? 'border-primary bg-primary/5'
            : onSelectStep
              ? 'border-primary/30 bg-primary/5 hover:border-primary/50'
              : 'border-primary/30 bg-primary/5',
        )}
      >
        <div className="mb-1 flex items-center gap-2">
          <Zap className="h-4 w-4 text-primary" />
          <span className={cn('font-semibold', isMini ? 'text-xs' : 'text-sm')}>
            {flow.trigger.label}
          </span>
        </div>
        <p className={cn('text-muted-foreground', isMini ? 'text-xs' : 'text-[15px] leading-relaxed')}>
          {flow.trigger.plainSummary}
        </p>
        {!isMini && (flow.lastRun || flow.nextRun) && (
          <p className="mt-1.5 text-sm text-muted-foreground">
            Last {flow.lastRun ?? 'Never'} · Next {flow.nextRun ?? '-'}
          </p>
        )}
        {!isMini && (
          <div className="mt-2 flex flex-wrap gap-1">
            {Object.keys(pl)
              .slice(0, 4)
              .map(k => (
                <BindingChip key={k} token={k} title={isExec ? pl[k] : k} />
              ))}
          </div>
        )}
        {isExec && pl['trigger.meeting_topic'] && (
          <p className="mt-2 text-sm text-muted-foreground">
            Received: <span className="text-foreground">{pl['trigger.meeting_topic']}</span>
          </p>
        )}
      </div>

      {flow.steps.map((s, idx) => {
        const prev = idx > 0 ? flow.steps[idx - 1] : null
        const connectorSample =
          isExec && prev && states?.[prev.id] === 'ok'
            ? resolveSample(Object.values(s.inputs).find(v => v.includes('{{steps.')) ?? '', pl) ??
              (prev.emits[0] ? humanLabel(prev.emits[0]) : null)
            : null
        const st = states?.[s.id]
        const stLine = st ? stepStateLine(st, statusCopy) : null
        const showInboxLink = Boolean(inboxThreadStepIds?.includes(s.id) && st === 'ok')
        const proposal = proposalRole?.[s.id]
        const proposalAdd = proposal === 'add'
        const proposalRemove = proposal === 'remove'

        return (
          <div key={s.id}>
            <div className="flex flex-col items-center py-1">
              <div className="h-3 w-px bg-border" />
              {isExec && connectorSample && !isMini && (
                <span className="my-0.5 max-w-[90%] truncate rounded bg-muted/60 px-2 py-0.5 text-xs text-muted-foreground">
                  {connectorSample}
                </span>
              )}
              {s.when && !isMini && (
                <span className="my-0.5 rounded border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-400">
                  if {humanLabel(s.when.split(' ')[0] ?? s.when)}
                </span>
              )}
              <div className="h-3 w-px bg-border" />
            </div>
            <div
              data-flow-node={s.id}
              role="button"
              tabIndex={proposalRemove ? -1 : 0}
              onClick={e => {
                e.stopPropagation()
                if (proposalRemove) return
                onSelectStep?.(s.id)
              }}
              onKeyDown={e => {
                if (proposalRemove) return
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  onSelectStep?.(s.id)
                }
              }}
              className={cn(
                'w-full text-left rounded-lg border transition-colors duration-200',
                proposalRemove ? 'cursor-default' : 'cursor-pointer',
                isMini ? 'p-2.5' : 'p-4',
                proposalAdd &&
                  'border-green-700/40 bg-green-600/[0.16] dark:border-green-400/75 dark:bg-green-500/[0.24]',
                proposalRemove &&
                  'border-red-700/35 bg-red-600/[0.12] dark:border-red-400/70 dark:bg-red-500/20',
                !proposal &&
                  (washingStepId === s.id
                    ? 'border-primary bg-primary/15'
                    : activeStepId === s.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/40'),
                s.dangerous && !proposal && 'border-dashed',
                st === 'failed' && !proposal && 'border-red-500/40 bg-red-500/5',
                st === 'running' && !proposal && 'border-primary/45 bg-primary/5',
              )}
            >
              <div className="mb-1.5 flex items-center gap-2">
                <span className={cn(
                  'flex shrink-0 items-center justify-center rounded-full bg-muted font-bold',
                  isMini ? 'h-5 w-5 text-[10px]' : 'h-6 w-6 text-xs',
                )}>
                  {idx + 1}
                </span>
                <span
                  className={cn(
                    'flex-1 font-semibold',
                    isMini ? 'text-xs' : 'text-[15px]',
                    proposalRemove && 'text-red-950/80 line-through decoration-red-700/45 dark:text-red-100/80 dark:decoration-red-400/60',
                  )}
                >
                  {s.label}
                </span>
                {markedStepId === s.id && (
                  <Sparkles
                    className={cn(
                      'h-3.5 w-3.5 shrink-0',
                      proposalAdd ? 'text-green-600 dark:text-green-400' : 'text-primary',
                    )}
                    aria-label="Maater changed this step"
                    data-testid={testIds.flowsStudioPrototype.flowAgentMark}
                  />
                )}
                {st === 'running' && !isExec && (
                  <span className="inline-flex shrink-0 items-center gap-1.5">
                    {statusBadge('running')}
                    {runningForSec != null && (
                      <span className="text-xs text-muted-foreground">
                        <LiveDuration baseSec={runningForSec} />
                      </span>
                    )}
                  </span>
                )}
                {s.dangerous && (
                  <StudioStatusPill className="h-5 border border-red-500/20 bg-red-500/10 px-1 text-xs text-red-600 dark:text-red-400">
                    <ShieldAlert className="mr-0.5 h-2.5 w-2.5" /> posts out
                  </StudioStatusPill>
                )}
              </div>
              {!isMini && <IntentSentence step={s} />}
              {isMini && (
                <p className="line-clamp-2 text-xs text-muted-foreground">
                  {s.intent?.replace(/\{(\w+)\}/g, (_, k) => humanLabel(s.inputs[k] ?? k)) ?? s.label}
                </p>
              )}
              {isExec && stLine && (
                <p className={cn('mt-2 flex items-center gap-1 text-sm', stLine.cls)}>
                  {stLine.icon}
                  {stLine.text}
                  {st === 'running' && runningForSec != null && (
                    <span className="text-muted-foreground">
                      <LiveDuration baseSec={runningForSec} />
                    </span>
                  )}
                  {st === 'failed' && s.registryId === 'document.create' && (
                    <span className="text-muted-foreground"> - unresolved Meeting topic</span>
                  )}
                </p>
              )}
              {showInboxLink && (
                <button
                  type="button"
                  className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                  onClick={e => {
                    e.stopPropagation()
                    onSelectStep?.(s.id)
                    onViewInbox?.(s.id)
                  }}
                >
                  <Inbox className="h-3 w-3" />
                  View in inbox
                </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pulse
// ---------------------------------------------------------------------------

function LiveDuration({ baseSec }: { baseSec: number }) {
  const [extra, setExtra] = useState(0)
  useEffect(() => {
    const started = Date.now()
    const id = window.setInterval(() => {
      setExtra(Math.floor((Date.now() - started) / 1000))
    }, 1000)
    return () => window.clearInterval(id)
  }, [baseSec])
  return <span className="tabular-nums">{formatDuration(baseSec + extra)}</span>
}

type PulseTraceProps = {
  run: FeedRun
  flow?: StudioFlow
  onFix: (flowId: string, stepRegistryId?: string) => void
  onOpenTranscript: (run: FeedRun) => void
}

function PulseRunTrace({ run, flow, onFix, onOpenTranscript, stepsOnly }: PulseTraceProps & { stepsOnly?: boolean }) {
  const meta = runMeta(run)
  const [expanded, setExpanded] = useState<string | null>(meta.agentStepId ?? null)
  const states = flow ? stepStatesForRun(run, flow) : undefined
  const steps = flow?.steps ?? []

  return (
    <div className="space-y-3">
      {!stepsOnly && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {statusBadge(run.status)}
            <span className="text-xs text-muted-foreground">{run.when}</span>
            {meta.durationSec != null && (
              <span className="text-xs text-muted-foreground">{formatDuration(meta.durationSec)}</span>
            )}
            {meta.runningForSec != null && <LiveDuration baseSec={meta.runningForSec} />}
          </div>
          <p className="text-sm font-medium text-foreground">{run.headline}</p>
        </>
      )}
      <div className="space-y-1">
        {steps.map((step, index) => {
          const state = states?.[step.id]
          const isAgent = step.registryId.startsWith('ai.')
          const open = expanded === step.id
          const each =
            meta.durationSec != null && steps.length > 0
              ? Math.max(1, Math.round(meta.durationSec / steps.length))
              : null
          return (
            <div key={step.id} className="rounded-md border border-border/70">
              <button
                type="button"
                className="flex w-full items-center gap-2 px-2 py-1.5 text-left"
                onClick={() => setExpanded(open ? null : step.id)}
              >
                <span className="w-4 text-xs text-muted-foreground">{index + 1}</span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{step.label}</span>
                <span className="text-xs text-muted-foreground">
                  {state === 'running' || (run.status === 'running' && step.id === meta.agentStepId)
                    ? 'running'
                    : state === 'failed'
                      ? 'failed'
                      : state === 'skipped'
                        ? 'skipped'
                        : each != null && state !== 'pending'
                          ? formatDuration(each)
                          : ''}
                </span>
              </button>
              {open && (
                <div className="space-y-2 border-t border-border/60 px-2 py-2">
                  {isAgent ? (
                    <>
                      <p className="text-sm text-muted-foreground">
                        {meta.agentOutput || (run.status === 'running' ? 'Still running.' : run.detail)}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => onOpenTranscript(run)}
                      >
                        <MessageSquare className="mr-1 h-3 w-3" />
                        Open transcript
                      </Button>
                    </>
                  ) : (
                    <ul className="space-y-1">
                      {Object.entries(step.inputs).map(([key, value]) => (
                        <li key={key} className="text-sm text-muted-foreground">
                          <span className="text-foreground">{key}:</span> {value}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )
        })}
        {steps.length === 0 && (
          <p className="text-sm text-muted-foreground">{run.detail}</p>
        )}
      </div>
      {run.status === 'failed' && run.fixHint && (
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          onClick={() => onFix(run.flowId, run.failedStep)}
        >
          <Wand2 className="mr-1 h-3 w-3" /> {run.fixHint}
        </Button>
      )}
    </div>
  )
}

function PreviewField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="mt-0.5 text-sm">{children}</div>
    </div>
  )
}

function PulseRunPreview({
  run,
  flow,
  onOpen,
  onClose,
  onSelectRun,
  onFix,
  onOpenTranscript,
}: PulseTraceProps & {
  onOpen: () => void
  onClose: () => void
  onSelectRun: (run: FeedRun) => void
}) {
  const meta = runMeta(run)
  const flowRuns = FEED.filter(item => item.flowId === run.flowId).slice(0, 6)
  const hiddenRuns = FEED.filter(item => item.flowId === run.flowId).length - flowRuns.length

  return (
    <div
      className="flex h-full min-h-0 flex-col border-l border-border bg-background"
      data-testid={testIds.flowsStudioPrototype.pulseRunDetail}
    >
      <ShellIndexRow title={run.flowName} className="bg-background">
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onOpen}>
            Open flow
          </Button>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
            title="Close preview"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </ShellIndexRow>
      <div className="min-h-0 flex-1 space-y-6 overflow-auto px-4 py-4">
        <section className="space-y-3">
          <h2 className="text-sm font-semibold">This run</h2>
          <div className="flex flex-wrap items-center gap-2">
            {statusBadge(run.status)}
            <span className="text-xs text-muted-foreground">{run.when}</span>
            {meta.durationSec != null && (
              <span className="text-xs text-muted-foreground">{formatDuration(meta.durationSec)}</span>
            )}
            {meta.runningForSec != null && <LiveDuration baseSec={meta.runningForSec} />}
            {run.costUsd != null && (
              <span className="text-xs text-muted-foreground">${run.costUsd.toFixed(2)}</span>
            )}
          </div>
          <div>
            <p className="text-sm font-medium">{run.headline}</p>
            <p className="mt-1 text-sm text-muted-foreground">{run.detail}</p>
          </div>
          <PulseRunTrace
            run={run}
            flow={flow}
            onFix={onFix}
            onOpenTranscript={onOpenTranscript}
            stepsOnly
          />
        </section>

        {flow && (
          <section className="space-y-3 border-t border-border pt-4">
            <h2 className="text-sm font-semibold">This flow</h2>
            <PreviewField label="Mode">{modeBadge(flow.mode)}</PreviewField>
            <PreviewField label="Trigger">{flow.trigger.plainSummary}</PreviewField>
            <PreviewField label="Last / Next">{`${flow.lastRun} / ${flow.nextRun}`}</PreviewField>
            <PreviewField label="Spend this week">${flow.costWeekUsd.toFixed(2)}</PreviewField>
            <PreviewField label="Steps">{String(flow.steps.length)}</PreviewField>
          </section>
        )}

        {flowRuns.length > 0 && (
          <section
            className="space-y-2 border-t border-border pt-4"
            data-testid={testIds.flowsStudioPrototype.pulsePreviewRuns}
          >
            <h2 className="text-sm font-semibold">Runs</h2>
            <div className="divide-y divide-border/60">
              {flowRuns.map(item => {
                const current = item.id === run.id
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      if (!current) onSelectRun(item)
                    }}
                    className={cn(
                      'grid w-full grid-cols-[7.5rem_minmax(0,1fr)_auto] items-center gap-2 py-2 text-left',
                      current ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
                    )}
                    aria-current={current ? 'true' : undefined}
                  >
                    <span className="truncate text-xs">{item.when}</span>
                    <span className="min-w-0 truncate text-sm">{item.headline}</span>
                    {statusBadge(item.status)}
                  </button>
                )
              })}
            </div>
            {hiddenRuns > 0 && (
              <Button type="button" variant="ghost" size="sm" className="h-7 px-0 text-xs" onClick={onOpen}>
                {hiddenRuns} more on the flow
              </Button>
            )}
          </section>
        )}
      </div>
    </div>
  )
}

function pulseRunMenuEntries(
  run: FeedRun,
  handlers: {
    onPreview: () => void
    onOpen: (tab: FlowDocTab) => void
    onFix?: () => void
    onCancel?: () => void
  },
): EntityContextMenuEntry[] {
  const entries: EntityContextMenuEntry[] = [
    {
      label: 'View details',
      icon: Eye,
      onSelect: handlers.onPreview,
    },
    {
      label: 'Open flow',
      icon: OpenDestinationIcons.open,
      onSelect: () => handlers.onOpen('overview'),
    },
    {
      label: 'Open runs',
      icon: List,
      onSelect: () => handlers.onOpen('runs'),
    },
  ]
  if (run.status === 'failed' && handlers.onFix) {
    entries.push({ separator: true })
    entries.push({
      label: run.fixHint ?? 'Replay with captured payload',
      icon: Wand2,
      onSelect: handlers.onFix,
    })
  }
  if (run.status === 'shadow') {
    entries.push({ separator: true })
    entries.push({
      label: 'Preview / Promote to live',
      icon: Eye,
      onSelect: () => handlers.onOpen('overview'),
    })
  }
  if (run.status === 'awaiting_approval') {
    entries.push({ separator: true })
    entries.push({
      label: 'Review draft in inbox',
      icon: ThumbsUp,
      onSelect: handlers.onPreview,
    })
  }
  if (run.status === 'running' && handlers.onCancel) {
    entries.push({ separator: true })
    entries.push({
      label: 'Cancel run',
      icon: Ban,
      destructive: true,
      onSelect: handlers.onCancel,
    })
  }
  return entries
}

type PulseStat = 'success24' | 'failed24' | 'exceptions7' | 'cost7'

function isAttentionRun(run: FeedRun) {
  return (
    run.status === 'failed' ||
    run.status === 'policy_blocked' ||
    run.status === 'warning' ||
    run.status === 'awaiting_approval' ||
    run.status === 'shadow' ||
    run.status === 'running'
  )
}

function isExceptionRun(run: FeedRun) {
  return run.status === 'failed' || run.status === 'policy_blocked' || run.status === 'warning'
}

type PulseRowPhase = 'enter' | 'in' | 'out'

function prefersReducedRowMotion() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function sortPulseRows<T extends { run: FeedRun }>(rows: T[], sort: 'recent' | 'oldest') {
  return [...rows].sort((a, b) => {
    const diff = runMeta(a.run).ageHours - runMeta(b.run).ageHours
    return sort === 'recent' ? diff : -diff
  })
}

/** Filter changes fade and collapse rows. Timing matches shell content motion. */
function usePulseFilterMotion(runs: FeedRun[], sort: 'recent' | 'oldest') {
  const key = runs.map(run => run.id).join('\n')
  const runsRef = useRef(runs)
  runsRef.current = runs
  const prevRuns = useRef(runs)
  const seenKey = useRef<string | null>(null)
  const [display, setDisplay] = useState<{ run: FeedRun; phase: PulseRowPhase }[]>(() =>
    runs.map(run => ({ run, phase: 'in' })),
  )

  useEffect(() => {
    const next = runsRef.current
    if (seenKey.current === null) {
      seenKey.current = key
      prevRuns.current = next
      setDisplay(next.map(run => ({ run, phase: 'in' })))
      return
    }
    if (seenKey.current === key || prefersReducedRowMotion()) {
      seenKey.current = key
      prevRuns.current = next
      setDisplay(next.map(run => ({ run, phase: 'in' })))
      return
    }
    const prev = prevRuns.current
    const nextIds = new Set(next.map(run => run.id))
    const prevIds = new Set(prev.map(run => run.id))
    const leaving = prev.filter(run => !nextIds.has(run.id))
    const merged = sortPulseRows(
      [
        ...next.map(run => ({
          run,
          phase: (prevIds.has(run.id) ? 'in' : 'enter') as PulseRowPhase,
        })),
        ...leaving.map(run => ({ run, phase: 'out' as const })),
      ],
      sort,
    )
    seenKey.current = key
    prevRuns.current = next
    setDisplay(merged)
    let cancelled = false
    const frame = window.requestAnimationFrame(() => {
      if (cancelled) return
      window.requestAnimationFrame(() => {
        if (cancelled) return
        setDisplay(current => current.map(row => (
          row.phase === 'enter' ? { ...row, phase: 'in' } : row
        )))
      })
    })
    const timer = window.setTimeout(() => {
      if (cancelled) return
      setDisplay(runsRef.current.map(run => ({ run, phase: 'in' })))
    }, SHELL_PANEL_MOTION_MS)
    return () => {
      cancelled = true
      window.cancelAnimationFrame(frame)
      window.clearTimeout(timer)
    }
  }, [key, sort])

  return display
}

function PulseSurface({
  flows,
  onFix,
  onOpenFlow,
  onOpenTranscript,
  ownerScope,
  setOwnerScope,
  canGoBack,
  canGoForward,
  onBack,
  onForward,
}: {
  flows: StudioFlow[]
  onFix: (flowId: string, stepRegistryId?: string) => void
  onOpenFlow: (flowId: string, tab?: FlowDocTab, event?: MouseEvent) => void
  onOpenTranscript: (run: FeedRun) => void
  ownerScope: OwnerScope
  setOwnerScope: (scope: OwnerScope) => void
  canGoBack?: boolean
  canGoForward?: boolean
  onBack?: () => void
  onForward?: () => void
}) {
  const { mode: pageWidthMode } = useStudioPageWidth()
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const frameRef = useRef<HTMLDivElement>(null)
  const [frameWidth, setFrameWidth] = useState(1200)
  const [runMenu, setRunMenu] = useState<{ run: FeedRun; x: number; y: number } | null>(null)
  const [query, setQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)
  const [flowId, setFlowId] = useState<string | null>(null)
  const [status, setStatus] = useState<RunStatus | 'attention' | null>(null)
  const [trigger, setTrigger] = useState<PulseTriggerKind | null>(null)
  const [extension, setExtension] = useState<string | null>(null)
  const [stat, setStat] = useState<PulseStat | null>(null)
  const [runSort, setRunSort] = useState<'recent' | 'oldest'>('recent')
  const [runView, setRunView] = useState<'card' | 'grid' | 'list'>('list')
  const [upNextOpen, setUpNextOpen] = useState(false)
  const [cancelled, setCancelled] = useState<Set<string>>(new Set())
  const feedScrollRef = useRef<HTMLDivElement>(null)
  const activityBarRef = useRef<HTMLDivElement>(null)
  const activityStuck = useStickyStuck(activityBarRef, feedScrollRef)

  const scoped = FEED.filter(run => ownerScope === 'team' || runMeta(run).mine)
  const in24 = scoped.filter(run => runMeta(run).ageHours <= 24)
  const in7 = scoped.filter(run => runMeta(run).ageHours <= 24 * 7)
  const stats: { id: PulseStat; label: string; value: string }[] = [
    {
      id: 'success24',
      label: 'Successful · 24h',
      value: String(in24.filter(run => run.status === 'ok').length),
    },
    {
      id: 'failed24',
      label: 'Failed · 24h',
      value: String(in24.filter(run => run.status === 'failed').length),
    },
    {
      id: 'exceptions7',
      label: 'Exceptions · 7d',
      value: String(in7.filter(isExceptionRun).length),
    },
    {
      id: 'cost7',
      label: 'Cost · 7d',
      value: `$${in7.reduce((sum, run) => sum + (run.costUsd ?? 0), 0).toFixed(2)}`,
    },
  ]

  const runs = scoped.filter(run => {
    const meta = runMeta(run)
    const hay = `${run.flowName} ${run.headline} ${meta.extension ?? ''}`.toLowerCase()
    if (query.trim() && !hay.includes(query.trim().toLowerCase())) return false
    if (flowId && run.flowId !== flowId) return false
    if (trigger && meta.triggerKind !== trigger) return false
    if (extension && meta.extension !== extension) return false
    if (stat === 'success24') return run.status === 'ok' && meta.ageHours <= 24
    if (stat === 'failed24') return run.status === 'failed' && meta.ageHours <= 24
    if (stat === 'exceptions7') return isExceptionRun(run) && meta.ageHours <= 24 * 7
    if (stat === 'cost7') return meta.ageHours <= 24 * 7
    if (status === 'attention') return isAttentionRun(run)
    if (status) return run.status === status
    return true
  })

  const flowOptions = Array.from(new Map(FEED.map(run => [run.flowId, run.flowName])).entries())
  const extensionOptions = Array.from(
    new Set(FEED.map(run => runMeta(run).extension).filter((name): name is string => Boolean(name))),
  )
  const filtersOn = Boolean(flowId || status || trigger || extension || stat)
  const filterCount = [flowId, status, trigger, extension].filter(Boolean).length
  const visibleRuns = [...runs].sort((a, b) => {
    const diff = runMeta(a).ageHours - runMeta(b).ageHours
    return runSort === 'recent' ? diff : -diff
  })
  const motionRows = usePulseFilterMotion(visibleRuns, runSort)
  const selectedRun = runs.find(run => run.id === selectedRunId) ?? FEED.find(run => run.id === selectedRunId) ?? null
  const roomForPeek = frameWidth >= PULSE_PEEK_FLOOR_PX + PULSE_FEED_FLOOR_PX
  const showPeek = previewOpen && roomForPeek

  useEffect(() => {
    const node = frameRef.current
    if (!node) return
    const apply = (width: number) => setFrameWidth(width)
    apply(node.getBoundingClientRect().width)
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width
      if (width != null) apply(width)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (selectedRunId && !runs.some(run => run.id === selectedRunId)) {
      setSelectedRunId(null)
    }
  }, [selectedRunId, ownerScope, query, flowId, status, trigger, extension, stat, runs])

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus()
  }, [searchOpen])

  const selectRun = (run: FeedRun) => {
    setSelectedRunId(run.id)
  }

  const openRunMenu = (run: FeedRun, x: number, y: number) => {
    setSelectedRunId(run.id)
    setRunMenu({ run, x, y })
  }

  const clearMenuFilters = () => {
    setFlowId(null)
    setStatus(null)
    setTrigger(null)
    setExtension(null)
    setStat(null)
  }

  const upNext = [
    { id: 'morning-briefing', kind: 'schedule' as const, label: 'Morning briefing', when: 'Tomorrow 08:00', flowId: 'morning-briefing' },
    { id: 'client-status-approval', kind: 'approval' as const, label: 'Friday client status draft', when: 'Expires in 31h', flowId: 'client-status-approve' },
    { id: 'client-status-shadow', kind: 'shadow' as const, label: 'Friday client status', when: 'Awaiting promote', flowId: 'client-status-approve' },
    { id: 'error-router', kind: 'schedule' as const, label: 'Error router', when: 'Tomorrow 09:15', flowId: 'error-router' },
    { id: 'spec-sync', kind: 'schedule' as const, label: 'Basecamp spec sync', when: 'Friday 16:00', flowId: 'basecamp-spec-sync' },
  ]
  const visibleUpNext = upNextOpen ? upNext : upNext.slice(0, 3)

  const statusOptions: RunStatus[] = [
    'ok',
    'failed',
    'warning',
    'policy_blocked',
    'awaiting_approval',
    'shadow',
    'running',
  ]
  const triggerOptions: PulseTriggerKind[] = ['schedule', 'webhook', 'manual', 'event']

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid={testIds.flowsStudioPrototype.pulseFeed}>
      <ProtoTitleRow
        crumbs={['Flows', 'Pulse']}
        title="Pulse"
        subtitle="Telemetry + exceptions · not a second inbox"
        canGoBack={canGoBack}
        canGoForward={canGoForward}
        onBack={onBack}
        onForward={onForward}
        testId={testIds.flowsStudioPrototype.titleRow}
        backTestId={testIds.flowsStudioPrototype.backButton}
        forwardTestId={testIds.flowsStudioPrototype.forwardButton}
        actions={
          <>
            <OwnerScopeSegment value={ownerScope} onChange={setOwnerScope} />
            <PreviewPanelToggle
              open={showPeek}
              onToggle={() => {
                if (!roomForPeek) return
                setPreviewOpen(value => !value)
              }}
            />
          </>
        }
      />

      <div
        ref={frameRef}
        className="flex min-h-0 flex-1"
        data-testid={testIds.flowsStudioPrototype.pulseMain}
      >
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        <div ref={feedScrollRef} className="min-h-0 w-full flex-1 overflow-y-auto">
          <div className={cn('mx-auto w-full space-y-6 px-10 pb-6 pt-8', studioPageWidthClass(pageWidthMode, 'max-w-[760px]'))}>
          <div
            className="grid grid-cols-4 gap-3"
            data-testid={testIds.flowsStudioPrototype.pulseStatBar}
          >
            {stats.map(item => {
              const active = stat === item.id
              return (
                <button
                  key={item.id}
                  type="button"
                  data-testid={testIds.flowsStudioPrototype.pulseStat(item.id)}
                  data-state={active ? 'active' : 'inactive'}
                  aria-pressed={active}
                  onClick={() => {
                    setStatus(null)
                    setFlowId(null)
                    setTrigger(null)
                    setExtension(null)
                    setStat(active ? null : item.id)
                  }}
                  className={cn(
                    'rounded-lg border bg-card px-3 py-2.5 text-left transition-colors',
                    active
                      ? 'border-primary bg-primary/15 shadow-sm'
                      : 'border-border hover:border-primary/40 hover:bg-muted/30',
                  )}
                >
                  <div className={cn(
                    'mb-1 text-xs font-medium uppercase tracking-wide',
                    active ? 'text-primary' : 'text-muted-foreground',
                  )}>
                    {item.label}
                  </div>
                  <p className={cn(
                    'text-2xl font-semibold leading-none',
                    active ? 'text-primary' : 'text-foreground',
                  )}>
                    {item.value}
                  </p>
                </button>
              )
            })}
          </div>

          <div data-testid={testIds.flowsStudioPrototype.pulseUpNext}>
            <h2 className="mb-2 text-base font-semibold">Up next</h2>
            <div className="flex flex-wrap gap-2">
              {visibleUpNext.map(item => (
                <button
                  key={item.id}
                  type="button"
                  data-testid={testIds.flowsStudioPrototype.pulseUpNextItem(item.id)}
                  onClick={event => onOpenFlow(item.flowId, 'overview', event)}
                  className="flex items-center gap-2 rounded-lg border border-border bg-muted/10 px-3 py-2 text-left transition-colors duration-200 hover:border-primary/70 hover:bg-primary/10"
                >
                  {item.kind === 'schedule' && <Clock className="h-4 w-4 text-muted-foreground" />}
                  {item.kind === 'approval' && <ThumbsUp className="h-4 w-4 text-primary" />}
                  {item.kind === 'shadow' && <Eye className="h-4 w-4 text-primary" />}
                  <div>
                    <p className="text-[15px] font-medium">{item.label}</p>
                    <p className="text-sm text-muted-foreground">{item.when}</p>
                  </div>
                </button>
              ))}
            </div>
            {upNext.length > 3 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-2 h-8 px-2 text-xs text-muted-foreground"
                data-testid={testIds.flowsStudioPrototype.pulseUpNextMore}
                aria-expanded={upNextOpen}
                onClick={() => setUpNextOpen(open => !open)}
              >
                {upNextOpen ? 'Show less' : `Show ${upNext.length - 3} more`}
              </Button>
            )}
          </div>
          </div>

          <div ref={activityBarRef} className={cn(STICKY_INDEX_BAND_CLASS, 'h-11')}>
          <ShellIndexRow
            title="Activity"
            testId={testIds.flowsStudioPrototype.pulseActivity}
            className={cn(
              'mx-auto h-full w-full bg-background px-10 @container/activity',
              studioPageWidthClass(pageWidthMode, 'max-w-[760px]'),
              activityStuck ? 'border-b border-border' : 'border-b-0',
            )}
          >
            <div className="ml-auto flex items-center gap-2">
            {searchOpen ? (
              <div className="relative w-52">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  ref={searchRef}
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  onBlur={() => {
                    if (!query.trim()) setSearchOpen(false)
                  }}
                  onKeyDown={event => {
                    if (event.key === 'Escape') {
                      setQuery('')
                      setSearchOpen(false)
                    }
                  }}
                  placeholder="Search runs"
                  className="h-8 pl-7 text-xs"
                  data-testid={testIds.flowsStudioPrototype.pulseSearch}
                />
              </div>
            ) : (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                aria-label="Search runs"
                data-testid={testIds.flowsStudioPrototype.pulseSearchToggle}
                onClick={() => setSearchOpen(true)}
              >
                <Search className="h-4 w-4" />
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className={cn(
                    'h-8 w-8 gap-1 px-0',
                    pageWidthMode === 'full' && '@[40rem]/activity:w-auto @[40rem]/activity:px-3',
                    filterCount > 0 && 'w-auto px-2',
                    filtersOn && 'border-primary/50 text-primary',
                  )}
                  aria-label="Filter runs"
                  title="Filters"
                  data-testid={testIds.flowsStudioPrototype.pulseFilterMenu}
                  data-state={filtersOn ? 'active' : 'inactive'}
                >
                  <SlidersHorizontal className="h-3.5 w-3.5" />
                  <span className={cn(pageWidthMode === 'full' ? 'hidden @[40rem]/activity:inline' : 'hidden')}>Filters</span>
                  {filterCount > 0 && (
                    <span className="rounded-full bg-primary px-1 text-[10px] leading-4 text-primary-foreground">
                      {filterCount}
                    </span>
                  )}
                  <ChevronDown className={cn('h-3 w-3', pageWidthMode === 'full' ? 'hidden @[40rem]/activity:block' : 'hidden')} aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
              >
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>Flow</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent
                    collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                    className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}
                  >
                    {flowOptions.map(([id, name]) => (
                      <DropdownMenuItem key={id} onSelect={() => { setFlowId(id); setStat(null) }}>
                        {name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>Status</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent
                    collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                    className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}
                  >
                    <DropdownMenuItem
                      onSelect={() => { setStatus(null); setStat(null) }}
                    >
                      All statuses
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid={testIds.flowsStudioPrototype.pulseNeedsAttention}
                      data-state={status === 'attention' ? 'active' : 'inactive'}
                      onSelect={() => { setStatus('attention'); setStat(null) }}
                    >
                      Needs attention
                    </DropdownMenuItem>
                    {statusOptions.map(option => (
                      <DropdownMenuItem key={option} onSelect={() => { setStatus(option); setStat(null) }}>
                        {option.replace('_', ' ')}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>Trigger</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent
                    collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                    className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}
                  >
                    {triggerOptions.map(option => (
                      <DropdownMenuItem key={option} onSelect={() => { setTrigger(option); setStat(null) }}>
                        {option}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>Extension</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent
                    collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                    className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}
                  >
                    {extensionOptions.map(option => (
                      <DropdownMenuItem key={option} onSelect={() => { setExtension(option); setStat(null) }}>
                        {option}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                {filtersOn && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={clearMenuFilters}>Clear filters</DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className={cn(
                    'h-8 w-8 gap-1 px-0',
                    pageWidthMode === 'full' && '@[40rem]/activity:w-auto @[40rem]/activity:px-3',
                  )}
                  aria-label={runSort === 'recent' ? 'Sort: Most recent' : 'Sort: Oldest first'}
                  title={runSort === 'recent' ? 'Most recent' : 'Oldest first'}
                  data-testid={testIds.flowsStudioPrototype.pulseSort}
                >
                  <ArrowUpDown className="h-3.5 w-3.5" />
                  <span className={cn(pageWidthMode === 'full' ? 'hidden @[40rem]/activity:inline' : 'hidden')}>
                    {runSort === 'recent' ? 'Most recent' : 'Oldest first'}
                  </span>
                  <ChevronDown className={cn('h-3 w-3', pageWidthMode === 'full' ? 'hidden @[40rem]/activity:block' : 'hidden')} aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}
              >
                <DropdownMenuItem onSelect={() => setRunSort('recent')}>Most recent</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setRunSort('oldest')}>Oldest first</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <ListViewModeToggle
              value={runView}
              onChange={setRunView}
              testId={testIds.flowsStudioPrototype.pulseViewMode}
            />
            </div>
          </ShellIndexRow>
          </div>

          <div className={cn('mx-auto w-full px-10 pb-8', studioPageWidthClass(pageWidthMode, 'max-w-[760px]'))}>
          <div>
            {runView === 'list' && motionRows.length > 0 && (
              <div
                className={cn(STICKY_COLUMN_HEAD_CLASS, 'grid items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground')}
                style={{ gridTemplateColumns: PULSE_LIST_COLUMNS }}
                data-testid={testIds.flowsStudioPrototype.pulseListHead}
              >
                <span className="px-2">Flow</span>
                <span>When</span>
                <span>Status</span>
                <span className="text-right">Duration</span>
                <span />
              </div>
            )}
            <div className={cn('relative z-0', runView === 'grid' ? 'grid grid-cols-2 gap-3 pt-3' : runView === 'card' ? 'space-y-2 pt-4' : '')}>
              {motionRows.map(row => (
                <div
                  key={row.run.id}
                  data-phase={row.phase}
                  aria-hidden={row.phase === 'out' ? true : undefined}
                  className={cn(
                    'grid overflow-hidden transition-[grid-template-rows,opacity] ease-in-out motion-reduce:transition-none',
                    row.phase === 'in' ? 'grid-rows-[1fr] opacity-100' : 'pointer-events-none grid-rows-[0fr] opacity-0',
                  )}
                  style={{ transitionDuration: `${SHELL_PANEL_MOTION_MS}ms` }}
                >
                  <div className="min-h-0 overflow-hidden">
                    <PulseActivityItem
                      run={row.run}
                      view={runView}
                      selected={selectedRunId === row.run.id}
                      cancelled={cancelled.has(row.run.id)}
                      onOpenFlow={onOpenFlow}
                      onSelect={selectRun}
                      onMenu={openRunMenu}
                    />
                  </div>
                </div>
              ))}
              {motionRows.length === 0 && (
                <p className="px-2 py-3 text-sm text-muted-foreground motion-reduce:animate-none animate-in fade-in-0 duration-200">
                  No runs in this view.
                </p>
              )}
            </div>
          </div>

          <div className="mt-6">
          <ConceptNote>
            Pulse is telemetry + exceptions - not a second inbox. Mine is flows you own. Team is every
            flow in the active workspaces. Click selects a run. The preview pane is the detail.
          </ConceptNote>
          </div>
          </div>
        </div>
        <ListSurfaceStatusBar
          includePreviewTip
          leading={<StudioPageWidthToggle />}
          tips="Click a run to select it. The preview pane shows the trace. Double-click opens Runs. Right-click or the row menu opens the flow, runs, or cancels a running row."
        />
        </div>

        {showPeek && (
          <div className="flex h-full min-h-0 w-[min(360px,42%)] shrink-0 flex-col">
            {selectedRun ? (
              <PulseRunPreview
                run={selectedRun}
                flow={flows.find(flow => flow.id === selectedRun.flowId)}
                onOpen={() => onOpenFlow(selectedRun.flowId, 'overview')}
                onClose={() => setPreviewOpen(false)}
                onSelectRun={selectRun}
                onFix={onFix}
                onOpenTranscript={onOpenTranscript}
              />
            ) : (
              <div className="flex h-full min-h-0 flex-col border-l border-border bg-background">
                <ShellIndexRow title="Preview" className="bg-background" />
                <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
                  Select a run to see its steps
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {runMenu && (
        <EntityContextMenu
          x={runMenu.x}
          y={runMenu.y}
          onClose={() => setRunMenu(null)}
          data-testid={testIds.flowsStudioPrototype.pulseRunContextMenu}
          entries={pulseRunMenuEntries(runMenu.run, {
            onPreview: () => {
              setSelectedRunId(runMenu.run.id)
              setPreviewOpen(true)
              setRunMenu(null)
            },
            onOpen: tab => {
              onOpenFlow(runMenu.run.flowId, tab)
              setRunMenu(null)
            },
            onFix:
              runMenu.run.status === 'failed'
                ? () => {
                    onFix(runMenu.run.flowId, runMenu.run.failedStep)
                    setRunMenu(null)
                  }
                : undefined,
            onCancel:
              runMenu.run.status === 'running' && !cancelled.has(runMenu.run.id)
                ? () => {
                    setCancelled(prev => new Set(prev).add(runMenu.run.id))
                    setRunMenu(null)
                  }
                : undefined,
          })}
        />
      )}
    </div>
  )
}

/** Flow | When | Status | Duration | menu. When is wide enough for "Yesterday 08:00" on one line. */
const PULSE_LIST_COLUMNS = 'minmax(0,1fr) 9.25rem 9.5rem 4.75rem 2rem'
/** Peek floor from the shell. The feed needs its own floor before both can sit side by side. */
const PULSE_PEEK_FLOOR_PX = 280
const PULSE_FEED_FLOOR_PX = 480

function PulseRunStatus({ run, cancelled }: { run: FeedRun; cancelled: boolean }) {
  if (cancelled) {
    return (
      <StudioStatusPill className="border border-border bg-muted text-muted-foreground">
        Cancelled
      </StudioStatusPill>
    )
  }
  return statusBadge(run.status)
}

function PulseRunDuration({ run, cancelled }: { run: FeedRun; cancelled: boolean }) {
  const meta = runMeta(run)
  if (meta.runningForSec != null && !cancelled) return <LiveDuration baseSec={meta.runningForSec} />
  if (meta.durationSec != null) return <>{formatDuration(meta.durationSec)}</>
  return <>-</>
}

function PulseActivityItem({
  run,
  view,
  selected,
  cancelled,
  onOpenFlow,
  onSelect,
  onMenu,
}: {
  run: FeedRun
  view: 'card' | 'grid' | 'list'
  selected: boolean
  cancelled: boolean
  onOpenFlow: (flowId: string, tab?: FlowDocTab, event?: MouseEvent) => void
  onSelect: (run: FeedRun) => void
  onMenu: (run: FeedRun, x: number, y: number) => void
}) {
  const meta = runMeta(run)
  const onClick = (event: MouseEvent) => {
    if (isModClick(event)) {
      onOpenFlow(run.flowId, 'runs', event)
      return
    }
    onSelect(run)
  }
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      onSelect(run)
    }
    if (event.key === ' ') {
      event.preventDefault()
      onSelect(run)
    }
  }
  const shared = {
    role: 'button' as const,
    tabIndex: 0,
    'data-testid': testIds.flowsStudioPrototype.pulseRunRow(run.id),
    'data-state': selected ? 'active' : 'inactive',
    onContextMenu: (event: MouseEvent) => {
      event.preventDefault()
      onMenu(run, event.clientX, event.clientY)
    },
    onClick,
    onDoubleClick: (event: MouseEvent) => {
      event.preventDefault()
      onOpenFlow(run.flowId, 'runs', event)
    },
    onKeyDown,
  }
  const menu = (
    <MenuEllipsisButton
      testId={testIds.flowsStudioPrototype.pulseRunOverflow}
      onOpen={(x, y) => onMenu(run, x, y)}
    />
  )
  if (view === 'list') {
    return (
      <div
        {...shared}
        className={cn(
          'grid items-center gap-2 border-b border-border/50 py-1.5 outline-none',
          selected ? 'bg-primary/5' : 'hover:bg-muted/40',
        )}
        style={{ gridTemplateColumns: PULSE_LIST_COLUMNS }}
      >
        <span className="min-w-0 truncate px-2 text-sm">{run.flowName}</span>
        <span className="whitespace-nowrap text-xs text-muted-foreground">{run.when}</span>
        <span className="min-w-0">
          <PulseRunStatus run={run} cancelled={cancelled} />
        </span>
        <span className="text-right text-xs text-muted-foreground">
          <PulseRunDuration run={run} cancelled={cancelled} />
        </span>
        {menu}
      </div>
    )
  }

  if (view === 'grid') {
    return (
      <div
        {...shared}
        className={cn(
          'flex min-h-[7.5rem] cursor-pointer flex-col rounded-lg border bg-card p-3 outline-none',
          selected ? 'border-primary/40 bg-primary/5' : 'border-border hover:bg-muted/70',
        )}
      >
        <div className="flex items-start gap-2">
          <span className="min-w-0 flex-1 text-sm font-medium leading-5 line-clamp-2">{run.flowName}</span>
          {menu}
        </div>
        <div className="mt-auto flex items-center justify-between gap-2 pt-3">
          <span className="whitespace-nowrap text-xs text-muted-foreground">{run.when}</span>
          <span className="text-xs text-muted-foreground">
            <PulseRunDuration run={run} cancelled={cancelled} />
          </span>
        </div>
        <div className="pt-2">
          <PulseRunStatus run={run} cancelled={cancelled} />
        </div>
      </div>
    )
  }

  return (
    <div
      {...shared}
      className={cn(
        'cursor-pointer rounded-lg border bg-card p-3 outline-none',
        selected ? 'border-primary/40 bg-primary/5' : 'border-border hover:bg-muted/70',
      )}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{run.flowName}</p>
          <p className="mt-1 text-sm">{run.headline}</p>
          <p className="mt-1 text-xs text-muted-foreground">{run.detail}</p>
        </div>
        {menu}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="whitespace-nowrap">{run.when}</span>
        <span>
          <PulseRunDuration run={run} cancelled={cancelled} />
        </span>
        <span className="capitalize">{meta.triggerKind}</span>
        {meta.extension ? (
          <span className="inline-flex items-center gap-1">
            <Puzzle className="h-3.5 w-3.5" aria-hidden="true" />
            {meta.extension}
          </span>
        ) : null}
        {run.costUsd != null ? <span>${run.costUsd.toFixed(2)}</span> : null}
      </div>
      <div className="mt-2">
        <PulseRunStatus run={run} cancelled={cancelled} />
      </div>
    </div>
  )
}

const FLOW_DOC_TAB_LABEL: Record<FlowDocTab, string> = {
  overview: 'Definition',
  runs: 'Runs',
}

/** ADR-0020 Open submenu - same destinations from right-click and `...`. */
function flowTreeMenuEntries(
  target: FlowTreeTarget,
  actions: {
    onOpenFolder: (path: string) => void
    onPreviewFlow: (id: string) => void
    onOpenFlow: (id: string, tab: FlowDocTab) => void
  },
  previewDisabled = false,
): EntityContextMenuEntry[] {
  if (target.kind === 'folder') {
    return [
      {
        label: 'Preview',
        icon: OpenDestinationIcons.preview,
        disabled: true,
        onSelect: () => {},
      },
      {
        label: 'Open',
        icon: OpenDestinationIcons.open,
        onSelect: () => actions.onOpenFolder(target.path),
      },
    ]
  }
  return [
    {
      label: 'Preview',
      icon: OpenDestinationIcons.preview,
      disabled: previewDisabled,
      onSelect: () => actions.onPreviewFlow(target.id),
    },
    flowOpenSubmenu(tab => actions.onOpenFlow(target.id, tab)),
  ]
}

function flowOpenSubmenu(onOpen: (tab: FlowDocTab) => void): EntityContextMenuEntry {
  const order: FlowDocTab[] = ['overview', 'runs']
  return {
    label: 'Open',
    icon: OpenDestinationIcons.open,
    children: order.map(tab => ({
      label: FLOW_DOC_TAB_LABEL[tab],
      onSelect: () => onOpen(tab),
    })),
  }
}

function MenuEllipsisButton({
  onOpen,
  testId,
  className,
}: {
  onOpen: (x: number, y: number) => void
  testId?: string
  className?: string
}) {
  return (
    <button
      type="button"
      className={cn(
        'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground',
        className,
      )}
      aria-label="More actions"
      data-testid={testId}
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()
        const r = e.currentTarget.getBoundingClientRect()
        onOpen(r.right, r.bottom)
      }}
    >
      <MoreHorizontal className="h-4 w-4" />
    </button>
  )
}

// Pruned tree (Flows sidebar mock)
// ---------------------------------------------------------------------------

type FlowTreeFolder = {
  id: string
  label: string
  depth: number
  childFolders: FlowTreeFolder[]
  flowIds: string[]
}

function buildFlowFolders(flows: StudioFlow[]): FlowTreeFolder[] {
  const root: FlowTreeFolder = { id: '', label: '', depth: -1, childFolders: [], flowIds: [] }
  for (const flow of flows) {
    const parts = flow.path.split('/').filter(Boolean)
    let cursor = root
    let acc = ''
    for (const part of parts) {
      acc = acc ? `${acc}/${part}` : part
      let next = cursor.childFolders.find(child => child.id === acc)
      if (!next) {
        next = { id: acc, label: part, depth: cursor.depth + 1, childFolders: [], flowIds: [] }
        cursor.childFolders.push(next)
      }
      cursor = next
    }
    cursor.flowIds.push(flow.id)
  }
  const sortFolder = (folder: FlowTreeFolder) => {
    folder.childFolders.sort((a, b) => a.label.localeCompare(b.label))
    folder.childFolders.forEach(sortFolder)
  }
  sortFolder(root)
  return root.childFolders
}

function findFlowFolder(folders: FlowTreeFolder[], id: string): FlowTreeFolder | null {
  for (const folder of folders) {
    if (folder.id === id) return folder
    const hit = findFlowFolder(folder.childFolders, id)
    if (hit) return hit
  }
  return null
}

function parentFolderPath(path: string): string | null {
  const index = path.lastIndexOf('/')
  if (index <= 0) return null
  return path.slice(0, index)
}

const FOLDER_TAB_PREFIX = 'folder:'

function studioFolderTabId(path: string) {
  return `${FOLDER_TAB_PREFIX}${path}`
}

function folderPathFromStudioTab(id: string): string | null {
  return id.startsWith(FOLDER_TAB_PREFIX) ? id.slice(FOLDER_TAB_PREFIX.length) : null
}

/** Mode panel: production sidebar header + nouns + Browse tree rows. */
function StudioModePanel({
  surface,
  onSurface,
  flows,
  activeFlowId,
  activeFolderPath,
  onSelectFlow,
  onOpenFolder,
  onTreeContextMenu,
  exceptionCount,
  openFlowIds,
  openFolderPaths,
  openIndexIds,
}: {
  surface: Surface
  onSurface: (s: Surface, event?: MouseEvent) => void
  flows: StudioFlow[]
  activeFlowId: string
  activeFolderPath: string | null
  onSelectFlow: (id: string, tab?: FlowDocTab, event?: MouseEvent) => void
  onOpenFolder: (path: string, event?: MouseEvent) => void
  onTreeContextMenu: (target: FlowTreeTarget, event: MouseEvent) => void
  exceptionCount: number
  openFlowIds: Set<string>
  openFolderPaths: Set<string>
  openIndexIds: Set<string>
}) {
  const folders = useMemo(() => buildFlowFolders(flows), [flows])
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [dense, setDense] = useState(false)
  const openFlow = surface === 'flow' ? flows.find(item => item.id === activeFlowId) : undefined

  const toggleFolder = (id: string) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const renderFolder = (folder: FlowTreeFolder) => {
    const expanded = !collapsed.has(folder.id)
    const folderFocused = surface === 'folder' && activeFolderPath === folder.id
    const contains =
      !folderFocused &&
      ((surface === 'folder' &&
        !!activeFolderPath &&
        activeFolderPath.startsWith(`${folder.id}/`)) ||
        (!!openFlow &&
          (openFlow.path === folder.id || openFlow.path.startsWith(`${folder.id}/`))))
    return (
      <div key={folder.id}>
        <SidebarTreeRow
          padLeft={wsNavRowPadLeftPx(folder.depth)}
          icon={flowFolderIcon(expanded)}
          label={folder.label}
          active={folderFocused}
          contains={contains}
          open={openFolderPaths.has(folder.id) && !folderFocused}
          className="cursor-pointer"
          chevron={{ expanded, onClick: () => toggleFolder(folder.id) }}
          onClick={event => onOpenFolder(folder.id, event)}
          onContextMenu={event => onTreeContextMenu({ kind: 'folder', path: folder.id }, event)}
        />
        {expanded ? (
          <>
            {folder.flowIds.map(id => {
              const flow = flows.find(item => item.id === id)
              if (!flow) return null
              const selected = activeFlowId === id && surface === 'flow'
              return (
                <SidebarTreeRow
                  key={id}
                  padLeft={wsNavRowPadLeftPx(folder.depth + 1)}
                  active={selected}
                  icon={flowRowIcon()}
                  label={flow.name}
                  open={openFlowIds.has(id) && !selected}
                  className="cursor-pointer"
                  onClick={event => onSelectFlow(id, 'overview', event)}
                  onContextMenu={event => onTreeContextMenu({ kind: 'flow', id }, event)}
                />
              )
            })}
            {folder.childFolders.map(renderFolder)}
          </>
        ) : null}
      </div>
    )
  }

  return (
    <aside
      className="ws-nav-section flex w-64 shrink-0 flex-col overflow-hidden border-r border-sidebar-border bg-sidebar text-sidebar-foreground"
      style={wsNavSectionStyle()}
      data-testid={testIds.flowsStudioPrototype.prunedTree}
      data-sidebar-density={dense ? 'dense' : 'detailed'}
    >
      <SidebarPanelHeader
        title="Flows"
        optionsMenu={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Flows menu"
                data-testid={testIds.flowsStudioPrototype.modePanelOverflow}
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
              className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}
            >
              <DropdownMenuItem onSelect={() => setDense(false)}>
                <AlignJustify className="h-4 w-4" />
                Detailed layout
                {!dense ? <Check className="ml-auto h-3.5 w-3.5" /> : null}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setDense(true)}>
                <List className="h-4 w-4" />
                Dense layout
                {dense ? <Check className="ml-auto h-3.5 w-3.5" /> : null}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => onSurface('composer')}>
                <Plus className="h-4 w-4" />
                New flow
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />
      <nav className="shrink-0 space-y-0.5 border-b border-sidebar-border px-1 py-1" data-testid={testIds.flowsStudioPrototype.surfaceNav}>
        {SURFACES.map(s => {
          const Icon = s.icon
          return (
            <ModePanelNavItem
              key={s.id}
              active={surface === s.id}
              open={openIndexIds.has(s.id) && surface !== s.id}
              icon={<Icon className="h-4 w-4 shrink-0" />}
              label={s.label}
              trailing={
                s.id === 'pulse' && exceptionCount > 0 ? (
                  <span className="text-sm tabular-nums text-muted-foreground">{exceptionCount}</span>
                ) : undefined
              }
              onClick={e => onSurface(s.id, e)}
            />
          )
        })}
      </nav>
      <div className="min-h-0 flex-1 overflow-y-auto py-1">
        {folders.map(renderFolder)}
        {flows.length === 0 ? (
          <div className="px-2 pt-3">
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-full text-sm"
              onClick={e => onSurface('composer', e)}
            >
              <Plus className="mr-1 h-4 w-4" /> New flow
            </Button>
          </div>
        ) : null}
      </div>
    </aside>
  )
}

function FlowFolderSurface({
  path,
  flows,
  peek,
  onPeek,
  onOpenFolder,
  onOpenFlow,
  onContextMenu,
  ownerScope,
  setOwnerScope,
  canGoBack,
  canGoForward,
  onBack,
  onForward,
}: {
  path: string
  flows: StudioFlow[]
  peek: FolderPeek
  onPeek: (next: FolderPeek) => void
  onOpenFolder: (path: string, event?: MouseEvent) => void
  onOpenFlow: (id: string, tab?: FlowDocTab, event?: MouseEvent) => void
  onContextMenu: (target: FlowTreeTarget, event: MouseEvent) => void
  ownerScope: OwnerScope
  setOwnerScope: (scope: OwnerScope) => void
  canGoBack?: boolean
  canGoForward?: boolean
  onBack?: () => void
  onForward?: () => void
}) {
  const folders = useMemo(() => buildFlowFolders(flows), [flows])
  const folder = findFlowFolder(folders, path)
  const parent = parentFolderPath(path)
  const parts = path.split('/')
  const selectedFlow =
    peek.selectedKind === 'flow' ? flows.find(item => item.id === peek.selectedId) : undefined
  const selectedFolder =
    peek.selectedKind === 'folder' ? findFlowFolder(folders, peek.selectedId ?? '') : undefined

  const selectRow = (id: string, kind: 'folder' | 'flow') => {
    onPeek({ selectedId: id, selectedKind: kind, open: true })
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid={testIds.flowsStudioPrototype.flowFolder}>
      <ProtoTitleRow
        crumbs={[
          { id: '', label: 'Flows' },
          ...parts.map((label, index) => ({
            id: parts.slice(0, index + 1).join('/'),
            label,
          })),
        ]}
        onCrumb={id => {
          if (id) onOpenFolder(id)
        }}
        title={folder?.label ?? parts[parts.length - 1] ?? 'Folder'}
        subtitle={path}
        canGoBack={canGoBack}
        canGoForward={canGoForward}
        onBack={onBack}
        onForward={onForward}
        testId={testIds.flowsStudioPrototype.titleRow}
        backTestId={testIds.flowsStudioPrototype.backButton}
        forwardTestId={testIds.flowsStudioPrototype.forwardButton}
        actions={
          <>
            <OwnerScopeSegment value={ownerScope} onChange={setOwnerScope} />
            <PreviewPanelToggle
              open={peek.open}
              onToggle={() => onPeek({ ...peek, open: !peek.open })}
            />
          </>
        }
      />
      <div className="flex min-h-0 flex-1">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto">
            {parent ? (
              <UpOneLevelRow onClick={() => onOpenFolder(parent)} />
            ) : null}
            {folder?.childFolders.map(child => (
              <ExtensionListRow
                key={child.id}
                selected={peek.selectedKind === 'folder' && peek.selectedId === child.id}
                testId={testIds.flowsStudioPrototype.flowFolderRow(child.id)}
                onSelect={() => selectRow(child.id, 'folder')}
                onOpen={event => onOpenFolder(child.id, event)}
                onContextMenu={event => onContextMenu({ kind: 'folder', path: child.id }, event)}
              >
                <span className="mt-0.5">{flowFolderIcon(false)}</span>
                <span className="min-w-0">
                  <span className="block truncate text-sm">{child.label}</span>
                  <span className="block text-xs text-muted-foreground">Folder</span>
                </span>
              </ExtensionListRow>
            ))}
            {folder?.flowIds.map(id => {
              const flow = flows.find(item => item.id === id)
              if (!flow) return null
              if (ownerScope === 'mine' && flow.mine === false) return null
              return (
                <ExtensionListRow
                  key={id}
                  selected={peek.selectedKind === 'flow' && peek.selectedId === id}
                  testId={testIds.flowsStudioPrototype.flowFolderRow(id)}
                  onSelect={() => selectRow(id, 'flow')}
                  onOpen={event => onOpenFlow(id, 'overview', event)}
                  onContextMenu={event => onContextMenu({ kind: 'flow', id }, event)}
                >
                  <span className="mt-0.5">{flowRowIcon()}</span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm">{flow.name}</span>
                    <span className="block text-xs text-muted-foreground">{modeLabel(flow.mode)}</span>
                  </span>
                </ExtensionListRow>
              )
            })}
          </div>
          <ListSurfaceStatusBar
            includePreviewTip
            tips="Click a row to select. Double-click opens the folder or the flow. Right-click uses the same menu as the sidebar."
          />
        </div>
        {peek.open ? (
          <div
            className="flex w-[min(320px,40%)] shrink-0 flex-col border-l border-border bg-card"
            data-testid={testIds.flowsStudioPrototype.flowFolderPreview}
          >
            {selectedFlow ? (
              <>
                <PreviewPaneHeader
                  testId={testIds.flowsStudioPrototype.flowFolderPreviewHeader}
                  icon={flowRowIcon()}
                  label="Flow preview"
                  onClose={() => onPeek({ ...peek, open: false })}
                  onOpen={() => onOpenFlow(selectedFlow.id, 'overview')}
                />
                <div className="space-y-2 px-4 py-4 text-sm">
                  <p className="font-medium">{selectedFlow.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {modeLabel(selectedFlow.mode)} - {selectedFlow.path}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Peek only. Double-click the row, or Open, to open the flow.
                  </p>
                </div>
              </>
            ) : selectedFolder ? (
              <>
                <PreviewPaneHeader
                  testId={testIds.flowsStudioPrototype.flowFolderPreviewHeader}
                  icon={flowFolderIcon(false)}
                  label="Folder"
                  onClose={() => onPeek({ ...peek, open: false })}
                  onOpen={() => onOpenFolder(selectedFolder.id)}
                />
                <div className="space-y-2 px-4 py-4 text-sm">
                  <p className="font-medium">{selectedFolder.label}</p>
                  <p className="text-xs text-muted-foreground">
                    Selected in the list. Double-click to open it.
                  </p>
                </div>
              </>
            ) : (
              <IndexPreviewEmpty
                noun="flow"
                label="Flow preview"
                onClose={() => onPeek({ ...peek, open: false })}
              />
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Flow document (Definition workspace / Runs history)
// ---------------------------------------------------------------------------

function activeFeedRun(flowId: string): FeedRun | null {
  return FEED.find(run => run.flowId === flowId && run.status === 'running') ?? null
}

function runningDefinitionStates(
  flow: StudioFlow,
  meta: RunMeta | null,
): Record<string, StepRunState> {
  const found = meta?.agentStepId
    ? flow.steps.findIndex(step => step.id === meta.agentStepId)
    : 0
  const activeIndex = found >= 0 ? found : 0
  return Object.fromEntries(
    flow.steps.map((step, index) => {
      if (index === activeIndex) return [step.id, 'running' as const]
      return [step.id, 'pending' as const]
    }),
  )
}

function stepStatesForRun(run: FeedRun, flow: StudioFlow): Record<string, StepRunState> | undefined {
  if (run.stepStates) return run.stepStates
  if (run.status === 'running') {
    return Object.fromEntries(flow.steps.map(s => [s.id, 'pending' as StepRunState]))
  }
  if (run.status === 'failed' && flow.id === 'zoom-meeting-notes') return ZOOM_FAILED_RUN_STATES
  if (run.status === 'ok' || run.status === 'warning') {
    return Object.fromEntries(flow.steps.map(s => [s.id, 'ok' as StepRunState]))
  }
  if (run.status === 'shadow' || run.status === 'awaiting_approval') {
    return defaultSimulateStates(flow.steps)
  }
  if (run.status === 'policy_blocked') {
    return Object.fromEntries(flow.steps.map(s => [s.id, 'skipped' as StepRunState]))
  }
  return undefined
}

function isWriteStep(s: StudioStep): boolean {
  return (
    Boolean(s.dangerous) ||
    s.registryId === 'inbox.post_thread' ||
    s.registryId === 'basecamp.post_comment' ||
    s.registryId.startsWith('document.')
  )
}

function outcomeForStep(s: StudioStep, mode: RuntimeMode): StepRunState {
  if (mode === 'shadow' && isWriteStep(s)) return 'stubbed'
  return 'ok'
}

function capturedEventSample(flow: StudioFlow): string | null {
  const pl = CAPTURED_PAYLOADS[flow.trigger.registryId]
  if (!pl) return null
  return (
    pl['trigger.meeting_topic'] ||
    pl['trigger.todo_title'] ||
    pl['trigger.body.form_name'] ||
    pl['trigger.flow_name'] ||
    pl['trigger.file_path'] ||
    null
  )
}

function manualRunGate(
  flow: StudioFlow,
): { ok: true; hint: string } | { ok: false; reason: string } {
  if (flow.systemPaused) return { ok: false, reason: 'System paused - this flow cannot run' }
  if (flow.mode === 'draft') return { ok: false, reason: 'Enable Shadow or Live to run' }
  const isSchedule = flow.trigger.registryId.startsWith('schedule.')
  const captured = CAPTURED_PAYLOADS[flow.trigger.registryId]
  if (!isSchedule && !captured) {
    return { ok: false, reason: 'Needs a captured event from this trigger' }
  }
  if (isSchedule) return { ok: true, hint: 'Manual run' }
  const sample = capturedEventSample(flow)
  return {
    ok: true,
    hint: sample ? `Uses last captured event: ${sample}` : 'Uses last captured event',
  }
}

function mergeStepInputs(
  steps: StudioStep[],
  stepId: string,
  inputs: Record<string, string>,
): StudioStep[] {
  return steps.map(s => (s.id === stepId ? { ...s, inputs: { ...s.inputs, ...inputs } } : s))
}

function proposeStepPrompt(steps: StudioStep[], stepId: string): StudioStep[] {
  return steps.map(step => {
    if (step.id !== stepId) return step
    const prompt = step.inputs.prompt ?? ''
    const next = promptRewrites(prompt)[0] ?? prompt
    return { ...step, inputs: { ...step.inputs, prompt: next } }
  })
}

/** Saved steps the proposal drops stay in place, marked remove. New ids are add. */
function proposalPipeline(saved: StudioStep[], proposed: StudioStep[]): {
  steps: StudioStep[]
  role: Record<string, 'add' | 'remove'>
} {
  const savedIds = new Set(saved.map(step => step.id))
  const proposedIds = new Set(proposed.map(step => step.id))
  const role: Record<string, 'add' | 'remove'> = {}
  const steps: StudioStep[] = []
  let savedIndex = 0
  for (const step of proposed) {
    while (savedIndex < saved.length && saved[savedIndex].id !== step.id) {
      const dropped = saved[savedIndex]
      if (!proposedIds.has(dropped.id)) {
        steps.push(dropped)
        role[dropped.id] = 'remove'
      }
      savedIndex += 1
    }
    if (!savedIds.has(step.id)) role[step.id] = 'add'
    steps.push(step)
    if (savedIndex < saved.length && saved[savedIndex].id === step.id) savedIndex += 1
  }
  while (savedIndex < saved.length) {
    const dropped = saved[savedIndex]
    if (!proposedIds.has(dropped.id)) {
      steps.push(dropped)
      role[dropped.id] = 'remove'
    }
    savedIndex += 1
  }
  return { steps, role }
}

function proposedRefineSteps(flow: StudioFlow): StudioStep[] {
  if (flow.steps.some(s => s.id === 'refine-shape')) return flow.steps
  let insertAt = flow.steps.length
  for (let i = flow.steps.length - 1; i >= 0; i--) {
    if (isWriteStep(flow.steps[i])) {
      insertAt = i
      break
    }
  }
  const extra: StudioStep = { ...COMPOSER_REFINE_STEP, id: 'refine-shape' }
  return [...flow.steps.slice(0, insertAt), extra, ...flow.steps.slice(insertAt)]
}

const LIVE_STEP_MS = 700

function SystemPausedNotice({ reason }: { reason: string }) {
  return (
    <div
      role="status"
      className="mx-auto w-full max-w-2xl rounded-lg border border-orange-500/25 bg-orange-500/[0.06] px-3 py-2.5"
    >
      <p className="flex items-center gap-2 text-sm font-medium text-orange-700 dark:text-orange-400">
        <PauseCircle className="h-4 w-4 shrink-0" />
        System paused
      </p>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{reason}</p>
    </div>
  )
}

function promptHunk(current: string, next: string): { same: string; removed: string; added: string } {
  if (!current) return { same: '', removed: '', added: next }
  if (next.startsWith(current)) return { same: current, removed: '', added: next.slice(current.length) }
  if (current.startsWith(next)) return { same: next, removed: current.slice(next.length), added: '' }
  let split = 0
  const limit = Math.min(current.length, next.length)
  while (split < limit && current[split] === next[split]) split += 1
  if (split > 0 && split < current.length) {
    const space = current.lastIndexOf(' ', split)
    if (space > 0) split = space + 1
  }
  return { same: current.slice(0, split), removed: current.slice(split), added: next.slice(split) }
}

const DIFF_ADD_CLASS =
  'diff-review-mark cursor-text border-green-700/40 bg-green-600/[0.16] hover:border-green-700/55 hover:bg-green-600/[0.22] dark:border-green-400/75 dark:bg-green-500/[0.24] dark:hover:border-green-300 dark:hover:bg-green-500/[0.32]'
const DIFF_REMOVE_CLASS =
  'diff-review-mark border-red-700/35 bg-red-600/[0.12] text-red-950/90 line-through decoration-red-700/45 hover:border-red-700/50 hover:bg-red-600/[0.18] dark:border-red-400/70 dark:bg-red-500/20 dark:text-red-100/90 dark:decoration-red-400/60 dark:hover:border-red-300 dark:hover:bg-red-500/[0.28]'

function promptRewrites(current: string): string[] {
  const base = current.trim()
  if (!base) return ['Name the output in the first line.']
  const sentence = base.split('. ')[0] ?? base
  const tightened = sentence.endsWith('.') ? sentence : `${sentence}.`
  const options = [
    tightened,
    `${base} Name the overdue items first.`,
    `Use the previous step's output.\n\n${base}`,
  ]
  const unique = options.filter((item, index) => item !== base && options.indexOf(item) === index)
  return unique.length > 0 ? unique : [`${base} Be specific about the output.`]
}

function literalRewrites(current: string, long: boolean): string[] {
  if (long) return promptRewrites(current)
  const base = current.trim()
  if (/^\d+$/.test(base)) {
    const n = Number(base)
    return [`${Math.max(1, Math.round(n * 1.6))}`, `${Math.max(1, Math.round(n * 0.6))}`].filter(item => item !== base)
  }
  const sentence = base.split('. ')[0] ?? base
  const tightened = sentence.endsWith('.') ? sentence : `${sentence}.`
  return tightened !== base ? [tightened] : [`${base} revised`]
}

function InspectorLiteralField({
  field,
  value,
  long,
  washing,
  marked,
  onCommit,
  onAsk,
  onFocusField,
}: {
  field: string
  value: string
  long: boolean
  washing: boolean
  marked: boolean
  onCommit: (next: string, source: 'user' | 'accept') => 'saved' | 'proposed'
  onAsk?: (draft: string) => void
  onFocusField?: () => void
}) {
  const [draft, setDraft] = useState(value)
  const [preview, setPreview] = useState<string[] | null>(null)
  const [previewIndex, setPreviewIndex] = useState(0)
  const [note, setNote] = useState<string | null>(null)
  const focused = useRef(false)
  const holdCommit = useRef(false)
  const returnFocus = useRef(false)

  useEffect(() => {
    if (!focused.current) setDraft(value)
  }, [value])

  useEffect(() => {
    if (preview || !returnFocus.current) return
    returnFocus.current = false
    document.querySelector<HTMLElement>(`[data-inspector-field="${field}"]`)?.focus()
  }, [preview, field])

  useEffect(() => {
    if (!note) return
    const timer = window.setTimeout(() => setNote(null), 1600)
    return () => window.clearTimeout(timer)
  }, [note])

  const commit = () => {
    focused.current = false
    if (holdCommit.current) {
      holdCommit.current = false
      return
    }
    if (draft === value) return
    setNote(onCommit(draft, 'user') === 'proposed' ? 'Waiting for approval' : 'Saved')
  }

  const openPreview = () => {
    holdCommit.current = true
    const options = literalRewrites(draft, long).filter(item => item !== draft)
    setPreview(options.length > 0 ? options : literalRewrites(draft, long))
    setPreviewIndex(0)
    window.setTimeout(() => {
      holdCommit.current = false
    }, 0)
  }

  const acceptSuggestion = (next: string) => {
    setDraft(next)
    setPreview(null)
    focused.current = false
    setNote(onCommit(next, 'accept') === 'proposed' ? 'Waiting for approval' : 'Saved')
  }

  const rejectSuggestion = () => {
    returnFocus.current = true
    setPreview(null)
  }

  const suggestion = preview?.[previewIndex] ?? null
  const hunk = suggestion ? promptHunk(draft, suggestion) : null

  const reviewButton = (
    kind: 'add' | 'remove' | 'quiet',
    label: string,
    onClick: () => void,
    testId?: string,
  ) => (
    <button
      type="button"
      data-review-hover={kind === 'add' ? 'accept' : kind === 'remove' ? 'reject' : undefined}
      data-testid={testId}
      className={cn(
        'inline-flex h-6 items-center gap-1 rounded-md border px-2 text-[12px] leading-none',
        kind === 'add'
          ? 'diff-review-accept'
          : 'border-border/80 bg-background text-muted-foreground hover:bg-muted/70 hover:text-foreground',
      )}
      onMouseDown={event => event.preventDefault()}
      onClick={event => {
        event.stopPropagation()
        onClick()
      }}
    >
      {kind === 'add' ? <Check className="h-3 w-3" /> : null}
      {label}
    </button>
  )

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {humanLabel(field)}
        </p>
        {marked && (
          <Sparkles
            className="h-3 w-3 text-primary"
            aria-label="Maater changed this field"
            data-testid={testIds.flowsStudioPrototype.flowAgentMark}
          />
        )}
        {note && <span className="text-[11px] text-muted-foreground">{note}</span>}
        <button
          type="button"
          className="ml-auto flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Suggest a rewrite"
          aria-label="Suggest a rewrite"
          data-testid={field === 'prompt' ? testIds.flowsStudioPrototype.flowInspectorSuggest : undefined}
          onMouseDown={event => event.preventDefault()}
          onClick={openPreview}
        >
          <Sparkles className="h-3.5 w-3.5" />
        </button>
      </div>
      {hunk && suggestion ? (
        long ? (
          <div
            className="group/hunk relative pl-6 text-xs leading-relaxed"
            data-testid={testIds.flowsStudioPrototype.flowInspectorSuggestion}
            onClick={event => {
              if ((event.target as HTMLElement).closest('button')) return
              rejectSuggestion()
            }}
          >
            {hunk.same ? <p className="whitespace-pre-wrap text-foreground/90">{hunk.same}</p> : null}
            {hunk.removed ? (
              <div className={cn('relative', hunk.same && 'mt-[0.75em]')}>
                <span className="diff-review-gutter select-none text-[11px] font-medium text-red-600 opacity-0 transition-opacity duration-200 group-hover/hunk:opacity-100 dark:text-red-400" aria-hidden>-</span>
                <div data-diff-kind="remove" className={cn(DIFF_REMOVE_CLASS, 'whitespace-pre-wrap')}>{hunk.removed}</div>
                <div className="diff-review-hunk-actions">
                  {reviewButton('remove', 'Reject', rejectSuggestion)}
                  {!hunk.added ? reviewButton('add', 'Accept', () => acceptSuggestion(suggestion)) : null}
                </div>
              </div>
            ) : null}
            {hunk.added ? (
              <div className={cn('relative', (hunk.same || hunk.removed) && 'mt-[0.75em]')}>
                <span className="diff-review-gutter select-none text-[11px] font-medium text-green-600 opacity-0 transition-opacity duration-200 group-hover/hunk:opacity-100 dark:text-green-400" aria-hidden>+</span>
                <div data-diff-kind="add" className={cn(DIFF_ADD_CLASS, 'whitespace-pre-wrap')}>{hunk.added}</div>
                <div className="diff-review-hunk-actions">
                  {reviewButton('add', 'Accept', () => acceptSuggestion(suggestion))}
                  {(preview?.length ?? 0) > 1
                    ? reviewButton('quiet', 'Try another', () => setPreviewIndex(index => (index + 1) % (preview?.length ?? 1)))
                    : null}
                  {onAsk
                    ? reviewButton('quiet', 'Ask Maater', () => onAsk(draft), testIds.flowsStudioPrototype.flowInspectorAsk)
                    : null}
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div
            className="group/hunk relative"
            data-testid={testIds.flowsStudioPrototype.flowInspectorSuggestion}
          >
            <div className="flex items-center gap-2">
              <span data-diff-kind="remove" className={cn(DIFF_REMOVE_CLASS, 'shrink-0 px-1.5 py-0.5 text-xs')}>{draft}</span>
              <Input
                readOnly
                value={suggestion}
                data-diff-kind="add"
                className={cn('h-8 min-w-0 flex-1 text-xs', DIFF_ADD_CLASS)}
                onClick={rejectSuggestion}
              />
            </div>
            <div className="diff-review-hunk-actions">
              {reviewButton('remove', 'Reject', rejectSuggestion)}
              {reviewButton('add', 'Accept', () => acceptSuggestion(suggestion))}
              {(preview?.length ?? 0) > 1
                ? reviewButton('quiet', 'Try another', () => setPreviewIndex(index => (index + 1) % (preview?.length ?? 1)))
                : null}
            </div>
          </div>
        )
      ) : long ? (
        <Textarea
          data-inspector-field={field}
          data-testid={field === 'prompt' ? testIds.flowsStudioPrototype.flowInspectorPrompt : undefined}
          value={draft}
          className={cn(
            'min-h-24 text-xs leading-relaxed transition-colors duration-200',
            washing && 'border-primary bg-primary/10',
          )}
          onChange={event => setDraft(event.target.value)}
          onFocus={() => {
            focused.current = true
            setPreview(null)
            onFocusField?.()
          }}
          onBlur={commit}
        />
      ) : (
        <Input
          data-inspector-field={field}
          value={draft}
          className={cn('h-8 text-xs transition-colors duration-200', washing && 'border-primary bg-primary/10')}
          onChange={event => setDraft(event.target.value)}
          onFocus={() => {
            focused.current = true
            setPreview(null)
            onFocusField?.()
          }}
          onBlur={commit}
        />
      )}
    </div>
  )
}

function FlowInfoField({
  label,
  value,
  mono,
}: {
  label: string
  value: string
  mono?: boolean
}) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={cn('mt-0.5 text-sm text-foreground', mono && 'font-mono text-xs break-all')}>
        {value}
      </div>
    </div>
  )
}

function inboxOutputStepIds(
  flow: StudioFlow,
  run: FeedRun,
  states: Record<string, StepRunState> | undefined,
): string[] {
  if (!run.threadLink || !states) return []
  return flow.steps
    .filter(s => s.registryId === 'inbox.post_thread' && states[s.id] === 'ok')
    .map(s => s.id)
}

function ReplayWithPayloadButton({
  onClick,
}: {
  onClick: () => void
}) {
  return (
    <div className="space-y-1.5">
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onClick}>
        <Wand2 className="mr-1 h-3 w-3" /> Replay with captured payload
      </Button>
      <p className="text-xs text-muted-foreground">
        Opens Runs with this event's saved payload, focused on the failed step.
      </p>
    </div>
  )
}

function RunsInboxThreadPeek({ run }: { run: FeedRun }) {
  return (
    <div className="space-y-3 p-4">
      <p className="text-[17px] font-semibold leading-snug text-foreground">{run.headline}</p>
      <p className="text-[13px] text-muted-foreground">Posted by this run · {run.when}</p>
      <p className="text-[13px] leading-relaxed text-foreground/90">{run.detail}</p>
    </div>
  )
}

function RunsInspectorBody({
  flow,
  run,
  peekId,
  onFix,
}: {
  flow: StudioFlow
  run: FeedRun
  peekId: string
  onFix: (flowId: string, stepRegistryId?: string) => void
}) {
  const states = stepStatesForRun(run, flow) ?? {}
  const payload = CAPTURED_PAYLOADS[flow.trigger.registryId] ?? {}
  const isTrigger = peekId === FLOW_TRIGGER_NODE
  const step = !isTrigger && peekId ? (flow.steps.find(s => s.id === peekId) ?? null) : null
  const st = step ? states[step.id] : undefined
  const isInboxOutput =
    step?.registryId === 'inbox.post_thread' && st === 'ok' && Boolean(run.threadLink)

  if (!peekId) {
    return (
      <div className="space-y-3 p-4">
        <FlowInfoField label="Status" value={run.status} />
        <FlowInfoField label="Started" value={run.when} />
        <FlowInfoField label="Trigger" value={flow.trigger.registryId} mono />
        {typeof run.costUsd === 'number' && (
          <FlowInfoField label="Cost" value={`$${run.costUsd.toFixed(2)} (est.)`} />
        )}
        <p className="text-sm leading-relaxed text-foreground/90">{run.headline}</p>
        <p className="text-sm text-muted-foreground">{run.detail}</p>
        {run.status === 'failed' && (
          <ReplayWithPayloadButton onClick={() => onFix(flow.id, run.failedStep)} />
        )}
      </div>
    )
  }

  if (isTrigger) {
    return (
      <div className="space-y-3 p-4">
        <FlowInfoField label="Trigger" value={flow.trigger.label} />
        <FlowInfoField label="Registry" value={flow.trigger.registryId} mono />
        <FlowInfoField label="When" value={flow.trigger.plainSummary} />
        {Object.entries(payload)
          .slice(0, 6)
          .map(([k, v]) => (
            <FlowInfoField key={k} label={humanLabel(k)} value={v} />
          ))}
      </div>
    )
  }

  if (isInboxOutput) {
    return <RunsInboxThreadPeek run={run} />
  }

  if (step) {
    return (
      <div className="space-y-3 p-4">
        <FlowInfoField label="Step" value={step.label} />
        <FlowInfoField label="Registry" value={step.registryId} mono />
        <FlowInfoField label="Status" value={st ?? 'pending'} />
        {st === 'failed' && step.registryId === 'document.create' && (
          <>
            <FlowInfoField label="Error" value="Unresolved Meeting topic" />
            <p className="text-sm text-muted-foreground">
              This step expected Meeting topic from the trigger. This run's payload did not include it.
            </p>
            <ReplayWithPayloadButton onClick={() => onFix(flow.id, step.registryId)} />
          </>
        )}
        {st === 'failed' && step.registryId !== 'document.create' && (
          <ReplayWithPayloadButton onClick={() => onFix(flow.id, step.registryId)} />
        )}
        {st === 'skipped' && (
          <p className="text-sm text-muted-foreground">
            This step did not run because an earlier step failed.
          </p>
        )}
        {st === 'running' && (
          <p className="text-sm text-muted-foreground">This step is running.</p>
        )}
        {st === 'pending' && (
          <p className="text-sm text-muted-foreground">Waiting for earlier steps.</p>
        )}
        {st === 'stubbed' && (
          <p className="text-sm text-muted-foreground">
            Shadow stubbed this side effect. Nothing was posted or written.
          </p>
        )}
        {st === 'ok' && (
          <p className="text-sm text-muted-foreground">Completed on this run.</p>
        )}
      </div>
    )
  }

  return null
}

function FlowDocumentSurface({
  flows,
  activeFlowId,
  activeStepId,
  setActiveStepId,
  setMode,
  flowDocTab,
  setFlowDocTab,
  onFix,
  onPatchFlow,
  onOpenRefine,
  agentApply,
  canGoBack,
  canGoForward,
  onBack,
  onForward,
}: {
  flows: StudioFlow[]
  activeFlowId: string
  activeStepId: string
  setActiveStepId: (id: string) => void
  setMode: (id: string, mode: RuntimeMode) => void
  flowDocTab: FlowDocTab
  setFlowDocTab: (tab: FlowDocTab) => void
  onFix: (flowId: string, stepRegistryId?: string) => void
  onPatchFlow: (id: string, patch: Partial<StudioFlow>) => void
  onOpenRefine: (session: StudioAgentSession) => void
  agentApply: { flowId: string; nonce: number; stepId?: string } | null
  canGoBack?: boolean
  canGoForward?: boolean
  onBack?: () => void
  onForward?: () => void
}) {
  const flow = flows.find(f => f.id === activeFlowId) ?? flows[0]
  const feedRuns = FEED.filter(r => r.flowId === flow.id)
  const [selectedRunId, setSelectedRunId] = useState(feedRuns[0]?.id ?? '')
  const [docInfoOpen, setDocInfoOpen] = useState(true)
  const [nodeMenu, setNodeMenu] = useState<{ x: number; y: number; nodeId: string } | null>(null)
  const [runsPeekId, setRunsPeekId] = useState('')
  const [historyLive, setHistoryLive] = useState<FeedRun[]>([])
  const [inFlight, setInFlight] = useState<{
    run: FeedRun
    stepStates: Record<string, StepRunState>
  } | null>(null)
  const [pendingSteps, setPendingSteps] = useState<StudioStep[] | null>(null)
  const [proposalNote, setProposalNote] = useState<string | null>(null)
  const [agentTouch, setAgentTouch] = useState<{ stepId: string; fields: string[]; washing: boolean } | null>(null)
  const [definitionView, setDefinitionView] = useState<'visual' | 'source'>('visual')
  const washTimer = useRef<number | null>(null)
  const liveGen = useRef(0)
  const seenAgentApply = useRef(0)

  const openRefine = () => {
    setFlowDocTab('overview')
    onOpenRefine({
      key: `flow-${flow.id}`,
      prompt: `Refine ${flow.name}`,
      skill: '/edit-flow',
      contextLabel: flow.name,
      intro: `This is the saved flow (${flow.mode}). Chip and sentence edits on Definition are always allowed. Agent Refine proposes a change on the pipeline - you do not need to disable the flow first. Live Approve demotes to Shadow.`,
      appliedReply:
        flow.mode === 'live'
          ? 'Proposed adding "Shape filing suggestions" on Definition. Approve will apply and demote to Shadow.'
          : `Proposed adding "Shape filing suggestions" on Definition. Approve will apply and keep ${flow.mode}.`,
      flowId: flow.id,
    })
  }

  const markAgentTouch = useCallback((stepId: string, fields: string[]) => {
    if (washTimer.current != null) window.clearTimeout(washTimer.current)
    const washing = !prefersReducedRowMotion()
    setAgentTouch({ stepId, fields, washing })
    setActiveStepId(stepId)
    setDocInfoOpen(true)
    if (washing) {
      washTimer.current = window.setTimeout(() => {
        setAgentTouch(prev => (prev && prev.stepId === stepId ? { ...prev, washing: false } : prev))
      }, 1200)
    }
    if (!prefersReducedRowMotion()) {
      document.querySelector(`[data-flow-node="${stepId}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [setActiveStepId])

  useEffect(() => {
    if (!agentApply || agentApply.flowId !== flow.id) return
    if (agentApply.nonce === seenAgentApply.current) return
    seenAgentApply.current = agentApply.nonce
    if (agentApply.stepId) {
      setPendingSteps(proposeStepPrompt(flow.steps, agentApply.stepId))
      setProposalNote('Proposed a prompt change. The saved definition is unchanged until you Approve.')
      markAgentTouch(agentApply.stepId, ['prompt'])
    } else {
      setPendingSteps(proposedRefineSteps(flow))
      setProposalNote(null)
      markAgentTouch('refine-shape', ['prompt'])
    }
    setFlowDocTab('overview')
  }, [agentApply, flow, markAgentTouch, setFlowDocTab])

  useEffect(() => {
    liveGen.current += 1
    setInFlight(null)
    setRunsPeekId('')
  }, [flow.id])

  const extraRuns = historyLive.filter(r => r.flowId === flow.id)
  const runs = [
    ...(inFlight?.run.flowId === flow.id ? [inFlight.run] : []),
    ...extraRuns,
    ...feedRuns,
  ]
  const selectedRun = runs.find(r => r.id === selectedRunId) ?? runs[0] ?? null
  const pathParts = flow.path.split('/').filter(Boolean)
  const filePath = `${flow.path}/${flow.slug}.md`
  const runStates = selectedRun
    ? inFlight && selectedRun.id === inFlight.run.id
      ? inFlight.stepStates
      : stepStatesForRun(selectedRun, flow)
    : undefined
  const inboxStepIds = selectedRun ? inboxOutputStepIds(flow, selectedRun, runStates) : []
  const runsIsTrigger = runsPeekId === FLOW_TRIGGER_NODE
  const runsPeekStep = !runsIsTrigger && runsPeekId
    ? (flow.steps.find(s => s.id === runsPeekId) ?? null)
    : null
  const gate = manualRunGate(flow)
  const liveFeedRun = activeFeedRun(flow.id)
  const liveFeedMeta = liveFeedRun ? runMeta(liveFeedRun) : null
  const liveRun = liveFeedRun
    ? {
        stepStates: runningDefinitionStates(flow, liveFeedMeta),
        runningForSec: liveFeedMeta?.runningForSec ?? undefined,
      }
    : null
  const runBusy = inFlight?.run.flowId === flow.id
  const runNowDisabled = runBusy || !gate.ok
  const runNowTitle = runBusy ? 'A run is in progress' : gate.ok ? gate.hint : gate.reason

  const selectRunsNode = (id: string) => {
    setRunsPeekId(id)
    setDocInfoOpen(true)
  }

  const selectRunSummary = () => {
    setRunsPeekId('')
    setDocInfoOpen(true)
  }

  const clearRunsPeek = () => setRunsPeekId('')

  const copyPath = () => {
    void navigator.clipboard.writeText(filePath)
  }

  const handlePatchStep = (stepId: string, inputs: Record<string, string>, source: 'user' | 'accept' = 'user') => {
    if (source === 'user') {
      setAgentTouch(prev => {
        if (!prev || prev.stepId !== stepId) return prev
        const fields = prev.fields.filter(field => !(field in inputs))
        return fields.length > 0 ? { ...prev, fields, washing: false } : null
      })
    } else {
      markAgentTouch(stepId, Object.keys(inputs))
    }
    const base = pendingSteps ?? flow.steps
    if (pendingSteps || flow.mode === 'live') {
      setPendingSteps(mergeStepInputs(base, stepId, inputs))
      if (flow.mode === 'live' && !pendingSteps) {
        setProposalNote('Proposed a definition change. Approve demotes to Shadow.')
      }
      return 'proposed' as const
    }
    onPatchFlow(flow.id, { steps: mergeStepInputs(flow.steps, stepId, inputs) })
    return 'saved' as const
  }

  const handleRunNow = () => {
    if (runNowDisabled || !gate.ok) return
    const modeAtStart = flow.mode
    const gen = ++liveGen.current
    const runId = `live-${flow.id}-${Date.now()}`
    const pending = Object.fromEntries(flow.steps.map(s => [s.id, 'pending' as StepRunState]))
    const seed: FeedRun = {
      id: runId,
      flowId: flow.id,
      flowName: flow.name,
      status: 'running',
      when: 'Just now',
      headline: modeAtStart === 'shadow' ? 'Shadow run in progress' : 'Run in progress',
      detail: gate.hint,
    }
    setInFlight({ run: seed, stepStates: pending })
    setSelectedRunId(runId)
    setRunsPeekId('')
    setFlowDocTab('runs')
    setDocInfoOpen(true)

    flow.steps.forEach((step, i) => {
      window.setTimeout(() => {
        if (gen !== liveGen.current) return
        setInFlight(prev => {
          if (!prev || prev.run.id !== runId) return prev
          const next = { ...prev.stepStates }
          if (i > 0) {
            const prevStep = flow.steps[i - 1]
            next[prevStep.id] = outcomeForStep(prevStep, modeAtStart)
          }
          next[step.id] = 'running'
          return {
            run: {
              ...prev.run,
              detail: `${gate.hint} · Step ${i + 1} of ${flow.steps.length}: ${step.label}`,
            },
            stepStates: next,
          }
        })
        setRunsPeekId(step.id)
      }, i * LIVE_STEP_MS)
    })

    window.setTimeout(() => {
      if (gen !== liveGen.current) return
      const finalStates = Object.fromEntries(
        flow.steps.map(s => [s.id, outcomeForStep(s, modeAtStart)]),
      )
      const hasInbox = flow.steps.some(s => s.registryId === 'inbox.post_thread')
      const finished: FeedRun = {
        id: runId,
        flowId: flow.id,
        flowName: flow.name,
        status: modeAtStart === 'shadow' ? 'shadow' : 'ok',
        when: 'Just now',
        headline:
          modeAtStart === 'shadow'
            ? hasInbox
              ? 'Would have posted to inbox'
              : 'Shadow run finished'
            : hasInbox
              ? 'Posted to inbox'
              : 'Run finished',
        detail: gate.hint,
        threadLink: modeAtStart === 'live' && hasInbox,
        stepStates: finalStates,
      }
      setHistoryLive(prev => [finished, ...prev])
      setInFlight(null)
      setSelectedRunId(runId)
      setRunsPeekId('')
    }, flow.steps.length * LIVE_STEP_MS)
  }

  const documentToolbar = (
    <div
      className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-3"
      data-testid={testIds.flowsStudioPrototype.flowDocumentTabs}
    >
      <Segmented
        value={flowDocTab}
        onChange={setFlowDocTab}
        options={[
          { id: 'overview', label: 'Definition' },
          { id: 'runs', label: 'Runs' },
        ]}
      />
      <Button
        size="sm"
        variant="outline"
        className="h-8 px-2.5 text-sm"
        onClick={handleRunNow}
        disabled={runNowDisabled}
        title={runNowTitle}
        data-testid={testIds.flowsStudioPrototype.flowDocumentRunNow}
      >
        {runBusy ? (
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
        ) : (
          <Play className="mr-1 h-3 w-3" />
        )}
        {runBusy ? 'Running' : 'Run now'}
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="h-8 px-2.5 text-sm"
        onClick={openRefine}
        data-testid={testIds.flowsStudioPrototype.flowDocumentRefine}
      >
        <Bot className="mr-1 h-3 w-3" />
        Refine
      </Button>
    </div>
  )

  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      data-testid={testIds.flowsStudioPrototype.flowDocument}
      onContextMenu={event => {
        event.preventDefault()
        event.stopPropagation()
        const node = (event.target as HTMLElement).closest('[data-flow-node]')
        setNodeMenu({
          x: event.clientX,
          y: event.clientY,
          nodeId: node?.getAttribute('data-flow-node') ?? '',
        })
      }}
    >
      <ProtoTitleRow
        crumbs={['Flows', ...pathParts]}
        title={flow.name}
        canGoBack={canGoBack}
        canGoForward={canGoForward}
        onBack={onBack}
        onForward={onForward}
        testId={testIds.flowsStudioPrototype.titleRow}
        backTestId={testIds.flowsStudioPrototype.backButton}
        forwardTestId={testIds.flowsStudioPrototype.forwardButton}
        actions={
          <>
            {liveRun && (
              <span
                className="inline-flex items-center gap-1.5"
                data-testid={testIds.flowsStudioPrototype.flowDocumentRunning}
              >
                {statusBadge('running')}
                {liveRun.runningForSec != null && (
                  <span className="text-xs text-muted-foreground">
                    <LiveDuration baseSec={liveRun.runningForSec} />
                  </span>
                )}
              </span>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-1.5 px-2.5 text-sm"
                  data-testid={testIds.flowsStudioPrototype.flowDocumentRuntime}
                >
                  {flow.mode === 'draft' ? (
                    <PauseCircle className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : flow.mode === 'shadow' ? (
                    <Eye className="h-3.5 w-3.5 text-primary" />
                  ) : (
                    <Zap className="h-3.5 w-3.5 text-green-500" />
                  )}
                  {flow.mode === 'draft'
                    ? 'Disabled'
                    : flow.mode === 'shadow'
                      ? 'Shadow'
                      : 'Live'}
                  <ChevronDown className="h-3 w-3 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
              >
                <DropdownMenuItem onSelect={() => setMode(flow.id, 'draft')}>
                  <PauseCircle className="mr-2 h-4 w-4" />
                  Disabled
                  {flow.mode === 'draft' ? <Check className="ml-auto h-4 w-4" /> : null}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setMode(flow.id, 'shadow')}>
                  <Eye className="mr-2 h-4 w-4" />
                  Shadow
                  {flow.mode === 'shadow' ? <Check className="ml-auto h-4 w-4" /> : null}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setMode(flow.id, 'live')}>
                  <Zap className="mr-2 h-4 w-4" />
                  Live
                  {flow.mode === 'live' ? <Check className="ml-auto h-4 w-4" /> : null}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <PreviewPanelToggle
              open={docInfoOpen}
              onToggle={() => setDocInfoOpen(v => !v)}
              paneLabel="details"
              testId={testIds.flowsStudioPrototype.flowDocumentInfoButton}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton
                  icon={<MoreVertical className="h-4 w-4" />}
                  label="More options"
                  variant="ghost"
                  size="sm"
                  data-testid={testIds.flowsStudioPrototype.flowDocumentMenu}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
              >
                <DropdownMenuItem onSelect={handleRunNow} disabled={runNowDisabled}>
                  <Play className="mr-2 h-4 w-4" />
                  {runBusy ? 'Running' : 'Run now'}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setFlowDocTab('runs')}>
                  <Activity className="mr-2 h-4 w-4" />
                  Open runs
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={openRefine}>
                  <Bot className="mr-2 h-4 w-4" />
                  Refine
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <List className="mr-2 h-4 w-4" />
                    Steps
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent
                    collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                    className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}
                  >
                    {flow.steps.map((step, index) => (
                      <DropdownMenuItem
                        key={step.id}
                        onSelect={() => {
                          setActiveStepId(step.id)
                          setFlowDocTab('overview')
                          setDocInfoOpen(true)
                        }}
                      >
                        <span className="mr-2 w-4 text-center text-xs text-muted-foreground">{index + 1}</span>
                        {step.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={copyPath}>
                  <Copy className="mr-2 h-4 w-4" />
                  Copy path
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      {flowDocTab === 'overview' ? (
        <BuilderSurface
          key={activeFlowId}
          flows={flows}
          activeFlowId={activeFlowId}
          activeStepId={activeStepId}
          setActiveStepId={setActiveStepId}
          hideChrome
          leading={
            <>
              {documentToolbar}
              {pendingSteps ? (
                <div
                  className="flex shrink-0 items-center gap-2 border-b border-primary/25 bg-primary/5 px-3 py-1.5"
                  data-testid={testIds.flowsStudioPrototype.flowDocumentProposal}
                >
                  <Bot className="h-3.5 w-3.5 shrink-0 text-primary" />
                  <p className="min-w-0 flex-1 text-xs text-muted-foreground">
                    {proposalNote ?? 'Proposed Refine. The saved definition is unchanged until you Approve.'}
                    {!proposalNote && flow.mode === 'live' ? ' Approve demotes to Shadow.' : ''}
                  </p>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setPendingSteps(null); setProposalNote(null); setAgentTouch(null) }}>
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      onPatchFlow(flow.id, {
                        steps: pendingSteps,
                        mode: flow.mode === 'live' ? 'shadow' : flow.mode,
                      })
                      setPendingSteps(null)
                      setProposalNote(null)
                      setDocInfoOpen(true)
                    }}
                  >
                    <Check className="mr-1 h-3 w-3" />
                    {flow.mode === 'live' ? 'Approve - demote to Shadow' : 'Approve'}
                  </Button>
                </div>
              ) : null}
            </>
          }
          sourceView={definitionView}
          liveRun={liveRun}
          peekOpen={docInfoOpen}
          onPeekOpen={() => setDocInfoOpen(true)}
          onPeekClose={() => setDocInfoOpen(false)}
          stepsOverride={pendingSteps ?? undefined}
          onPatchStep={handlePatchStep}
          agentTouch={agentTouch}
          onAskStep={(step, draft) => {
            const clipped = draft.trim().slice(0, 180)
            onOpenRefine({
              key: `step-${flow.id}-${step.id}`,
              prompt: '',
              skill: '/edit-flow',
              contextLabel: step.label,
              intro: clipped
                ? `This step is in context. Current prompt draft: "${clipped}". Tell me what should change. I will not write until you approve.`
                : 'This step is in context. Tell me what should change. I will not write until you approve.',
              appliedReply: `Updated the prompt on ${step.label}.`,
              placeholder: 'What should change?',
              flowId: flow.id,
              stepId: step.id,
            })
          }}
        />
      ) : (
        <PanelGroup direction="horizontal" className="min-h-0 flex-1">
          <Panel minSize={35} className="flex min-h-0 flex-col overflow-hidden">
            {documentToolbar}
            <div
              className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[220px_minmax(0,1fr)]"
              data-testid={testIds.flowsStudioPrototype.flowDocumentRuns}
            >
              <div className="min-h-0 overflow-y-auto border-r border-border">
                {runs.length === 0 && (
                  <p className="px-4 py-5 text-sm leading-relaxed text-muted-foreground">No runs yet.</p>
                )}
                {runs.map(run => (
                  <button
                    key={run.id}
                    type="button"
                    onClick={() => {
                      setSelectedRunId(run.id)
                      setRunsPeekId('')
                    }}
                    className={cn(
                      'w-full border-b border-border px-3 py-3 text-left transition-colors',
                      selectedRun?.id === run.id
                        ? 'border-l-2 border-l-primary bg-primary/5'
                        : 'border-l-2 border-l-transparent hover:bg-muted/40',
                    )}
                  >
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      {statusBadge(run.status)}
                      {run.status === 'running' && (
                        <Loader2 className="h-3 w-3 animate-spin text-primary" />
                      )}
                    </div>
                    <p className="text-xs font-medium tabular-nums text-foreground/80">{run.when}</p>
                    <p className="mt-1 truncate text-xs leading-snug text-muted-foreground">
                      {flow.trigger.registryId}
                    </p>
                  </button>
                ))}
              </div>
              <div
                className="min-h-0 overflow-y-auto p-5"
                onClick={clearRunsPeek}
              >
                {!selectedRun && (
                  <p className="text-sm leading-relaxed text-muted-foreground">Select a run.</p>
                )}
                {selectedRun && (
                  <div className="space-y-3" onClick={e => e.stopPropagation()}>
                    <div
                      role="button"
                      tabIndex={0}
                      data-testid={testIds.flowsStudioPrototype.flowRunSummary}
                      aria-pressed={!runsPeekId}
                      onClick={selectRunSummary}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          selectRunSummary()
                        }
                      }}
                      className={cn(
                        'cursor-pointer rounded-lg border p-4 text-left transition-colors',
                        runsPeekId
                          ? 'border-border hover:border-primary/40'
                          : 'border-primary bg-primary/5',
                      )}
                    >
                      <p className="mb-1.5 text-sm font-semibold">Run summary</p>
                      <p className="text-sm leading-relaxed text-foreground/90">{selectedRun.headline}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{selectedRun.detail}</p>
                      <p className="mt-3 text-sm text-muted-foreground">Started {selectedRun.when}</p>
                    </div>
                    <FlowPipeline
                      flow={flow}
                      mode="execution"
                      density="full"
                      activeStepId={runsPeekId}
                      onSelectStep={selectRunsNode}
                      payload={CAPTURED_PAYLOADS[flow.trigger.registryId]}
                      stepStates={runStates}
                      statusCopy="run"
                      inboxThreadStepIds={inboxStepIds}
                      onViewInbox={selectRunsNode}
                    />
                  </div>
                )}
              </div>
            </div>
          </Panel>
          {docInfoOpen && selectedRun ? (
            <>
              <SecondaryPreviewResizeHandle />
              <SecondaryPanel
                panelType="flow-run"
                panelId="flow-run-peek"
                defaultSize={PANEL_SIZES.SECONDARY_DEFAULT}
                minSize={PANEL_SIZES.SECONDARY_MIN}
                background="card"
                className="min-w-0 overflow-hidden"
              >
                <div
                  className="flex h-full min-h-0 flex-col"
                  data-testid={testIds.flowsStudioPrototype.flowDocumentInfo}
                >
                  <PreviewPaneHeader
                    row
                    label={runsIsTrigger ? flow.trigger.label : (runsPeekStep?.label ?? 'This run')}
                    onClose={() => setDocInfoOpen(false)}
                  />
                  <div className="min-h-0 flex-1 overflow-auto">
                    <RunsInspectorBody
                      flow={flow}
                      run={selectedRun}
                      peekId={runsPeekId}
                      onFix={onFix}
                    />
                  </div>
                </div>
              </SecondaryPanel>
            </>
          ) : null}
        </PanelGroup>
      )}
      <ListSurfaceStatusBar
        leading={
          <>
            {flowDocTab === 'overview' && (
              <DefinitionViewToggle value={definitionView} onChange={setDefinitionView} />
            )}
            <StudioPageWidthToggle />
          </>
        }
        tips="Column keeps the pipeline in a reading measure. Full uses the panel width. Visual and Source use the same footer as a document."
      />
      {nodeMenu ? (
        <EntityContextMenu
          x={nodeMenu.x}
          y={nodeMenu.y}
          onClose={() => setNodeMenu(null)}
          data-testid={testIds.flowsStudioPrototype.flowDocumentContextMenu}
          entries={[
            {
              label: 'Preview',
              icon: OpenDestinationIcons.preview,
              disabled:
                docInfoOpen &&
                (flowDocTab === 'runs'
                  ? runsPeekId === nodeMenu.nodeId
                  : activeStepId === nodeMenu.nodeId),
              onSelect: () => {
                if (flowDocTab === 'runs') setRunsPeekId(nodeMenu.nodeId)
                else setActiveStepId(nodeMenu.nodeId)
                setDocInfoOpen(true)
              },
            },
            flowOpenSubmenu(tab => setFlowDocTab(tab)),
          ]}
        />
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

function BuilderSurface({
  flows,
  activeFlowId,
  activeStepId,
  setActiveStepId,
  hideChrome,
  sourceView = 'visual',
  liveRun = null,
  peekOpen,
  onPeekOpen,
  onPeekClose,
  stepsOverride,
  onPatchStep,
  agentTouch,
  onAskStep,
  leading,
  canGoBack,
  canGoForward,
  onBack,
  onForward,
}: {
  flows: StudioFlow[]
  activeFlowId: string
  setActiveFlowId?: (id: string) => void
  activeStepId: string
  setActiveStepId: (id: string) => void
  /** When nested as Definition, skip the duplicate title row */
  hideChrome?: boolean
  /** Visual pipeline or source YAML. The document footer owns the toggle. */
  sourceView?: 'visual' | 'source'
  /** In-progress feed run. Definition shows the same Running badge as Activity. */
  liveRun?: {
    stepStates: Record<string, StepRunState>
    runningForSec?: number
  } | null
  /** Title-row (i) pin - Definition secondary panel */
  peekOpen?: boolean
  onPeekOpen?: () => void
  onPeekClose?: () => void
  /** Pending Refine overlay - pipeline shows proposed steps until Approve */
  stepsOverride?: StudioStep[]
  onPatchStep?: (stepId: string, inputs: Record<string, string>, source: 'user' | 'accept') => 'saved' | 'proposed'
  /** Step and fields Maater just changed. Wash is one-shot. The mark stays. */
  agentTouch?: { stepId: string; fields: string[]; washing: boolean } | null
  /** Open the agent on this step. The draft is context. Nothing is sent. */
  onAskStep?: (step: StudioStep, draft: string) => void
  /** Left-column chrome that shares a line with the peek header (Definition toolbar). */
  leading?: ReactNode
  canGoBack?: boolean
  canGoForward?: boolean
  onBack?: () => void
  onForward?: () => void
}) {
  const [bindingField, setBindingField] = useState<string | null>(null)
  const [, setEditingLiteral] = useState<string | null>(null)
  const [inspectorOpen, setInspectorOpen] = useState(!hideChrome)
  const panelOpen = hideChrome ? Boolean(peekOpen) : inspectorOpen

  const flow = flows.find(f => f.id === activeFlowId) ?? flows[0]
  const pipelineFlow = useMemo(
    () => (stepsOverride ? { ...flow, steps: stepsOverride } : flow),
    [flow, stepsOverride],
  )
  const proposalView = stepsOverride ? proposalPipeline(flow.steps, stepsOverride) : null
  const isTriggerNode = activeStepId === FLOW_TRIGGER_NODE
  const step = isTriggerNode ? null : (pipelineFlow.steps.find(s => s.id === activeStepId) ?? null)
  const peekedNode = isTriggerNode || Boolean(step)
  const peekLabel = isTriggerNode ? flow.trigger.label : (step?.label ?? flow.name)
  const filePath = `${flow.path}/${flow.slug}.md`

  const clearToFlowPeek = () => {
    setActiveStepId('')
    setBindingField(null)
    setEditingLiteral(null)
    if (hideChrome) onPeekOpen?.()
  }

  const selectNode = (id: string) => {
    setActiveStepId(id)
    setBindingField(null)
    setEditingLiteral(null)
    if (hideChrome) onPeekOpen?.()
    else setInspectorOpen(true)
  }
  const payload = CAPTURED_PAYLOADS[flow.trigger.registryId] ?? {}
  const upstreamEmits = step
    ? pipelineFlow.steps.slice(0, pipelineFlow.steps.findIndex(s => s.id === step.id)).flatMap(s => s.emits)
    : []

  const sourceYaml = useMemo(() => {
    const stepsYaml = pipelineFlow.steps
      .map(s => {
        const inputs = Object.entries(s.inputs)
          .map(([k, v]) => `      ${k}: '${v}'`)
          .join('\n')
        const when = s.when ? `    when: '${s.when}'\n` : ''
        return `  - id: ${s.registryId}\n${when}    inputs:\n${inputs}`
      })
      .join('\n')
    return `---\nslug: ${flow.slug}\nkind: flow\ntriggers:\n  - id: ${flow.trigger.registryId}\nsteps:\n${stepsYaml}\n---`
  }, [flow, pipelineFlow])

  const builderActions = (
    <>
      {!hideChrome && (
        <PreviewPanelToggle
          open={inspectorOpen}
          onToggle={() => setInspectorOpen(v => !v)}
          paneLabel="details"
        />
      )}
    </>
  )

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid={testIds.flowsStudioPrototype.builder}>
      {!hideChrome && (
        <ProtoTitleRow
          crumbs={['Flows', 'Builder', flow.name]}
          title={flow.name}
          subtitle={
            <span className="inline-flex flex-wrap items-center gap-2">
              {modeBadge(flow.mode)}
              <span>{flow.trigger.plainSummary}</span>
            </span>
          }
          canGoBack={canGoBack}
          canGoForward={canGoForward}
          onBack={onBack}
          onForward={onForward}
          testId={testIds.flowsStudioPrototype.titleRow}
          backTestId={testIds.flowsStudioPrototype.backButton}
          forwardTestId={testIds.flowsStudioPrototype.forwardButton}
          actions={builderActions}
        />
      )}

      <PanelGroup direction="horizontal" className="min-h-0 flex-1">
        <Panel minSize={35} className="flex min-h-0 flex-col overflow-hidden">
        {leading}
        <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
          {sourceView === 'visual' ? (
            <div
              className="flex-1 p-6"
              onClick={clearToFlowPeek}
            >
              {flow.systemPaused && (
                <div className="mb-4" onClick={e => e.stopPropagation()}>
                  <SystemPausedNotice reason={flow.systemPaused} />
                </div>
              )}
              <FlowPipeline
                flow={proposalView ? { ...pipelineFlow, steps: proposalView.steps } : pipelineFlow}
                mode="definition"
                density="full"
                activeStepId={activeStepId}
                onSelectStep={selectNode}
                stepStates={stepsOverride ? undefined : liveRun?.stepStates}
                runningForSec={stepsOverride ? undefined : liveRun?.runningForSec}
                payload={payload}
                markedStepId={agentTouch?.stepId}
                washingStepId={agentTouch?.washing ? agentTouch.stepId : undefined}
                proposalRole={proposalView?.role}
              />
            </div>
          ) : (
            <div className="flex flex-1 flex-col overflow-hidden p-6">
              {flow.systemPaused && (
                <div className="mb-4">
                  <SystemPausedNotice reason={flow.systemPaused} />
                </div>
              )}
              {agentTouch && (
                <p className="mb-2 text-xs text-primary">
                  Maater changed {agentTouch.fields.join(', ')} on this step. History records the save, not each keystroke.
                </p>
              )}
              <pre className="flex-1 overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-muted/20 p-3 font-mono text-xs">
                {sourceYaml}
              </pre>
            </div>
          )}

          {flow.id === 'zoom-meeting-notes' && (
            <div className="flex items-center gap-2 border-t border-yellow-500/20 bg-yellow-500/5 px-3 py-1.5 text-xs text-yellow-600">
              <AlertTriangle className="h-3 w-3 shrink-0" />
              Zoom source is Partial - manual runs need a captured payload.
            </div>
          )}
        </section>

        </Panel>
        {panelOpen && (
          <>
            <SecondaryPreviewResizeHandle />
            <SecondaryPanel
              panelType="flow-definition"
              panelId="flow-definition-peek"
              defaultSize={PANEL_SIZES.SECONDARY_DEFAULT}
              minSize={PANEL_SIZES.SECONDARY_MIN}
              background="card"
              className="min-w-0 overflow-hidden"
            >
            <div
              className="flex h-full min-h-0 flex-col"
              data-testid={testIds.flowsStudioPrototype.propertyRail}
            >
              <PreviewPaneHeader
                row
                label={peekLabel}
                onClose={() => {
                  if (hideChrome) onPeekClose?.()
                  else setInspectorOpen(false)
                }}
              />
              <div className="min-h-0 flex-1 overflow-auto">
              {!peekedNode && (
                <div className="space-y-3 p-4">
                  <FlowInfoField label="Path" value={filePath} mono />
                  <FlowInfoField label="Slug" value={flow.slug} mono />
                  <FlowInfoField label="Type" value="Flow" />
                  <FlowInfoField label="Mode" value={flow.mode} />
                  {liveRun && (
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Run
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        {statusBadge('running')}
                        {liveRun.runningForSec != null && (
                          <span className="text-sm text-muted-foreground">
                            <LiveDuration baseSec={liveRun.runningForSec} />
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <FlowInfoField label="Trigger" value={flow.trigger.registryId} mono />
                  <FlowInfoField
                    label="Last / Next"
                    value={`${flow.lastRun} / ${flow.nextRun}`}
                  />
                  <FlowInfoField
                    label="Policy"
                    value="Up to 20 runs per hour (frontmatter)"
                  />
                </div>
              )}
              {isTriggerNode && (
                <div className="space-y-3 p-4">
                  <FlowInfoField label="Trigger" value={flow.trigger.label} />
                  <FlowInfoField label="Registry" value={flow.trigger.registryId} mono />
                  <FlowInfoField label="When" value={flow.trigger.plainSummary} />
                  <FlowInfoField
                    label="Last / Next"
                    value={`${flow.lastRun} / ${flow.nextRun}`}
                  />
                </div>
              )}
              {step && (
                <div className="space-y-4 p-4">
                  <FlowInfoField label="Registry" value={step.registryId} mono />
                  <div>
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Intent
                    </p>
                    <IntentSentence
                      step={step}
                      onFieldClick={field => {
                        const raw = step.inputs[field]
                        if (raw && isBoundValue(raw)) {
                          setEditingLiteral(null)
                          setBindingField(bindingField === field ? null : field)
                        } else {
                          setBindingField(null)
                          setEditingLiteral(field)
                          requestAnimationFrame(() => {
                            document
                              .querySelector<HTMLElement>(`[data-inspector-field="${field}"]`)
                              ?.focus()
                          })
                        }
                      }}
                    />
                  </div>
                  {Object.entries(step.inputs)
                    .filter(([, v]) => !isBoundValue(v))
                    .sort(([a], [b]) => Number(b === 'prompt') - Number(a === 'prompt'))
                    .map(([field, value]) => {
                      const long = field === 'prompt' || field === 'body' || value.length > 48
                      const touched = agentTouch?.stepId === step.id && agentTouch.fields.includes(field)
                      return (
                        <InspectorLiteralField
                          key={field}
                          field={field}
                          value={value}
                          long={long}
                          washing={Boolean(touched && agentTouch?.washing)}
                          marked={Boolean(touched)}
                          onCommit={(next, source) => onPatchStep?.(step.id, { [field]: next }, source) ?? 'saved'}
                          onAsk={field === 'prompt' && onAskStep ? draft => onAskStep(step, draft) : undefined}
                          onFocusField={() => {
                            setBindingField(null)
                            setEditingLiteral(field)
                          }}
                        />
                      )
                    })}
                  {step.when && (
                    <div>
                      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Condition
                      </p>
                      <StudioStatusPill className="border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400">
                        if {humanLabel(step.when.split(' ')[0] ?? '')}
                      </StudioStatusPill>
                    </div>
                  )}
                  <div>
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Outputs
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {step.emits.map(e => (
                        <BindingChip key={e} token={e} variant="emit" title={e} />
                      ))}
                    </div>
                  </div>
                  {bindingField && (
                    <div
                      className="overflow-hidden rounded-lg border border-primary/30"
                      data-testid={testIds.flowsStudioPrototype.bindingPicker}
                    >
                      <div className="bg-primary/5 px-2 py-1 text-xs font-semibold text-primary">
                        Bind "{humanLabel(bindingField)}" from last run
                      </div>
                      <div className="max-h-44 divide-y divide-border overflow-y-auto">
                        {Object.entries(payload).map(([token, sample]) => (
                          <button
                            key={token}
                            type="button"
                            className="w-full px-2 py-1.5 text-left hover:bg-muted/40"
                            onClick={() => setBindingField(null)}
                          >
                            <BindingChip token={token} />
                            <p className="mt-0.5 truncate text-xs text-muted-foreground">{sample}</p>
                          </button>
                        ))}
                        {upstreamEmits.map(token => (
                          <button
                            key={token}
                            type="button"
                            className="w-full px-2 py-1.5 text-left hover:bg-muted/40"
                            onClick={() => setBindingField(null)}
                          >
                            <BindingChip token={token} variant="emit" />
                            <p className="mt-0.5 text-xs text-muted-foreground">Earlier step output</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              </div>
            </div>
            </SecondaryPanel>
          </>
        )}
      </PanelGroup>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Gallery
// ---------------------------------------------------------------------------

function recipeFlowFromInstall(
  recipe: Recipe,
  ws: Workspace,
  startsAs: 'draft' | 'shadow',
): StudioFlow {
  const slug = recipe.id.replace(/^rec-/, '')
  return {
    id: `installed-${recipe.id}-${ws.id}`,
    name: recipe.name,
    slug,
    path: flowsPathFor(ws),
    trigger: {
      registryId: 'schedule.cron',
      label: recipe.triggerLabel,
      detail: 'Installed from Gallery',
      plainSummary: `Installed into ${ws.name}`,
    },
    steps: [
      {
        id: 's1',
        registryId: 'ai.session',
        label: recipe.stepLabels[0] ?? 'First step',
        intent: 'Run the recipe steps (stub from Gallery install).',
        inputs: { prompt: recipe.oneLiner },
        emits: ['steps.ai_session.text'],
      },
      {
        id: 's2',
        registryId: 'inbox.post_thread',
        label: recipe.stepLabels[recipe.stepLabels.length - 1] ?? 'Post to inbox',
        intent: 'Post to inbox titled {summary}.',
        inputs: { summary: recipe.name, body: '{{steps.ai_session.text}}' },
        emits: ['steps.inbox_post_thread.thread_id'],
      },
    ],
    mode: startsAs,
    health: 'ok',
    lastRun: 'Never',
    nextRun: '-',
    costWeekUsd: 0,
    installedFromGallery: true,
  }
}

function GallerySurface({
  installed,
  onInstall,
}: {
  installed: Record<string, { mode: 'draft' | 'shadow'; workspaceId: string; path: string }>
  onInstall: (recipe: Recipe, startsAs: 'draft' | 'shadow', workspace: Workspace) => void
}) {
  const { mode: pageWidthMode } = useStudioPageWidth()
  // Match real sidebar: Hop + Personal active (see workspace PIP selector)
  const [activeIds, setActiveIds] = useState<string[]>(['ws-hop', 'ws-personal'])
  const [targetId, setTargetId] = useState('ws-hop')
  const [pickerOpen, setPickerOpen] = useState(false)

  const activeWorkspaces = useMemo(
    () => PROTO_WORKSPACES.filter(w => activeIds.includes(w.id)),
    [activeIds],
  )

  const effectiveTarget =
    activeWorkspaces.find(w => w.id === targetId) ?? activeWorkspaces[0] ?? PROTO_WORKSPACES[1]

  const targetCaps = capsFor(effectiveTarget.id)

  const visibleRecipes = useMemo(() => {
    return RECIPES.filter(recipe => {
      const gate = GROUP_EXTENSION_GATE[recipe.group]
      if (gate && !targetCaps.includes(gate)) return false
      return true
    })
  }, [targetCaps])

  const groups = [...new Set(visibleRecipes.map(r => r.group))]

  const toggleActive = (id: string) => {
    setActiveIds(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
      if (next.length === 0) return prev
      if (!next.includes(targetId)) setTargetId(next[0])
      return next
    })
  }

  const selectOnly = (id: string) => {
    setActiveIds([id])
    setTargetId(id)
    setPickerOpen(false)
  }

  const pipIds = activeIds.slice(0, PIP_CAP)
  const pipOverflow = Math.max(0, activeIds.length - PIP_CAP)

  return (
    <TooltipProvider delayDuration={400}>
      <div className="flex min-h-0 flex-1 flex-col" data-testid={testIds.flowsStudioPrototype.gallery}>
        <div className={cn('mx-auto w-full flex-1 space-y-5 overflow-y-auto p-4', studioPageWidthClass(pageWidthMode, 'max-w-4xl'))}>
        <SettingsSection
          title="Install target"
          description={
            <>
              {effectiveTarget.name}
              {' -> '}
              {flowsPathFor(effectiveTarget)}/&lt;slug&gt;.md
              <span className="mt-1 block">
                Extensions here:{' '}
                {targetCaps.length === 0
                  ? 'none'
                  : targetCaps.filter(c => ['basecamp', 'zoom', 'llm', 'webhook'].includes(c)).join(', ')}
              </span>
            </>
          }
          actions={
          <DropdownMenu open={pickerOpen} onOpenChange={setPickerOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Workspace scope for Gallery"
                className={cn(
                  'group/pips flex h-8 items-center gap-1.5 rounded-full border border-border bg-background px-2.5',
                  'text-muted-foreground hover:text-foreground',
                )}
              >
                <WorkspacePipGlyph
                  workspaces={PROTO_WORKSPACES}
                  visibleIds={pipIds}
                  overflow={pipOverflow}
                  highlightedIds={[effectiveTarget.id]}
                  layout="inline"
                  interactive
                />
                <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
              className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
              onCloseAutoFocus={e => e.preventDefault()}
            >
              <div className="mb-1.5 px-1.5">
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Active workspaces
                </p>
                <p className="text-xs text-muted-foreground/60">
                  Click to add or remove; Only for one workspace. Highlighted row is the install target.
                </p>
              </div>
              <div className="max-h-64 space-y-0.5 overflow-y-auto">
                {PROTO_WORKSPACES.map(ws => (
                  <WorkspaceToggleChip
                    key={ws.id}
                    workspace={ws}
                    active={activeIds.includes(ws.id)}
                    onToggle={() => {
                      if (activeIds.includes(ws.id) && activeIds.length === 1) return
                      if (activeIds.includes(ws.id)) {
                        toggleActive(ws.id)
                      } else {
                        toggleActive(ws.id)
                        setTargetId(ws.id)
                      }
                    }}
                    onOnly={() => selectOnly(ws.id)}
                  >
                    {activeIds.includes(ws.id) ? (
                      <button
                        type="button"
                        className={cn(
                          'shrink-0 rounded px-1.5 py-1 text-xs font-medium',
                          ws.id === effectiveTarget.id
                            ? 'bg-accent text-accent-foreground'
                            : 'text-muted-foreground hover:bg-accent/50',
                        )}
                        onClick={e => {
                          e.stopPropagation()
                          setTargetId(ws.id)
                          if (!activeIds.includes(ws.id)) {
                            setActiveIds(prev => [...prev, ws.id])
                          }
                        }}
                        title="Install into this workspace"
                      >
                        {ws.id === effectiveTarget.id ? 'Target' : 'Use'}
                      </button>
                    ) : null}
                  </WorkspaceToggleChip>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          }
          data-testid={testIds.flowsStudioPrototype.galleryWorkspaceTarget}
        />

        {groups.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No recipes match extensions on {effectiveTarget.name}. Switch install target (e.g. Hop Studios
            for Basecamp).
          </p>
        ) : null}

        {groups.map(group => (
          <div key={group}>
            <div className="mb-2 flex items-center gap-2">
              {groupIcon(group)}
              <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{group}</h3>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {visibleRecipes
                .filter(r => r.group === group)
                .map(recipe => {
                  const reqs = recipe.requires.map(r => ({
                    ...r,
                    met: targetCaps.includes(r.cap),
                  }))
                  const unmet = reqs.find(r => !r.met)
                  const startsAs: 'draft' | 'shadow' = unmet ? 'draft' : 'shadow'
                  const installKey = `${recipe.id}:${effectiveTarget.id}`
                  const installedRec = installed[installKey]

                  return (
                    <div key={recipe.id} className="flex flex-col rounded-lg border border-border bg-card p-4">
                      <p className="text-sm font-medium">{recipe.name}</p>
                      <p className="mt-1 flex-1 text-sm leading-relaxed text-muted-foreground">{recipe.oneLiner}</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span className="flex items-center gap-1 rounded border border-border bg-muted px-1.5 py-0.5 text-xs">
                          <Zap className="h-3.5 w-3.5" />
                          {recipe.triggerLabel}
                        </span>
                        {recipe.stepLabels.map(s => (
                          <span
                            key={s}
                            className="rounded border border-border bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-muted-foreground">
                          On <span className="font-medium text-foreground">{effectiveTarget.name}</span>:
                        </p>
                        {reqs.map(req => (
                          <div key={req.label} className="flex items-center gap-1.5 text-xs">
                            {req.met ? (
                              <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                            ) : (
                              <AlertTriangle className="h-4 w-4 text-yellow-500" />
                            )}
                            <span className={req.met ? 'text-muted-foreground' : 'text-yellow-600'}>
                              {req.label}
                            </span>
                          </div>
                        ))}
                      </div>
                      <Button
                        size="sm"
                        variant={installedRec ? 'ghost' : 'outline'}
                        disabled={!!installedRec}
                        className="mt-3 h-8 text-sm"
                        data-testid={testIds.flowsStudioPrototype.galleryInstallButton}
                        onClick={() => onInstall(recipe, startsAs, effectiveTarget)}
                        title={unmet ? `Needs ${unmet.label} on ${effectiveTarget.name}` : undefined}
                      >
                        {installedRec ? (
                          <>
                            <Check className="mr-1 h-4 w-4" /> Added to {effectiveTarget.name} (
                            {installedRec.mode === 'draft' ? 'Draft' : 'Shadow'})
                          </>
                        ) : unmet ? (
                          <>Add to {effectiveTarget.name} anyway - Draft</>
                        ) : (
                          <>
                            <Plus className="mr-1 h-4 w-4" /> Add to {effectiveTarget.name} - Shadow
                          </>
                        )}
                      </Button>
                      {installedRec ? (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {installedRec.path}/{recipe.id.replace(/^rec-/, '')}.md
                        </p>
                      ) : unmet ? (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Needs {unmet.label} on {effectiveTarget.name} for Live.
                        </p>
                      ) : null}
                    </div>
                  )
                })}
            </div>
          </div>
        ))}

        <ConceptNote>
          Install target uses the same PIP selector as the sidebar (Active workspaces / Only). GGSC is a
          client path under Hop, not a workspace. Basecamp recipes disappear when Personal is the target.
          Continuity chrome matches Pulse / Definition:{' '}
          <span className="font-mono text-[10px]">{CONTINUITY_GUIDE}</span>
        </ConceptNote>
        </div>
      </div>
    </TooltipProvider>
  )
}

// ---------------------------------------------------------------------------
// Sources (ex-Triggers)
// ---------------------------------------------------------------------------

function SourcesSurface() {
  const { mode: pageWidthMode } = useStudioPageWidth()
  const [expandedId, setExpandedId] = useState('webhook.received')
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'available' | 'coming' | 'all'>('available')
  const [groupBy, setGroupBy] = useState<'source' | 'status' | 'used'>('source')

  const filtered = TRIGGER_CATALOG.filter(t => {
    const q = query.toLowerCase()
    const matchesQ =
      !q ||
      t.label.toLowerCase().includes(q) ||
      t.description.toLowerCase().includes(q) ||
      t.group.toLowerCase().includes(q)
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'available' && t.status !== 'planned') ||
      (statusFilter === 'coming' && t.status === 'planned')
    return matchesQ && matchesStatus
  })

  const sorted = [...filtered].sort((a, b) => {
    if (groupBy === 'used') return b.usedBy.length - a.usedBy.length
    if (groupBy === 'status') return a.status.localeCompare(b.status)
    return a.group.localeCompare(b.group) || a.label.localeCompare(b.label)
  })

  const groups =
    groupBy === 'source'
      ? [...new Set(sorted.map(t => t.group))]
      : groupBy === 'status'
        ? [...new Set(sorted.map(t => t.status))]
        : ['Most used']

  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      data-testid={testIds.flowsStudioPrototype.sourcesCatalog}
    >
      <div className={cn('mx-auto w-full flex-1 space-y-4 overflow-y-auto p-4', studioPageWidthClass(pageWidthMode, 'max-w-3xl'))}>
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/10 p-2">
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search sources..."
          className="h-8 max-w-xs text-xs"
        />
        <Segmented
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { id: 'available', label: 'Available' },
            { id: 'coming', label: 'Coming' },
            { id: 'all', label: 'All' },
          ]}
        />
        <Segmented
          value={groupBy}
          onChange={setGroupBy}
          options={[
            { id: 'source', label: 'By source' },
            { id: 'status', label: 'By status' },
            { id: 'used', label: 'Most used' },
          ]}
        />
      </div>

      {/* Webhook star card - Coming source; show on Coming / All, hide on Available-only */}
      {statusFilter !== 'available' && (
        <div
          className="overflow-hidden rounded-lg border border-primary/30 bg-primary/5"
          data-testid={testIds.flowsStudioPrototype.webhookPanel}
        >
          <div className="flex items-center gap-2 border-b border-primary/20 px-3 py-2">
            <Webhook className="h-4 w-4 text-primary" />
            <span className="flex-1 text-sm font-semibold">Webhooks</span>
            <StudioStatusPill className={BADGE_DRAFT_CLASS}>Coming</StudioStatusPill>
          </div>
          <div className="space-y-2 p-3">
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded border border-border bg-muted/40 px-2 py-1.5 font-mono text-[10px]">
                https://api.flownotes.space/api/hooks/fl_9a2c...e71b
              </code>
              <Button size="sm" variant="outline" className="h-7 shrink-0 text-xs">
                <Copy className="mr-1 h-3 w-3" /> Copy
              </Button>
            </div>
            <div className="divide-y divide-border rounded border border-border">
              <div className="flex items-center gap-2 px-2 py-1.5">
                <span className="text-xs text-muted-foreground">12m ago</span>
                <span className="flex-1 truncate text-xs">Uptime alert - client site down</span>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs">
                  Create flow from payload
                </Button>
              </div>
              <div className="flex items-center gap-2 px-2 py-1.5">
                <span className="text-xs text-muted-foreground">3h ago</span>
                <span className="flex-1 truncate text-xs">DeployBot - ggsc production</span>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs">
                  Create flow from payload
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {groups.map(group => {
        const items =
          groupBy === 'source'
            ? sorted.filter(t => t.group === group)
            : groupBy === 'status'
              ? sorted.filter(t => t.status === group)
              : sorted
        if (!items.length) return null
        return (
          <div key={group}>
            <div className="mb-1.5 flex items-center gap-2">
              {groupBy === 'source' && groupIcon(group)}
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {groupBy === 'status'
                  ? group === 'live'
                    ? 'Available'
                    : group === 'partial'
                      ? 'Partial'
                      : 'Coming'
                  : group}
              </h3>
            </div>
            <div className="space-y-2">
              {items.map(t => {
                const open = expandedId === t.registryId
                return (
                  <div key={t.registryId} className="overflow-hidden rounded-lg border border-border">
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/30"
                      onClick={() => setExpandedId(open ? '' : t.registryId)}
                    >
                      {open ? (
                        <ChevronDown className="h-3 w-3 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-3 w-3 text-muted-foreground" />
                      )}
                      <span className="flex-1 text-xs font-medium">{t.label}</span>
                      {t.usedBy.length > 0 && (
                        <span className="text-xs text-muted-foreground">Used by {t.usedBy.length}</span>
                      )}
                      {sourceStatusBadge(t.status)}
                    </button>
                    {open && (
                      <div className="space-y-2 bg-muted/10 px-3 pb-3 pt-1">
                        <p className="text-xs text-muted-foreground">{t.description}</p>
                        {t.sample && (
                          <p className="rounded border border-border bg-background/50 px-2 py-1.5 text-xs">
                            <span className="text-muted-foreground">Sample: </span>
                            {t.sample}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-1">
                          {t.payloadFields.map(f => (
                            <BindingChip key={f.path} token={`trigger.${f.path}`} title={`trigger.${f.path}`} />
                          ))}
                        </div>
                        {t.usedBy.length > 0 ? (
                          <p className="text-xs text-muted-foreground">Used by: {t.usedBy.join(', ')}</p>
                        ) : (
                          t.status === 'live' && (
                            <p className="text-xs text-muted-foreground">
                              No flows yet - opportunity to automate.
                            </p>
                          )
                        )}
                        <Button size="sm" variant="outline" className="h-7 text-xs">
                          <Plus className="mr-1 h-3 w-3" /> Create flow from this
                        </Button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}

      {/* Keep legacy test id for older references */}
      <div className="hidden" data-testid={testIds.flowsStudioPrototype.triggerCatalog} />
      <ConceptNote>
        Sources share Flows breadcrumb chrome with Pulse / Gallery. Guide:{' '}
        <span className="font-mono text-[10px]">{CONTINUITY_GUIDE}</span>
      </ConceptNote>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Composer
// ---------------------------------------------------------------------------

function ComposerSurface({
  onApprove,
  onRefine,
  onReject,
  proposalSteps,
  proposalYaml,
  proposalFromRefine,
  onResetProposal,
}: {
  onApprove: () => void
  onRefine: (prompt: string) => void
  onReject: () => void
  proposalSteps: StudioStep[]
  proposalYaml: string
  proposalFromRefine: boolean
  onResetProposal: () => void
}) {
  const { mode: pageWidthMode } = useStudioPageWidth()
  const [prompt, setPrompt] = useState('')
  const [generated, setGenerated] = useState(false)
  const [previewMode, setPreviewMode] = useState<'pipeline' | 'source'>('pipeline')
  const example =
    'When a new pilot feedback doc is created, classify its severity and post a triage summary to the Hop inbox.'

  useEffect(() => {
    if (proposalFromRefine) setPreviewMode('pipeline')
  }, [proposalFromRefine, proposalSteps])

  const reject = () => {
    setGenerated(false)
    onResetProposal()
    onReject()
  }

  return (
    <div
      className={cn('mx-auto w-full flex-1 space-y-4 overflow-y-auto p-4', studioPageWidthClass(pageWidthMode, 'max-w-[760px]'))}
      data-testid={testIds.flowsStudioPrototype.composer}
    >
      <div className="py-2 text-center">
        <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Wand2 className="h-5 w-5" />
        </div>
        <p className="text-xs text-muted-foreground">
          The agent authors it; you verify the pipeline. Approve starts in Shadow so it can practice on
          real events before side effects go live.
        </p>
      </div>

      <Textarea
        placeholder={example}
        value={prompt}
        onChange={e => setPrompt(e.target.value)}
        className="min-h-20 text-xs"
      />
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs text-muted-foreground"
          onClick={() => setPrompt(example)}
        >
          Use example prompt
        </Button>
        <Button size="sm" className="ml-auto h-7 text-xs" disabled={!prompt} onClick={() => {
          onResetProposal()
          setGenerated(true)
        }}>
          <Sparkles className="mr-1 h-3 w-3" /> Generate flow
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Generate calls <span className="font-mono">/create-flow</span> with this as the first message.
        Iterate with Refine - not a second chat in this panel.
      </p>

      {generated && (
        <div className="overflow-hidden rounded-lg border border-border">
          <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-3 py-2">
            <span className="flex-1 text-xs font-semibold">Proposed: Pilot feedback triage</span>
            {proposalFromRefine && (
              <span className="text-xs text-muted-foreground">Updated from Refine</span>
            )}
            <StudioStatusPill className={BADGE_SCHEMA_CLASS}>Schema valid</StudioStatusPill>
            <StudioStatusPill className={BADGE_SHADOW_CLASS}>Starts in Shadow</StudioStatusPill>
            <Segmented
              value={previewMode}
              onChange={setPreviewMode}
              options={[
                { id: 'pipeline', label: 'Preview' },
                { id: 'source', label: 'Source' },
              ]}
            />
          </div>
          {previewMode === 'pipeline' ? (
            <div className="p-4">
              <FlowPipeline
                flow={{
                  name: 'Pilot feedback triage',
                  trigger: {
                    registryId: 'document.created',
                    label: 'Document created',
                    detail: 'pilot-feedback folder',
                    plainSummary: 'Runs when a doc is created in pilot-feedback/',
                  },
                  steps: proposalSteps,
                }}
                mode="definition"
                density="mini"
                payload={CAPTURED_PAYLOADS['document.created']}
              />
            </div>
          ) : (
            <pre className="max-h-72 overflow-auto whitespace-pre-wrap bg-muted/40 p-3 font-mono text-[10px]">
              {proposalYaml.split('\n').map((line, i) => (
                <div key={i} className="flex">
                  <span className="w-4 shrink-0 select-none text-green-500">+</span>
                  <span>{line}</span>
                </div>
              ))}
            </pre>
          )}
          <div className="flex items-center gap-2 border-t border-border px-3 py-2">
            <p className="flex-1 text-xs text-muted-foreground">
              Document created is Coming - the flow saves in Shadow with a wiring notice until the source
              ships.
            </p>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={reject}>
              Reject
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              data-testid={testIds.flowsStudioPrototype.composerRefine}
              onClick={() => onRefine(prompt)}
            >
              <Bot className="mr-1 h-3 w-3" /> Refine
            </Button>
            <Button size="sm" className="h-7 text-xs" onClick={onApprove}>
              <Check className="mr-1 h-3 w-3" /> Approve - start in Shadow
            </Button>
          </div>
        </div>
      )}

      <ConceptNote>
        Mini-pipeline is the approve theater. Generate and Refine both use{' '}
        <span className="font-mono">/create-flow</span>. Refine opens the agent panel; the result
        lands back on this strip. YAML stays behind Source.
      </ConceptNote>
    </div>
  )
}

type StudioAgentSession = {
  key: string
  prompt: string
  skill: string
  contextLabel: string
  intro: string
  appliedReply: string
  placeholder?: string
  flowId?: string
  stepId?: string
}

type FlowSessionFile = {
  id: string
  title: string
  kind: string
  meta: string
  detail: string
}

const FLOW_SESSION_FILES: FlowSessionFile[] = [
  {
    id: 'memory',
    title: 'Session memory',
    kind: 'session_memory',
    meta: '1 fact',
    detail: 'Durable facts for this flow. A run can propose an edit.',
  },
  {
    id: 'pointer',
    title: 'Pointer: searchDocuments',
    kind: 'pointer',
    meta: 'pointer',
    detail: 'A tool result kept on this thread.',
  },
  {
    id: 'soul',
    title: 'Flow soul',
    kind: 'soul',
    meta: 'identity',
    detail: 'This flow\'s soul. A run can propose an edit. Approve applies it.',
  },
  {
    id: 'transcript',
    title: 'Transcript',
    kind: 'note',
    meta: '12 turns',
    detail: 'This thread. Clear it to start over. Soul and memory stay.',
  },
]

/** Chat-pane session files, on the flow's Refine agent pane. Static prototype. */
function FlowSessionFiles() {
  const [expanded, setExpanded] = useState(true)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [transcriptCleared, setTranscriptCleared] = useState(false)
  const selected = FLOW_SESSION_FILES.find(file => file.id === selectedId) ?? null

  return (
    <div
      className="border-b border-border bg-muted/40 px-3 pb-2 pt-1"
      data-testid={testIds.flowsStudioPrototype.flowSessionFiles}
    >
      <button
        type="button"
        className="mb-1 flex h-6 w-full items-center gap-2 text-left text-[11px] font-medium text-muted-foreground hover:text-foreground"
        data-testid={testIds.flowsStudioPrototype.flowSessionFilesToggle}
        aria-expanded={expanded}
        onClick={() => setExpanded(prev => !prev)}
      >
        <span>
          Session files
          <span className="ml-1 text-muted-foreground/70">{FLOW_SESSION_FILES.length}</span>
        </span>
        <ChevronDown
          className={cn(
            'ml-auto h-3.5 w-3.5 shrink-0 transition-transform',
            expanded && 'rotate-180',
          )}
        />
      </button>
      {expanded ? (
        <ul className="space-y-0.5">
          {FLOW_SESSION_FILES.map(file => {
            const cleared = file.id === 'transcript' && transcriptCleared
            const meta = cleared ? 'new' : file.meta
            return (
              <li
                key={file.id}
                className="flex items-center gap-1 rounded-md px-1 py-0.5 hover:bg-muted/70"
              >
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-2 text-left"
                  data-testid={testIds.flowsStudioPrototype.flowSessionFile(file.id)}
                  aria-pressed={selectedId === file.id}
                  onClick={() => setSelectedId(prev => (prev === file.id ? null : file.id))}
                >
                  {file.id === 'transcript' ? (
                    <MessageSquare className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  ) : (
                    getDocumentDisplayIcon(
                      { kind: file.kind, filePath: file.title },
                      { className: 'h-3.5 w-3.5 shrink-0' },
                    )
                  )}
                  <span className="min-w-0 flex-1 truncate text-[13px] text-foreground/90">
                    {file.title}
                  </span>
                  <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground/70">
                    {meta}
                  </span>
                </button>
                {file.id === 'transcript' ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 shrink-0 px-1.5 text-[11px] text-muted-foreground hover:text-foreground"
                    data-testid={testIds.flowsStudioPrototype.flowSessionTranscriptClear}
                    onClick={() => {
                      setTranscriptCleared(prev => !prev)
                      setSelectedId('transcript')
                    }}
                  >
                    {transcriptCleared ? 'Restore' : 'Clear'}
                  </Button>
                ) : null}
              </li>
            )
          })}
        </ul>
      ) : null}
      {expanded && selected ? (
        <p className="px-1 pt-1 text-[11px] leading-snug text-muted-foreground">
          {selected.id === 'transcript' && transcriptCleared
            ? 'New transcript. Soul and memory stay.'
            : selected.detail}
        </p>
      ) : null}
    </div>
  )
}

function ComposerAgentPanel({
  prompt,
  onClose,
  onApplied,
  skill = '/create-flow',
  contextLabel = 'Proposed: Pilot feedback triage',
  intro = 'Proposal is on the Composer strip (Preview / Source). Tell me what to change and I will re-run /create-flow. Result lands back there for Reject / Approve.',
  appliedReply = 'Re-ran /create-flow. Added "Shape filing suggestions" before the inbox step. Preview and Source on the strip are updated - Approve still starts in Shadow.',
  placeholder = 'Message...',
}: {
  prompt: string
  onClose: () => void
  onApplied: () => void
  skill?: string
  contextLabel?: string
  intro?: string
  appliedReply?: string
  placeholder?: string
}) {
  const [draft, setDraft] = useState('')
  const [contextOn, setContextOn] = useState(true)
  const [width, setWidth] = useState(420)
  const widthRef = useRef(width)
  widthRef.current = width
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const [turns, setTurns] = useState<{ role: 'user' | 'assistant'; text: string }[]>(() =>
    prompt.trim()
      ? [
          { role: 'user', text: prompt },
          { role: 'assistant', text: intro },
        ]
      : [{ role: 'assistant', text: intro }],
  )

  useEffect(() => {
    composerRef.current?.focus()
  }, [])

  const send = () => {
    const text = draft.trim()
    if (!text) return
    setTurns(prev => [...prev, { role: 'user', text }, { role: 'assistant', text: appliedReply }])
    setDraft('')
    onApplied()
  }

  const onGripDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    const startX = event.clientX
    const startW = widthRef.current
    const move = (ev: PointerEvent) => {
      setWidth(Math.min(560, Math.max(320, startW - (ev.clientX - startX))))
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  return (
    <aside
      className="relative flex h-full shrink-0 flex-col border-l border-border bg-card"
      style={{ width }}
      data-testid={testIds.flowsStudioPrototype.composerAgentPanel}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize agent panel"
        className="absolute inset-y-0 left-0 z-10 w-1.5 cursor-col-resize hover:bg-primary/30"
        onPointerDown={onGripDown}
      />
      <div className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-3">
        <Sparkles className="h-4 w-4 text-primary" />
        <p className="min-w-0 flex-1 truncate text-sm font-medium">AI Assistant</p>
        <span className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
          {skill}
        </span>
        <button
          type="button"
          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={onClose}
          aria-label="Close agent panel"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <FlowSessionFiles />
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {turns.map((turn, index) => (
          <div
            key={index}
            className={cn('flex', turn.role === 'user' ? 'justify-end' : 'justify-start')}
          >
            <div
              className={cn(
                'max-w-[90%] rounded-2xl px-3 py-2 text-sm leading-relaxed',
                turn.role === 'user'
                  ? 'bg-primary/15 text-foreground'
                  : 'text-foreground/90',
              )}
            >
              {turn.text}
            </div>
          </div>
        ))}
      </div>
      <div className="shrink-0 space-y-2 border-t border-border p-3">
        {contextOn && (
          <span className="inline-flex max-w-full items-center gap-1 rounded-md border border-green-500/20 bg-green-500/10 py-0.5 pl-1.5 pr-1 text-[11px] text-green-700 dark:text-green-400">
            <span className="h-2 w-2 shrink-0 rounded-full bg-green-500" />
            <Zap className="h-3 w-3 shrink-0" />
            <span className="min-w-0 truncate">{contextLabel}</span>
            <button
              type="button"
              className="rounded-sm p-0.5 hover:bg-green-500/20"
              aria-label="Remove flow from context"
              onClick={() => setContextOn(false)}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        )}
        <Textarea
          ref={composerRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder={placeholder}
          className="min-h-16 text-sm"
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              send()
            }
          }}
        />
        <div className="flex justify-end">
          <Button size="sm" className="h-8 px-3 text-sm" disabled={!draft.trim()} onClick={send}>
            Send
          </Button>
        </div>
      </div>
    </aside>
  )
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

const PILOT_FEEDBACK_FLOW: StudioFlow = {
  id: 'pilot-feedback-triage',
  name: 'Pilot feedback triage',
  slug: 'pilot-feedback-triage',
  path: 'Hop Studios/flows',
  trigger: {
    registryId: 'document.created',
    label: 'Document created',
    detail: 'pilot-feedback folder',
    plainSummary: 'Runs when a doc is created in pilot-feedback/',
  },
  steps: COMPOSER_PREVIEW_STEPS,
  mode: 'shadow',
  health: 'ok',
  lastRun: 'Never',
  nextRun: 'Event-driven',
  costWeekUsd: 0,
}

type StudioOpenTab = { id: string; pinned: boolean }

const STUDIO_TAB_SESSION_KEY = 'flows-studio-proto-tabs'
const STUDIO_INDEX_IDS = new Set(['pulse', 'gallery', 'sources', 'composer'])

function isStudioIndexTab(id: string) {
  return STUDIO_INDEX_IDS.has(id)
}

function flowIdFromStudioTab(id: string): string | null {
  return id.startsWith('flow:') ? id.slice('flow:'.length) : null
}

function studioFlowTabId(flowId: string) {
  return `flow:${flowId}`
}

function studioTabSurface(id: string): Surface {
  if (folderPathFromStudioTab(id)) return 'folder'
  return isStudioIndexTab(id) ? (id as Surface) : 'flow'
}

function readStudioTabs(): { tabs: StudioOpenTab[]; activeId: string } {
  const fallback = { tabs: [{ id: 'pulse', pinned: false }], activeId: 'pulse' }
  if (typeof sessionStorage === 'undefined') return fallback
  try {
    const raw = sessionStorage.getItem(STUDIO_TAB_SESSION_KEY)
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as { tabs?: StudioOpenTab[]; activeId?: string }
    const knownFlows = new Set(INITIAL_FLOWS.map(flow => studioFlowTabId(flow.id)))
    const tabs = (parsed.tabs ?? []).filter(
      tab =>
        tab &&
        (isStudioIndexTab(tab.id) || knownFlows.has(tab.id) || folderPathFromStudioTab(tab.id) != null),
    )
    if (tabs.length === 0) return fallback
    const activeId = tabs.some(tab => tab.id === parsed.activeId) ? parsed.activeId! : tabs[0].id
    return { tabs, activeId }
  } catch {
    return fallback
  }
}

function StudioTabBar({
  tabs,
  activeId,
  onActivate,
  onClose,
  onTogglePin,
  onNew,
}: {
  tabs: { id: string; label: string; pinned: boolean; icon?: ReactNode }[]
  activeId: string
  onActivate: (id: string) => void
  onClose: (id: string) => void
  onTogglePin: (id: string) => void
  onNew: () => void
}) {
  const [menu, setMenu] = useState<{ id: string; x: number; y: number } | null>(null)
  const menuTab = tabs.find(tab => tab.id === menu?.id)

  return (
    <div
      className={cn(
        'document-tabs-bar document-tabs--workspace-focus relative flex w-full shrink-0 items-end overflow-hidden border-b border-border pl-1 pr-2',
        SHELL_COLUMN_HEADER_CLASS,
      )}
      style={wsNavSectionStyle()}
      data-testid={testIds.flowsStudioPrototype.tabRow}
    >
      <div className="tab-group tab-group--has-active flex h-full min-w-0 flex-1 items-end" style={wsNavSectionStyle()}>
        <div className="tab-group__strip flex min-w-0 items-end gap-0.5 overflow-x-auto">
          {tabs.map(tab => {
            const isActive = tab.id === activeId
            return (
              <div key={tab.id} className="tab-group__tab-wrap relative">
                <div
                  className={cn(
                    'tab-group__tab group flex max-w-[180px] items-center gap-1 px-2 py-1.5 min-w-0 flex-shrink-0 relative rounded-t-lg text-xs',
                    isActive && 'tab-group__tab--active text-[hsl(var(--tab-active-text))]',
                    !isActive && 'text-[hsl(var(--tab-inactive-text))]',
                  )}
                  data-testid={testIds.flowsStudioPrototype.tab(tab.id)}
                  onContextMenu={event => {
                    event.preventDefault()
                    setMenu({ id: tab.id, x: event.clientX, y: event.clientY })
                  }}
                >
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                    title={tab.pinned ? tab.label : `${tab.label} (Double-click to pin)`}
                    onClick={() => onActivate(tab.id)}
                    onDoubleClick={event => {
                      event.preventDefault()
                      event.stopPropagation()
                      onTogglePin(tab.id)
                    }}
                  >
                    {tab.pinned ? (
                      <Pin className="tab-group__pin-icon h-3 w-3 shrink-0" aria-label="Pinned tab" />
                    ) : null}
                    {tab.icon ? <span className="flex shrink-0 items-center">{tab.icon}</span> : null}
                    <span className={cn('truncate', !tab.pinned && 'italic text-muted-foreground/80')}>
                      {tab.label}
                    </span>
                  </button>
                  {tabs.length > 1 ? (
                    <button
                      type="button"
                      data-close-tab
                      className="relative z-20 rounded p-0.5 opacity-0 transition-opacity group-hover:opacity-70 hover:!opacity-100"
                      title="Close tab"
                      onClick={event => {
                        event.stopPropagation()
                        onClose(tab.id)
                      }}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      </div>
      <button
        type="button"
        className="mb-0.5 flex h-7 w-7 items-center justify-center rounded-md text-[hsl(var(--tab-inactive-text))] transition-colors duration-200 hover:bg-muted/70 hover:text-foreground"
        title="New tab"
        onClick={onNew}
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
      {menu && menuTab ? (
        <EntityContextMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          entries={[
            {
              label: menuTab.pinned ? 'Unpin' : 'Pin',
              icon: Pin,
              onSelect: () => {
                onTogglePin(menuTab.id)
                setMenu(null)
              },
            },
            {
              label: 'Close',
              icon: X,
              onSelect: () => {
                onClose(menuTab.id)
                setMenu(null)
              },
            },
          ]}
        />
      ) : null}
    </div>
  )
}

export function FlowsStudioTest() {
  const { theme, setTheme } = useTheme()
  const [pageWidthMode, setPageWidthMode] = useState<StudioPageWidth>('default')
  const [studioSession] = useState(readStudioTabs)
  const [openTabs, setOpenTabs] = useState<StudioOpenTab[]>(studioSession.tabs)
  const [activeTabId, setActiveTabId] = useState(studioSession.activeId)
  const [flows, setFlows] = useState(INITIAL_FLOWS)
  const [activeFlowId, setActiveFlowId] = useState(
    () => flowIdFromStudioTab(studioSession.activeId) ?? INITIAL_FLOWS[1].id,
  )
  const [activeStepId, setActiveStepId] = useState('')
  const [installed, setInstalled] = useState<
    Record<string, { mode: 'draft' | 'shadow'; workspaceId: string; path: string }>
  >({})
  const [ownerScope, setOwnerScope] = useState<OwnerScope>('team')
  const [flowDocTab, setFlowDocTab] = useState<FlowDocTab>('overview')
  const [history, setHistory] = useState<{ stack: string[]; index: number }>({
    stack: [studioSession.activeId],
    index: 0,
  })
  const [composerAgentOpen, setComposerAgentOpen] = useState(false)
  const [flowAgent, setFlowAgent] = useState<StudioAgentSession | null>(null)
  const [flowAgentApply, setFlowAgentApply] = useState<{ flowId: string; nonce: number; stepId?: string } | null>(null)
  const [composerRefinePrompt, setComposerRefinePrompt] = useState('')
  const [composerProposal, setComposerProposal] = useState({
    steps: COMPOSER_PREVIEW_STEPS,
    yaml: COMPOSER_EXAMPLE_YAML,
    fromRefine: false,
  })
  const [folderPeek, setFolderPeek] = useState<FolderPeek>({
    selectedId: null,
    selectedKind: null,
    open: true,
  })
  const [treeMenu, setTreeMenu] = useState<{ target: FlowTreeTarget; x: number; y: number } | null>(null)

  const exceptionCount = FEED.filter(
    r => r.status === 'failed' || r.status === 'policy_blocked' || r.status === 'warning',
  ).length

  const surface = studioTabSurface(activeTabId)

  useEffect(() => {
    sessionStorage.setItem(
      STUDIO_TAB_SESSION_KEY,
      JSON.stringify({ tabs: openTabs, activeId: activeTabId }),
    )
  }, [openTabs, activeTabId])

  const remember = (id: string) => {
    setHistory(h => {
      const trimmed = h.stack.slice(0, h.index + 1)
      if (trimmed[trimmed.length - 1] === id) return { stack: trimmed, index: trimmed.length - 1 }
      const stack = [...trimmed, id]
      return { stack, index: stack.length - 1 }
    })
  }

  const focusTab = (id: string) => {
    setActiveTabId(id)
    const flowId = flowIdFromStudioTab(id)
    if (flowId) setActiveFlowId(flowId)
    else if (id !== 'composer') setComposerAgentOpen(false)
  }

  const openTarget = (tabId: string, event?: MouseEvent, forceNew = false) => {
    const current = openTabs.find(tab => tab.id === activeTabId) ?? openTabs[0]
    const existing = openTabs.find(tab => tab.id === tabId)
    if (existing) {
      focusTab(existing.id)
      remember(existing.id)
      return
    }
    const intent =
      forceNew || isModClick(event)
        ? 'new'
        : resolveOpenIntent(event, {
            currentTab: {
              id: current.id,
              isPinned: current.pinned,
              type: isStudioIndexTab(current.id) ? 'home' : 'note',
            },
            activeTabId: current.id,
          })
    if (intent === 'new') {
      setOpenTabs(tabs => [...tabs, { id: tabId, pinned: false }])
    } else {
      setOpenTabs(tabs => tabs.map(tab => (tab.id === current.id ? { id: tabId, pinned: tab.pinned } : tab)))
    }
    focusTab(tabId)
    remember(tabId)
  }

  const goBack = () => {
    if (history.index <= 0) return
    const index = history.index - 1
    const id = history.stack[index] ?? 'pulse'
    setHistory({ ...history, index })
    if (!openTabs.some(tab => tab.id === id)) {
      setOpenTabs(tabs => [...tabs, { id, pinned: false }])
    }
    focusTab(id)
  }

  const goForward = () => {
    if (history.index >= history.stack.length - 1) return
    const index = history.index + 1
    const id = history.stack[index] ?? 'pulse'
    setHistory({ ...history, index })
    if (!openTabs.some(tab => tab.id === id)) {
      setOpenTabs(tabs => [...tabs, { id, pinned: false }])
    }
    focusTab(id)
  }

  const setMode = (id: string, mode: RuntimeMode) => {
    setFlows(prev =>
      prev.map(f => (f.id === id ? { ...f, mode, systemPaused: undefined } : f)),
    )
  }

  const handleFix = (flowId: string, stepRegistryId?: string) => {
    const flow = flows.find(f => f.id === flowId)
    const step = flow?.steps.find(s => s.registryId === stepRegistryId)
    setActiveStepId(step?.id ?? '')
    setFlowDocTab('runs')
    openTarget(studioFlowTabId(flowId))
  }

  const handleOpenFlow = (flowId: string, tab: FlowDocTab = 'overview', event?: MouseEvent) => {
    setActiveStepId('')
    setFlowDocTab(tab)
    setComposerAgentOpen(false)
    openTarget(studioFlowTabId(flowId), event)
  }

  const handleInstall = (
    recipe: Recipe,
    startsAs: 'draft' | 'shadow',
    workspace: Workspace,
  ) => {
    const key = `${recipe.id}:${workspace.id}`
    const flow = recipeFlowFromInstall(recipe, workspace, startsAs)
    setInstalled(prev => ({
      ...prev,
      [key]: { mode: startsAs, workspaceId: workspace.id, path: flowsPathFor(workspace) },
    }))
    setFlows(prev => (prev.some(f => f.id === flow.id) ? prev : [...prev, flow]))
    setActiveStepId('')
    setFlowDocTab('overview')
    openTarget(studioFlowTabId(flow.id), undefined, true)
  }

  const handleComposerApprove = () => {
    const approved: StudioFlow = {
      ...PILOT_FEEDBACK_FLOW,
      steps: composerProposal.steps,
    }
    setFlows(prev => {
      if (prev.some(f => f.id === approved.id)) {
        return prev.map(f => (f.id === approved.id ? approved : f))
      }
      return [...prev, approved]
    })
    setActiveStepId('')
    setFlowDocTab('overview')
    setComposerAgentOpen(false)
    openTarget(studioFlowTabId(PILOT_FEEDBACK_FLOW.id), undefined, true)
  }

  const handleSelectFlowFromTree = (id: string, tab: FlowDocTab = 'overview', event?: MouseEvent) => {
    setActiveStepId('')
    setFlowDocTab(tab)
    setComposerAgentOpen(false)
    openTarget(studioFlowTabId(id), event)
  }

  const handleOpenFolder = (path: string, event?: MouseEvent) => {
    setFolderPeek({ selectedId: null, selectedKind: null, open: true })
    setComposerAgentOpen(false)
    openTarget(studioFolderTabId(path), event)
  }

  const handlePreviewFlow = (id: string) => {
    const flow = flows.find(item => item.id === id)
    if (!flow) return
    setFolderPeek({ selectedId: id, selectedKind: 'flow', open: true })
    setComposerAgentOpen(false)
    openTarget(studioFolderTabId(flow.path))
  }

  const showTreeMenu = (target: FlowTreeTarget, event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setTreeMenu({ target, x: event.clientX, y: event.clientY })
    const folderPath = folderPathFromStudioTab(activeTabId)
    if (!folderPath) return
    if (target.kind === 'flow') {
      const flow = flows.find(item => item.id === target.id)
      if (flow?.path === folderPath) {
        setFolderPeek(prev => ({ ...prev, selectedId: flow.id, selectedKind: 'flow' }))
      }
      return
    }
    if (parentFolderPath(target.path) === folderPath) {
      setFolderPeek(prev => ({ ...prev, selectedId: target.path, selectedKind: 'folder' }))
    }
  }

  const handleSurface = (s: Surface, event?: MouseEvent) => {
    openTarget(s, event)
  }

  const activateTab = (id: string) => {
    focusTab(id)
    remember(id)
  }

  const togglePin = (id: string) => {
    setOpenTabs(tabs => tabs.map(tab => (tab.id === id ? { ...tab, pinned: !tab.pinned } : tab)))
  }

  const closeTab = (id: string) => {
    if (openTabs.length <= 1) return
    const remaining = openTabs.filter(tab => tab.id !== id)
    setOpenTabs(remaining)
    if (id !== activeTabId) return
    const index = openTabs.findIndex(tab => tab.id === id)
    const neighbor = remaining[Math.max(0, Math.min(index, remaining.length - 1))]
    focusTab(neighbor.id)
    remember(neighbor.id)
  }

  const openFlowIds = new Set(
    openTabs.map(tab => flowIdFromStudioTab(tab.id)).filter((id): id is string => Boolean(id)),
  )
  const openFolderPaths = new Set(
    openTabs
      .map(tab => folderPathFromStudioTab(tab.id))
      .filter((path): path is string => Boolean(path)),
  )
  const openIndexIds = new Set(openTabs.map(tab => tab.id).filter(isStudioIndexTab))
  const activeFolderPath = folderPathFromStudioTab(activeTabId)

  const tabBarTabs = openTabs.map(tab => {
    const flowId = flowIdFromStudioTab(tab.id)
    const flow = flowId ? flows.find(item => item.id === flowId) : undefined
    const folderPath = folderPathFromStudioTab(tab.id)
    const index = SURFACES.find(item => item.id === tab.id)
    const Icon = index?.icon
    return {
      id: tab.id,
      pinned: tab.pinned,
      label: flow?.name ?? (folderPath ? folderPath.split('/').pop() : index?.label) ?? 'Flow',
      icon: flow ? (
        flowRowIcon()
      ) : folderPath ? (
        flowFolderIcon(false)
      ) : Icon ? (
        <Icon className="h-3.5 w-3.5" />
      ) : undefined,
    }
  })

  const catalogTitle =
    surface === 'gallery'
      ? {
          crumbs: ['Flows', 'Gallery'],
          title: 'Gallery',
          subtitle: 'Recipes install into a workspace under {contentRoot}/flows/',
        }
      : surface === 'sources'
        ? {
            crumbs: ['Flows', 'Sources'],
            title: 'Sources',
            subtitle: 'What Maater can react to - samples first, not a schema dump',
          }
        : {
            crumbs: ['Flows', 'Composer'],
            title: 'Composer',
            subtitle: 'Describe an automation - approve starts in Shadow',
          }

  const navProps = {
    canGoBack: history.index > 0,
    canGoForward: history.index < history.stack.length - 1,
    onBack: goBack,
    onForward: goForward,
  }

  return (
    <StudioPageWidthContext.Provider
      value={{
        mode: pageWidthMode,
        toggle: () => setPageWidthMode(mode => (mode === 'default' ? 'full' : 'default')),
      }}
    >
    <div
      className="flex h-screen min-h-screen flex-col bg-background text-foreground antialiased"
      style={maaterProtoAppSkinStyle()}
      data-maater-gallery-skin="app"
      data-testid={testIds.flowsStudioPrototype.root}
    >
      <ProtoAppHeader
        themeControl="light-dark"
        appTheme={theme}
        onAppTheme={(t) => {
          void setTheme(t)
        }}
        badge="v4.32"
        testId={testIds.flowsStudioPrototype.appHeader}
        prototypeIndexTestId={testIds.flowsStudioPrototype.backToPrototypeIndex}
      />

      <div className="flex min-h-0 flex-1">
        <ProtoIconRail
          active="flows"
          testId={testIds.flowsStudioPrototype.iconRail}
        />
        <StudioModePanel
          surface={surface}
          onSurface={handleSurface}
          flows={flows}
          activeFlowId={activeFlowId}
          activeFolderPath={activeFolderPath}
          onSelectFlow={handleSelectFlowFromTree}
          onOpenFolder={handleOpenFolder}
          onTreeContextMenu={showTreeMenu}
          exceptionCount={exceptionCount}
          openFlowIds={openFlowIds}
          openFolderPaths={openFolderPaths}
          openIndexIds={openIndexIds}
        />
        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <StudioTabBar
            tabs={tabBarTabs}
            activeId={activeTabId}
            onActivate={activateTab}
            onClose={closeTab}
            onTogglePin={togglePin}
            onNew={() => openTarget('composer', undefined, true)}
          />
          {surface === 'pulse' && (
            <PulseSurface
              flows={flows}
              onFix={handleFix}
              onOpenFlow={handleOpenFlow}
              ownerScope={ownerScope}
              setOwnerScope={setOwnerScope}
              onOpenTranscript={run => {
                const meta = runMeta(run)
                setFlowAgent({
                  key: `run-${run.id}`,
                  prompt: run.headline,
                  skill: 'flow run',
                  contextLabel: meta.conversationLabel ?? run.flowName,
                  intro: meta.agentOutput || run.detail,
                  appliedReply: 'Noted against this run. Flow memory is not written from here.',
                  placeholder: 'Ask about this run...',
                  flowId: run.flowId,
                })
              }}
              {...navProps}
            />
          )}
          {surface === 'flow' && (
            <FlowDocumentSurface
              key={activeFlowId}
              flows={flows}
              activeFlowId={activeFlowId}
              activeStepId={activeStepId}
              setActiveStepId={setActiveStepId}
              setMode={setMode}
              flowDocTab={flowDocTab}
              setFlowDocTab={setFlowDocTab}
              onFix={handleFix}
              onPatchFlow={(id, patch) =>
                setFlows(prev => prev.map(f => (f.id === id ? { ...f, ...patch } : f)))
              }
              onOpenRefine={setFlowAgent}
              agentApply={flowAgentApply}
              {...navProps}
            />
          )}
          {surface === 'folder' && activeFolderPath && (
            <FlowFolderSurface
              path={activeFolderPath}
              flows={flows}
              peek={folderPeek}
              onPeek={setFolderPeek}
              onOpenFolder={handleOpenFolder}
              onOpenFlow={handleSelectFlowFromTree}
              onContextMenu={showTreeMenu}
              ownerScope={ownerScope}
              setOwnerScope={setOwnerScope}
              {...navProps}
            />
          )}
          {(surface === 'gallery' || surface === 'sources' || surface === 'composer') && (
            <>
              <ProtoTitleRow
                crumbs={catalogTitle.crumbs}
                title={catalogTitle.title}
                subtitle={catalogTitle.subtitle}
                {...navProps}
                testId={testIds.flowsStudioPrototype.titleRow}
                backTestId={testIds.flowsStudioPrototype.backButton}
                forwardTestId={testIds.flowsStudioPrototype.forwardButton}
              />
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <ListSurfaceStatusBar
                  leading={<StudioPageWidthToggle />}
                  tips="Column keeps this page's reading measure. Full uses the panel width."
                  className="order-last"
                />
                {surface === 'gallery' && (
                  <GallerySurface installed={installed} onInstall={handleInstall} />
                )}
                {surface === 'sources' && <SourcesSurface />}
                {surface === 'composer' && (
                  <ComposerSurface
                    onApprove={handleComposerApprove}
                    onRefine={p => {
                      setFlowAgent(null)
                      setComposerRefinePrompt(p)
                      setComposerAgentOpen(true)
                    }}
                    onReject={() => setComposerAgentOpen(false)}
                    proposalSteps={composerProposal.steps}
                    proposalYaml={composerProposal.yaml}
                    proposalFromRefine={composerProposal.fromRefine}
                    onResetProposal={() =>
                      setComposerProposal({
                        steps: COMPOSER_PREVIEW_STEPS,
                        yaml: COMPOSER_EXAMPLE_YAML,
                        fromRefine: false,
                      })
                    }
                  />
                )}
              </div>
            </>
          )}
        </main>
        {flowAgent ? (
          <ComposerAgentPanel
            key={flowAgent.key}
            prompt={flowAgent.prompt}
            skill={flowAgent.skill}
            contextLabel={flowAgent.contextLabel}
            intro={flowAgent.intro}
            appliedReply={flowAgent.appliedReply}
            placeholder={flowAgent.placeholder}
            onClose={() => setFlowAgent(null)}
            onApplied={() => {
              if (!flowAgent.flowId) return
              setFlowAgentApply(prev => ({
                flowId: flowAgent.flowId!,
                stepId: flowAgent.stepId,
                nonce: (prev && prev.flowId === flowAgent.flowId ? prev.nonce : 0) + 1,
              }))
            }}
          />
        ) : surface === 'composer' && composerAgentOpen ? (
          <ComposerAgentPanel
            key={composerRefinePrompt}
            prompt={composerRefinePrompt}
            onClose={() => setComposerAgentOpen(false)}
            onApplied={() =>
              setComposerProposal({
                steps: composerRefinedSteps(),
                yaml: COMPOSER_REFINED_YAML,
                fromRefine: true,
              })
            }
          />
        ) : null}
      </div>
      {treeMenu ? (
        <EntityContextMenu
          x={treeMenu.x}
          y={treeMenu.y}
          onClose={() => setTreeMenu(null)}
          data-testid={testIds.flowsStudioPrototype.flowTreeContextMenu}
          entries={(() => {
            const target = treeMenu.target
            const selectedFlowId = target.kind === 'flow' ? target.id : null
            return flowTreeMenuEntries(
              target,
              {
                onOpenFolder: path => handleOpenFolder(path),
                onPreviewFlow: handlePreviewFlow,
                onOpenFlow: (id, tab) => handleSelectFlowFromTree(id, tab),
              },
              selectedFlowId != null &&
                folderPeek.open &&
                folderPeek.selectedKind === 'flow' &&
                folderPeek.selectedId === selectedFlowId &&
                folderPathFromStudioTab(activeTabId) ===
                  flows.find(item => item.id === selectedFlowId)?.path,
            )
          })()}
        />
      ) : null}
    </div>
    </StudioPageWidthContext.Provider>
  )
}
